import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import html2canvas from 'html2canvas';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  type ChartOptions,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { useEscapeKey } from '../hooks/useEscapeKey';
import type { SnackbarVariant } from '../components/Snackbar';
import UndoSnackbar from '../components/UndoSnackbar';
import { adminApi } from '../services/adminApi';
import { getStockQuote, searchStocks } from '../services/stockApi';
import stocksList from '../data/stocksList.json';
import { ensureSymbolColorsAssigned, getSymbolColor } from '../utils/symbolColors';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

interface PortfoliosScreenProps {
  isDarkMode: boolean;
  authToken?: string | null;
  authUserId?: number | null;
  onUnauthorized?: () => void;
  onNotify?: (message: string, variant?: SnackbarVariant) => void;
  onOpenResearch?: (symbol: string) => void;
  externalAddHoldingRequest?: { id: number; symbol: string } | null;
  onExternalAddHoldingConsumed?: () => void;
}

type Holding = {
  id: string;
  portfolioId: number;
  symbol: string;
  buyDate: string; // dd/MM/yyyy (display)
  sellDate?: string; // dd/MM/yyyy (display) - Closed holdings only
  buyPrice: number;
  currentPrice: number | null;
  changePct?: number | null;
  shares: number;
  buyPriceAlert?: number | null;
  sellPriceAlert?: number | null;
  status: '' | 'Not Reached' | 'Buy Triggered' | 'Sell Triggered' | 'Within 5% of Buy Target' | 'Within 5% of Sell Target' | 'Within 10% of Buy Target' | 'Within 10% of Sell Target';
  isClosed?: boolean;
};

type DisplayHolding = Holding & {
  lotCount?: number;
};

type StockListItem = {
  symbol: string;
  name: string;
  exchange: string;
  exchangeName?: string;
  category: string;
};

const getExchangeSearchPriority = (exchangeRaw: string | undefined): number => {
  const exchange = String(exchangeRaw || '').trim().toUpperCase();
  if (exchange === 'NASDAQ' || exchange === 'NASDAQGS' || exchange === 'NASDAQGM' || exchange === 'NASDAQCM') return 0;
  if (exchange === 'NYSE') return 1;
  if (exchange === 'AMEX' || exchange === 'NYSEAMERICAN' || exchange === 'NYSE AMERICAN') return 2;
  return 3;
};

type SortableHoldingColumn =
  | 'portfolioName'
  | 'symbol'
  | 'sellDate'
  | 'changePct'
  | 'buyDate'
  | 'buyPrice'
  | 'currentPrice'
  | 'cost'
  | 'marketValue'
  | 'profitLoss'
  | 'shares'
  | 'buyPriceAlert'
  | 'sellPriceAlert'
  | 'status';

const ALL_PORTFOLIOS_ID = -1;

const LRM = '\u200E';

const formatCurrency = (value: number) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return `${LRM}$0.00`;
  const formatted = n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
  return `${LRM}${formatted}`;
};

const formatCurrency0 = (value: number) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return `${LRM}$0`;
  const formatted = n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  return `${LRM}${formatted}`;
};

const formatNumber = (value: number | null | undefined) => {
  if (value == null) return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
};

const formatCurrencyMaybe = (value: number | null | undefined) => {
  if (value == null) return '';
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '';
  const formatted = n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
  return `${LRM}${formatted}`;
};

const formatPctUpTo2 = (value: number): string => {
  const nRaw = Number(value);
  if (!Number.isFinite(nRaw)) return '—';
  const n = Math.round(nRaw * 100) / 100;
  const safe = Object.is(n, -0) ? 0 : n;
  const sign = safe > 0 ? '+' : '';
  return `${sign}${safe.toFixed(2)}%`;
};

const normalizeAlertValue = (raw: unknown): number | null => {
  const n = Number(raw);
  if (!Number.isFinite(n) || n === 0) return null;
  return n;
};

const mergeAlertValue = (a: number | null | undefined, b: number | null | undefined): number | null => {
  const av = a == null ? null : normalizeAlertValue(a);
  const bv = b == null ? null : normalizeAlertValue(b);
  if (av == null) return bv;
  if (bv == null) return av;
  return Math.min(av, bv);
};

const computeAlertStatus = (
  currentPrice: number | null,
  buyPriceAlert: number | null | undefined,
  sellPriceAlert: number | null | undefined
): Holding['status'] => {
  const price = Number(currentPrice);
  const buy = buyPriceAlert == null ? NaN : Number(buyPriceAlert);
  const sell = sellPriceAlert == null ? NaN : Number(sellPriceAlert);

  const hasBuy = Number.isFinite(buy) && buy !== 0;
  const hasSell = Number.isFinite(sell) && sell !== 0;
  if (!hasBuy && !hasSell) return '';

  if (!Number.isFinite(price)) return 'Not Reached';
  if (hasSell && sell <= price) return 'Sell Triggered';
  if (hasBuy && price <= buy) return 'Buy Triggered';

  const buyDistancePct = hasBuy ? Math.abs((price - buy) / buy) * 100 : Number.POSITIVE_INFINITY;
  const sellDistancePct = hasSell ? Math.abs((price - sell) / sell) * 100 : Number.POSITIVE_INFINITY;
  const isSellTarget = sellDistancePct < buyDistancePct;
  const distancePct = Math.min(buyDistancePct, sellDistancePct);
  if (distancePct <= 5) return `Within 5% of ${isSellTarget ? 'Sell' : 'Buy'} Target`;
  if (distancePct <= 10) return `Within 10% of ${isSellTarget ? 'Sell' : 'Buy'} Target`;
  return 'Not Reached';
};

let _measureCanvas: HTMLCanvasElement | null = null;
const measureTextWidth = (text: string, font: string) => {
  if (typeof document === 'undefined') return text.length * 10;
  if (!_measureCanvas) _measureCanvas = document.createElement('canvas');
  const ctx = _measureCanvas.getContext('2d');
  if (!ctx) return text.length * 10;
  ctx.font = font;
  return ctx.measureText(text).width;
};

type AutoFitTextProps = {
  text: string;
  maxFontSize: number;
  minFontSize: number;
  fontFamily: string;
  fontWeight: number;
  color: string;
  maxWidth?: string | number;
  paddingX?: number;
};

const AutoFitText: React.FC<AutoFitTextProps> = ({
  text,
  maxFontSize,
  minFontSize,
  fontFamily,
  fontWeight,
  color,
  maxWidth,
  paddingX = 6,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => setContainerWidth(el.clientWidth);
    update();

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => update());
      ro.observe(el);
      return () => ro.disconnect();
    }

    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const fittedFontSize = useMemo(() => {
    const available = Math.max(0, containerWidth - paddingX * 2);
    if (!available) return maxFontSize;

    let lo = minFontSize;
    let hi = maxFontSize;
    let best = minFontSize;

    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      const font = `${fontWeight} ${mid}px ${fontFamily}`;
      const w = measureTextWidth(text, font);
      if (w <= available) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }

    return Math.max(minFontSize, Math.min(maxFontSize, best));
  }, [containerWidth, fontFamily, fontWeight, maxFontSize, minFontSize, paddingX, text]);

  return (
    <div
      ref={containerRef}
      style={{
        maxWidth,
        paddingLeft: paddingX,
        paddingRight: paddingX,
        color,
      }}
    >
      <span
        style={{
          fontFamily,
          fontSize: fittedFontSize,
          fontWeight,
          color,
          whiteSpace: 'nowrap',
          lineHeight: '100%',
          display: 'inline-block',
        }}
      >
        {text}
      </span>
    </div>
  );
};

const PortfoliosScreen: React.FC<PortfoliosScreenProps> = ({
  isDarkMode,
  authToken,
  authUserId,
  onUnauthorized,
  onNotify,
  onOpenResearch,
  externalAddHoldingRequest,
  onExternalAddHoldingConsumed,
}) => {
  type DoughnutChartRef = ChartJS<'doughnut', number[], string> | undefined;
  const doughnutChartRefs = useRef<Record<string, DoughnutChartRef | null>>({});
  const doughnutModalChartRef = useRef<DoughnutChartRef | null>(null);
  type ExpandedChartId = 'portfolio-holdings' | 'profit-loss' | 'buy-vs-current';

  const resolvedUserId =
    typeof authUserId === 'number' && Number.isFinite(authUserId) && authUserId > 0
      ? authUserId
      : undefined;
  const canPersist = Boolean(authToken);

  const isUnauthorized = useCallback((e: any): boolean => {
    const status = Number(e?.status);
    return status === 401;
  }, []);

  const colors = useMemo(() => {
    const dark = isDarkMode;
    return {
      pageBg: dark ? '#0b1220' : '#ffffff',
      text: dark ? '#F9FAFB' : '#0f172a',
      subText: dark ? '#F9FAFB' : '#64748b',
      cardBg: dark ? '#0f172a' : '#ffffff',
      cardBorder: dark ? '#1f2a44' : '#e2e8f0',
      tableHeaderBg: dark ? '#0b1220' : '#f8fafc',
      tableRowHover: dark ? '#0b1730' : '#f1f5f9',
      shadow: dark ? '0 10px 30px rgba(0,0,0,0.35)' : '0 10px 30px rgba(15, 23, 42, 0.08)',
      primary: '#2563eb',
      success: '#16a34a',
      danger: '#ef4444',
      warn: '#f59e0b',
      chipBg: dark ? 'rgba(148,163,184,0.12)' : 'rgba(100,116,139,0.12)',
    };
  }, [isDarkMode]);

  const setDoughnutChartRef = (key: string) => (chart: DoughnutChartRef | null) => {
    doughnutChartRefs.current[key] = chart;
  };

  const zoomInIcon = isDarkMode ? '/images/darkmode/zoomin.svg' : '/images/zoomin.svg';
  const bookmarkEmptyIcon = isDarkMode ? '/images/darkmode/bookmark_empy.svg' : '/images/bookmark_empy.svg';

  const [portfolios, setPortfolios] = useState<Array<{ id: number; name: string; isDefault: boolean }>>([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<number | null>(null);
  const selectedPortfolioName = useMemo(() => {
    if (selectedPortfolioId === ALL_PORTFOLIOS_ID) return 'All Portfolios';
    const found = portfolios.find((p) => p.id === selectedPortfolioId);
    return found?.name || 'Portfolio Holdings';
  }, [portfolios, selectedPortfolioId]);
  const isAllPortfolios = selectedPortfolioId === ALL_PORTFOLIOS_ID;
  const portfolioNameById = useMemo(
    () => new Map<number, string>(portfolios.map((p) => [p.id, p.name])),
    [portfolios]
  );
  const [activeTab, setActiveTab] = useState<'holdings' | 'closed'>('holdings');
  const portfolioLastTabHydratedRef = useRef(false);
  const portfolioLastTabDirtyRef = useRef(false);
  const [groupByStock, setGroupByStock] = useState(false);
  const groupByStockHydratedRef = useRef(false);
  const groupByStockDirtyRef = useRef(false);
  const [isAddHoldingOpen, setIsAddHoldingOpen] = useState(false);
  const [addHoldingMode, setAddHoldingMode] = useState<'add' | 'edit'>('add');
  const [editHoldingId, setEditHoldingId] = useState<string | null>(null);
  const [addHoldingError, setAddHoldingError] = useState<string | null>(null);
  const [draftSymbol, setDraftSymbol] = useState('');
  const [draftOpenDate, setDraftOpenDate] = useState<string>(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  });
  const [draftShares, setDraftShares] = useState('');
  const [draftBuyPrice, setDraftBuyPrice] = useState('');
  const [draftBuyAlert, setDraftBuyAlert] = useState('');
  const [draftSellAlert, setDraftSellAlert] = useState('');
  const [draftCurrentPrice, setDraftCurrentPrice] = useState<number | null>(null);
  const [symbolSearchBusy, setSymbolSearchBusy] = useState(false);
  const [showSymbolAutocomplete, setShowSymbolAutocomplete] = useState(false);
  const [symbolSelectedIndex, setSymbolSelectedIndex] = useState(-1);
  const [remoteStockResults, setRemoteStockResults] = useState<StockListItem[]>([]);
  const symbolAutocompleteRef = useRef<HTMLDivElement | null>(null);
  const symbolInputRef = useRef<HTMLInputElement | null>(null);
  const holdingsTableWrapRef = useRef<HTMLDivElement | null>(null);
  const [hasHoldingsOverflowX, setHasHoldingsOverflowX] = useState(false);
  const lastOpenResearchAtRef = useRef(0);

  const HOLDING_ADD_UNDO_MS = 6500;
  const HOLDING_DELETE_UNDO_MS = 6500;
  const HOLDING_EDIT_UNDO_MS = 6500;
  const HOLDING_SELL_UNDO_MS = 6500;
  type HoldingAddLaunchSource = 'portfolios' | 'header';
  const [holdingAddLaunchSource, setHoldingAddLaunchSource] = useState<HoldingAddLaunchSource>('portfolios');

  type PendingHoldingAdd = { holdingId: number; portfolioName: string; timerId: number };
  const [pendingHoldingAdd, setPendingHoldingAdd] = useState<PendingHoldingAdd | null>(null);
  const pendingHoldingAddRef = useRef<PendingHoldingAdd | null>(null);

  useEffect(() => {
    pendingHoldingAddRef.current = pendingHoldingAdd;
  }, [pendingHoldingAdd]);

  useEffect(() => {
    // Reset per-login/session guards.
    groupByStockHydratedRef.current = false;
    groupByStockDirtyRef.current = false;
    portfolioLastTabHydratedRef.current = false;
    portfolioLastTabDirtyRef.current = false;
  }, [authToken]);

  useEffect(() => {
    if (!authToken) return;
    if (groupByStockHydratedRef.current) return;

    groupByStockHydratedRef.current = true;
    (async () => {
      try {
        const pref = await adminApi.getPortfolioGroupByStock(authToken);
        if (!groupByStockDirtyRef.current) {
          setGroupByStock(!!pref.groupByStock);
        }
      } catch {
        // Best-effort: keep local default.
      }
    })();
  }, [authToken]);

  useEffect(() => {
    if (!authToken) return;
    if (portfolioLastTabHydratedRef.current) return;

    portfolioLastTabHydratedRef.current = true;
    (async () => {
      try {
        const pref = await adminApi.getPortfolioLastTab(authToken);
        if (!portfolioLastTabDirtyRef.current) {
          setActiveTab(pref.tab);
        }
      } catch {
        // Best-effort
      }
    })();
  }, [authToken]);

  const dismissPendingHoldingAdd = useCallback(() => {
    const pending = pendingHoldingAddRef.current;
    if (pending) window.clearTimeout(pending.timerId);
    pendingHoldingAddRef.current = null;
    setPendingHoldingAdd(null);
  }, []);

  const triggerOpenResearch = useCallback((symbol: string) => {
    if (!onOpenResearch) return;
    const now = Date.now();
    if (now - lastOpenResearchAtRef.current < 300) return;
    lastOpenResearchAtRef.current = now;
    onOpenResearch(symbol);
  }, [onOpenResearch]);

  type PendingHoldingDelete = { holdingId: string; row: Holding; index: number; timerId: number };
  const [pendingHoldingDelete, setPendingHoldingDelete] = useState<PendingHoldingDelete | null>(null);
  const pendingHoldingDeleteRef = useRef<PendingHoldingDelete | null>(null);

  type PendingUndo = { message: string; timerId: number };

  const [pendingHoldingEditUndo, setPendingHoldingEditUndo] = useState<PendingUndo | null>(null);
  const pendingHoldingEditUndoRef = useRef<(PendingUndo & { snapshot: Holding; portfolioId: number }) | null>(null);

  const [pendingHoldingSellUndo, setPendingHoldingSellUndo] = useState<PendingUndo | null>(null);
  const pendingHoldingSellUndoRef = useRef<
    | (PendingUndo &
        (
          | { kind: 'local'; rows: Holding[] }
          | { kind: 'closed-add'; createdId: number }
          | { kind: 'sell-all'; snapshot: Holding; portfolioId: number; holdingId: number }
          | { kind: 'partial'; snapshot: Holding; portfolioId: number; holdingId: number; createdClosedId: number }
        ))
    | null
  >(null);

  const lastEditHoldingSnapshotRef = useRef<Holding | null>(null);

  const dismissPendingHoldingDelete = useCallback(() => {
    const pending = pendingHoldingDeleteRef.current;
    if (pending) window.clearTimeout(pending.timerId);
    pendingHoldingDeleteRef.current = null;
    setPendingHoldingDelete(null);
  }, []);

  const dismissPendingHoldingEditUndo = useCallback(() => {
    const pending = pendingHoldingEditUndoRef.current;
    if (pending) window.clearTimeout(pending.timerId);
    pendingHoldingEditUndoRef.current = null;
    setPendingHoldingEditUndo(null);
  }, []);

  const dismissPendingHoldingSellUndo = useCallback(() => {
    const pending = pendingHoldingSellUndoRef.current;
    if (pending) window.clearTimeout(pending.timerId);
    pendingHoldingSellUndoRef.current = null;
    setPendingHoldingSellUndo(null);
  }, []);

  const dismissAllPortfoliosUndoSnackbars = useCallback(() => {
    dismissPendingHoldingAdd();
    const pendingDelete = pendingHoldingDeleteRef.current;
    if (pendingDelete) {
      void commitPendingHoldingDelete(pendingDelete.holdingId);
    } else {
      dismissPendingHoldingDelete();
    }
    dismissPendingHoldingEditUndo();
    dismissPendingHoldingSellUndo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissPendingHoldingAdd, dismissPendingHoldingDelete, dismissPendingHoldingEditUndo, dismissPendingHoldingSellUndo]);
  const [expandedChart, setExpandedChart] = useState<ExpandedChartId | null>(null);
  const [expandedHoldingsKey, setExpandedHoldingsKey] = useState<string | null>(null);
  const [symbolColorVersion, setSymbolColorVersion] = useState(0);
  const rowsRef = useRef<Holding[]>([]);
  const holdingsCacheRef = useRef<Map<number, Holding[]>>(new Map());
  const holdingsLoadIdRef = useRef(0);
  const lastAuthUserIdRef = useRef<number | null | undefined>(authUserId);
  const changePctLoggedRef = useRef(false);
  const lastQuotesUpdatedRef = useRef<Map<string, string>>(new Map());
  const quotesBySymbolRef = useRef(new Map<string, { currentPrice: number | null; changePct: number | null }>());
  const [quotesVersion, setQuotesVersion] = useState(0);
  const [isQuotesRefreshing, setIsQuotesRefreshing] = useState(false);
  const [isHoldingsLoading, setIsHoldingsLoading] = useState(false);
  const holdingsLoadTimeoutRef = useRef<number | null>(null);
  const refreshTintTimeoutRef = useRef<number | null>(null);

  const [isSellModalOpen, setIsSellModalOpen] = useState(false);
  const [sellRow, setSellRow] = useState<Holding | null>(null);
  const [sellSharesText, setSellSharesText] = useState('');
  const [sellPriceText, setSellPriceText] = useState('');
  const [sellDateText, setSellDateText] = useState('');
  const [sellError, setSellError] = useState<string | null>(null);
  const [sellModalPos, setSellModalPos] = useState<{ x: number; y: number } | null>(null);
  const [isSellDragging, setIsSellDragging] = useState(false);
  const sellDragRef = useRef<null | { startClientX: number; startClientY: number; originX: number; originY: number }>(null);

  const [skipDeleteConfirm, setSkipDeleteConfirm] = useState(() => {
    try {
      return window.localStorage.getItem('portfolios_skip_delete_confirm') === '1';
    } catch {
      return false;
    }
  });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState<Holding | null>(null);
  const [deleteDontShowAgain, setDeleteDontShowAgain] = useState(false);

  const openSellModal = (row: Holding) => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    setSellRow(row);
    setSellSharesText(row.shares != null ? String(row.shares) : '');
    setSellPriceText(row.currentPrice != null ? String(row.currentPrice) : '');
    setSellDateText(`${y}-${m}-${day}`);
    setSellError(null);
    setSellModalPos(null);
    setIsSellModalOpen(true);
  };

  const closeSellModal = () => {
    setIsSellModalOpen(false);
    setSellRow(null);
    setSellSharesText('');
    setSellPriceText('');
    setSellDateText('');
    setSellError(null);
    setSellModalPos(null);
    setIsSellDragging(false);
    sellDragRef.current = null;
  };

  const pushSellError = (message: string) => {
    setSellError(message);
    onNotify?.(message, 'error');
  };

  const pushAddHoldingError = (message: string) => {
    setAddHoldingError(message);
    onNotify?.(message, 'error');
  };

  const openDeleteModal = (row: Holding) => {
    setDeleteRow(row);
    setDeleteDontShowAgain(false);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeleteRow(null);
    setDeleteDontShowAgain(false);
  };

  const performDeleteHolding = (rowId: string) => {
    // Only one undo snackbar at a time.
    dismissAllPortfoliosUndoSnackbars();

    // If another delete is pending, commit it now (no undo).
    const existing = pendingHoldingDeleteRef.current;
    if (existing) {
      window.clearTimeout(existing.timerId);
      pendingHoldingDeleteRef.current = null;
      setPendingHoldingDelete(null);

      if (authToken) {
        void (async () => {
          try {
            const id = Number(existing.holdingId);
            if (Number.isFinite(id) && id > 0) await adminApi.deleteMyPortfolioHolding(authToken, id);
            await refreshHoldingsFromDb();
          } catch (e: any) {
            if (isUnauthorized(e)) {
              onUnauthorized?.();
              return;
            }
            onNotify?.(e?.message || 'Failed deleting holding', 'error');
          }
        })();
      }
    }

    const removedIndex = rows.findIndex((r) => r.id === rowId);
    const removedRow = removedIndex >= 0 ? rows[removedIndex] : null;
    if (!removedRow) return;

    setRows((prev) => prev.filter((r) => r.id !== rowId));

    const timerId = window.setTimeout(() => {
      const pending = pendingHoldingDeleteRef.current;
      if (!pending || pending.holdingId !== rowId) return;

      pendingHoldingDeleteRef.current = null;
      setPendingHoldingDelete(null);

      if (!authToken) return;

      void (async () => {
        try {
          const id = Number(rowId);
          if (Number.isFinite(id) && id > 0) await adminApi.deleteMyPortfolioHolding(authToken, id);
          await refreshHoldingsFromDb();
        } catch (e: any) {
          // Best-effort: re-add the row if the server delete fails.
          setRows((prev) => {
            if (prev.some((r) => r.id === removedRow.id)) return prev;
            const next = [...prev];
            const idx = Math.max(0, Math.min(removedIndex, next.length));
            next.splice(idx, 0, removedRow);
            return next;
          });

          if (isUnauthorized(e)) {
            onUnauthorized?.();
            return;
          }
          onNotify?.(e?.message || 'Failed deleting holding', 'error');
        }
      })();
    }, HOLDING_DELETE_UNDO_MS);

    const pending: PendingHoldingDelete = { holdingId: rowId, row: removedRow, index: removedIndex, timerId };
    pendingHoldingDeleteRef.current = pending;
    setPendingHoldingDelete(pending);
  };

  useEffect(() => {
    if (!isSellModalOpen) return;
    // Initialize at center of the viewport
    const modalW = 432;
    const modalH = 430;
    const pad = 12;
    const w = typeof window !== 'undefined' ? window.innerWidth : 0;
    const h = typeof window !== 'undefined' ? window.innerHeight : 0;
    const x = Math.max(pad, Math.round((w - modalW) / 2));
    const y = Math.max(pad, Math.round((h - modalH) / 2));
    setSellModalPos({ x, y });
  }, [isSellModalOpen]);

  useEffect(() => {
    if (!isSellDragging) return;

    const modalW = 432;
    const modalH = 430;
    const pad = 12;

    const onMove = (e: MouseEvent) => {
      const drag = sellDragRef.current;
      if (!drag) return;
      const dx = e.clientX - drag.startClientX;
      const dy = e.clientY - drag.startClientY;

      const w = window.innerWidth;
      const h = window.innerHeight;
      const maxX = Math.max(pad, w - modalW - pad);
      const maxY = Math.max(pad, h - modalH - pad);

      const nextX = Math.min(maxX, Math.max(pad, drag.originX + dx));
      const nextY = Math.min(maxY, Math.max(pad, drag.originY + dy));
      setSellModalPos({ x: nextX, y: nextY });
    };

    const onUp = () => {
      setIsSellDragging(false);
      sellDragRef.current = null;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isSellDragging]);

  const [isPortfolioMenuOpen, setIsPortfolioMenuOpen] = useState(false);
  const portfolioMenuRef = useRef<HTMLDivElement | null>(null);

  const [isPortfolioNameModalOpen, setIsPortfolioNameModalOpen] = useState(false);
  const [portfolioNameModalMode, setPortfolioNameModalMode] = useState<'add' | 'edit'>('add');
  const [portfolioNameModalEditId, setPortfolioNameModalEditId] = useState<number | null>(null);
  const [portfolioNameModalValue, setPortfolioNameModalValue] = useState('');
  const [portfolioNameModalBusy, setPortfolioNameModalBusy] = useState(false);
  const [portfolioNameModalError, setPortfolioNameModalError] = useState<string | null>(null);
  const portfolioNameModalInputRef = useRef<HTMLInputElement | null>(null);

  const [holdingActionsOpenForId, setHoldingActionsOpenForId] = useState<string | null>(null);
  const [holdingActionsAnchor, setHoldingActionsAnchor] = useState<{ top: number; left: number } | null>(null);
  const holdingActionsMenuRef = useRef<HTMLDivElement | null>(null);

  const computeHoldingActionsAnchor = useCallback((rowId: string): { top: number; left: number } | null => {
    if (!rowId || typeof document === 'undefined') return null;
    const btn = document.getElementById(`portfolios-holding-actions-btn-${rowId}`);
    if (!btn) return null;

    const rect = btn.getBoundingClientRect();
    const menuWidth = 200;
    const itemsCount = activeTab === 'closed' ? 2 : 3;
    const itemH = 40;
    const menuHeight = itemsCount * itemH;
    const margin = 8;

    let left = rect.left;
    left = Math.max(margin, Math.min(left, window.innerWidth - menuWidth - margin));

    let top = rect.bottom + 2;
    if (top + menuHeight + margin > window.innerHeight) {
      top = Math.max(margin, rect.top - 2 - menuHeight);
    }

    return { top, left };
  }, [activeTab]);

  const closeHoldingActionsMenu = useCallback(() => {
    setHoldingActionsOpenForId(null);
    setHoldingActionsAnchor(null);
  }, []);

  const toggleHoldingActionsMenu = useCallback(
    (rowId: string) => {
      setHoldingActionsOpenForId((prev) => {
        const next = prev === rowId ? null : rowId;
        if (next) setHoldingActionsAnchor(computeHoldingActionsAnchor(next));
        else setHoldingActionsAnchor(null);
        return next;
      });
    },
    [computeHoldingActionsAnchor]
  );

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (!holdingActionsOpenForId) return;
      const btn = document.getElementById(`portfolios-holding-actions-btn-${holdingActionsOpenForId}`);
      if (btn && btn.contains(e.target as Node)) return;
      if (holdingActionsMenuRef.current && holdingActionsMenuRef.current.contains(e.target as Node)) return;
      closeHoldingActionsMenu();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [closeHoldingActionsMenu, holdingActionsOpenForId]);

  useEffect(() => {
    if (!holdingActionsOpenForId) return;

    const update = () => {
      const next = computeHoldingActionsAnchor(holdingActionsOpenForId);
      if (next) setHoldingActionsAnchor(next);
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [computeHoldingActionsAnchor, holdingActionsOpenForId]);

  const [savingBookmarkByChartId, setSavingBookmarkByChartId] = useState<Record<string, boolean>>({});
  const [bookmarkFlashByChartId, setBookmarkFlashByChartId] = useState<Record<string, boolean>>({});
  const bookmarkFlashTimeoutsRef = useRef<Record<string, number>>({});

  useEffect(() => {
    const timeouts = bookmarkFlashTimeoutsRef.current;
    return () => {
      Object.keys(timeouts).forEach((k) => {
        const t = timeouts[k];
        if (!t) return;
        try {
          window.clearTimeout(t);
        } catch {
          // ignore
        }
      });
    };
  }, []);

  useEffect(() => {
    if (!isPortfolioMenuOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const el = portfolioMenuRef.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      setIsPortfolioMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [isPortfolioMenuOpen]);

  useEffect(() => {
    if (!isPortfolioNameModalOpen) return;
    const t = window.setTimeout(() => {
      portfolioNameModalInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [isPortfolioNameModalOpen]);

  const triggerBookmarkFlash = (chartId: string) => {
    setBookmarkFlashByChartId({ [chartId]: true });

    const timeouts = bookmarkFlashTimeoutsRef.current;
    Object.keys(timeouts).forEach((key) => {
      const t = timeouts[key];
      if (!t) return;
      try {
        window.clearTimeout(t);
      } catch {
        // ignore
      }
      delete timeouts[key];
    });

    bookmarkFlashTimeoutsRef.current[chartId] = window.setTimeout(() => {
      setBookmarkFlashByChartId((prev) => {
        if (!prev[chartId]) return prev;
        return { ...prev, [chartId]: false };
      });
    }, 30_000);
  };

  const [sort, setSort] = useState<{ key: SortableHoldingColumn; dir: 'asc' | 'desc' }>({ key: 'symbol', dir: 'asc' });
  const [portfolioError, setPortfolioError] = useState<string | null>(null);
  const [portfoliosLoaded, setPortfoliosLoaded] = useState(false);
  useEffect(() => {
    if (authToken) return () => undefined;
    setPortfoliosLoaded(false);
    return () => undefined;
  }, [authToken]);

  const toggleSort = (key: SortableHoldingColumn) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  const sortIndicator = (key: SortableHoldingColumn): React.ReactNode => {
    if (sort.key !== key) {
      return (
        <img
          src="/images/SortList.svg"
          alt="Sort"
          style={{ width: 16, height: 16, marginLeft: 6, verticalAlign: 'text-bottom', opacity: isDarkMode ? 0.9 : 0.75 }}
        />
      );
    }
    return sort.dir === 'asc' ? ' ↑' : ' ↓';
  };

  useEscapeKey(Boolean(expandedChart), () => {
    if (expandedChart) setExpandedChart(null);
  });

  useEscapeKey(Boolean(isPortfolioMenuOpen), () => {
    if (isPortfolioMenuOpen) setIsPortfolioMenuOpen(false);
  });

  useEscapeKey(Boolean(isPortfolioNameModalOpen), () => {
    if (isPortfolioNameModalOpen) setIsPortfolioNameModalOpen(false);
  });

  useEscapeKey(Boolean(isSellModalOpen), () => {
    if (isSellModalOpen) closeSellModal();
  });

  const MAX_PORTFOLIOS_PER_USER = 3;

  const submitPortfolioNameModal = () => {
    if (portfolioNameModalMode === 'add' && portfolios.length >= MAX_PORTFOLIOS_PER_USER) {
      setPortfolioNameModalError(`You can only have up to ${MAX_PORTFOLIOS_PER_USER} portfolios.`);
      return;
    }

    const name = String(portfolioNameModalValue || '').trim();
    if (!name) {
      setPortfolioNameModalError(portfolioNameModalMode === 'edit' ? 'Edit portfolio name' : 'Add portfolio name');
      return;
    }

    if (!authToken) {
      onNotify?.('Please login to save portfolios.', 'error');
      return;
    }

    if (portfolioNameModalBusy) return;
    if (portfolioNameModalMode === 'edit' && !portfolioNameModalEditId) return;

    setPortfolioNameModalBusy(true);
    setPortfolioNameModalError(null);

    void (async () => {
      try {
        const saved = await adminApi.upsertMyPortfolio(authToken, {
          id: portfolioNameModalMode === 'edit' ? portfolioNameModalEditId : undefined,
          name,
        });
        const list = await adminApi.listMyPortfolios(authToken);
        setPortfolios(list.map((p) => ({ id: p.id, name: p.name, isDefault: Boolean(p.isDefault) })));
        setSelectedPortfolioId(saved.id);
        setIsPortfolioNameModalOpen(false);
        setIsPortfolioMenuOpen(true);
      } catch (e: any) {
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        setPortfolioNameModalError(e?.message || 'Failed saving portfolio');
      } finally {
        setPortfolioNameModalBusy(false);
      }
    })();
  };

  const canAddPortfolio = portfolios.length < MAX_PORTFOLIOS_PER_USER;

  const openAddPortfolioModal = () => {
    setPortfolioNameModalMode('add');
    setPortfolioNameModalEditId(null);
    setPortfolioNameModalValue('');
    setPortfolioNameModalError(null);
    setIsPortfolioNameModalOpen(true);
    setIsPortfolioMenuOpen(false);
  };

  const openEditPortfolioModal = (portfolio: { id: number; name: string }) => {
    setPortfolioNameModalMode('edit');
    setPortfolioNameModalEditId(portfolio.id);
    setPortfolioNameModalValue(portfolio.name);
    setPortfolioNameModalError(null);
    setIsPortfolioNameModalOpen(true);
    setIsPortfolioMenuOpen(false);
  };

  const selectPortfolio = (id: number) => {
    setSelectedPortfolioId(id);
    setIsPortfolioMenuOpen(false);
    const cached = holdingsCacheRef.current.get(id);
    if (cached) {
      setRows(cached);
    }
  };

  const resolveRowPortfolioId = (row: Holding | null | undefined): number | null => {
    const rowId = Number(row?.portfolioId ?? 0);
    if (Number.isFinite(rowId) && rowId > 0) return rowId;
    const selectedId = Number(selectedPortfolioId ?? 0);
    if (Number.isFinite(selectedId) && selectedId > 0) return selectedId;
    return null;
  };

  useEscapeKey(Boolean(holdingActionsOpenForId), () => {
    if (holdingActionsOpenForId) closeHoldingActionsMenu();
  });

  useEscapeKey(Boolean(isAddHoldingOpen), () => {
    if (isAddHoldingOpen) setIsAddHoldingOpen(false);
  });

  const getTodayInputDate = (): string => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const displayDateToInputDate = (raw: string): string => {
    // raw: dd/MM/yyyy
    const t = String(raw || '').trim();
    const parts = t.split('/');
    if (parts.length !== 3) return '';
    const [dd, mm, yyyy] = parts;
    if (!yyyy || !mm || !dd) return '';
    const y = String(yyyy).padStart(4, '0');
    const m = String(mm).padStart(2, '0');
    const d = String(dd).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const inputDateToUtcIso = (raw: string): string | null => {
    // raw: yyyy-MM-dd
    const t = String(raw || '').trim();
    if (!t) return null;
    const parts = t.split('-');
    if (parts.length !== 3) return null;
    const [yStr, mStr, dStr] = parts;
    const y = Number(yStr);
    const m = Number(mStr);
    const d = Number(dStr);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
    if (y < 1900 || y > 3000) return null;
    if (m < 1 || m > 12) return null;
    if (d < 1 || d > 31) return null;
    const iso = new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0)).toISOString();
    return iso;
  };

  const inputDateToDisplayDate = (raw: string): string => {
    // raw: yyyy-MM-dd -> dd/MM/yyyy
    const t = String(raw || '').trim();
    if (!t) return '';
    const parts = t.split('-');
    if (parts.length !== 3) return '';
    const [yyyy, mm, dd] = parts;
    if (!yyyy || !mm || !dd) return '';
    return `${String(dd).padStart(2, '0')}/${String(mm).padStart(2, '0')}/${String(yyyy).padStart(4, '0')}`;
  };

  const resetDraftHolding = () => {
    setDraftSymbol('');
    setDraftOpenDate(getTodayInputDate());
    setDraftShares('');
    setDraftBuyPrice('');
    setDraftBuyAlert('');
    setDraftSellAlert('');
    setDraftCurrentPrice(null);
    setSymbolSearchBusy(false);
    setShowSymbolAutocomplete(false);
    setSymbolSelectedIndex(-1);
    setAddHoldingError(null);
  };

  const openAddHoldingModal = () => {
    if (isAllPortfolios) {
      onNotify?.('Please select a portfolio first.', 'error');
      return;
    }
    setActiveTab('holdings');
    setAddHoldingMode('add');
    setEditHoldingId(null);
    setDraftOpenDate(getTodayInputDate());
    setAddHoldingError(null);
    setIsAddHoldingOpen(true);
  };

  const openEditHoldingModal = (row: Holding) => {
    const isClosed = Boolean(row.isClosed);
    const sellDateInput = isClosed ? displayDateToInputDate(String(row.sellDate || '')) : '';
    const buyDateInput = displayDateToInputDate(row.buyDate);
    const sellPriceValue = Number(row.currentPrice);
    lastEditHoldingSnapshotRef.current = row;
    setAddHoldingMode('edit');
    setEditHoldingId(row.id);
    setDraftSymbol(String(row.symbol || '').trim().toUpperCase());
    setDraftOpenDate((isClosed ? sellDateInput : buyDateInput) || getTodayInputDate());
    setDraftShares(String(row.shares ?? ''));
    setDraftBuyPrice(String(isClosed && Number.isFinite(sellPriceValue) ? sellPriceValue : (row.buyPrice ?? '')));
    setDraftBuyAlert(row.buyPriceAlert == null ? '' : String(row.buyPriceAlert));
    setDraftSellAlert(row.sellPriceAlert == null ? '' : String(row.sellPriceAlert));
    setDraftCurrentPrice(Number.isFinite(Number(row.currentPrice)) ? Number(row.currentPrice) : null);
    setSymbolSearchBusy(false);
    setShowSymbolAutocomplete(false);
    setSymbolSelectedIndex(-1);
    setAddHoldingError(null);
    setIsAddHoldingOpen(true);
  };

  const closeAddHoldingModal = () => {
    setIsAddHoldingOpen(false);
    setAddHoldingMode('add');
    setEditHoldingId(null);
    setHoldingAddLaunchSource('portfolios');
    setAddHoldingError(null);
    resetDraftHolding();
  };

  const parseNumOrNull = (raw: string): number | null => {
    const t = String(raw ?? '').trim();
    if (!t) return null;

    let cleaned = t.replace(/\s+/g, '');
    cleaned = cleaned.replace(/[$₪€£]/g, '');

    const hasComma = cleaned.includes(',');
    const hasDot = cleaned.includes('.');
    if (hasComma && !hasDot) cleaned = cleaned.replace(',', '.');
    cleaned = cleaned.replace(/,/g, '');

    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  };

  const normalizeSymbol = useCallback((raw: string): string => String(raw || '').trim().toUpperCase(), []);
  const normalizeSearchSymbolInput = useCallback((raw: string): string => {
    const normalized = normalizeSymbol(raw).replace(/\s+/g, ' ');
    const classShareMatch = normalized.match(/^([A-Z]{1,5})\s+([A-Z])$/);
    if (classShareMatch) return `${classShareMatch[1]}.${classShareMatch[2]}`;
    return normalized;
  }, [normalizeSymbol]);

  const getLatestChangePct = useCallback(
    (symbol: string, fallback?: number | null): number | null => {
      const key = normalizeSymbol(symbol);
      const cached = quotesBySymbolRef.current.get(key);
      const next = cached?.changePct ?? null;
      return next == null ? (fallback ?? null) : next;
    },
    [normalizeSymbol]
  );

  const getLatestCurrentPrice = useCallback(
    (symbol: string, fallback?: number | null): number | null => {
      const key = normalizeSymbol(symbol);
      const cached = quotesBySymbolRef.current.get(key);
      const next = cached?.currentPrice ?? null;
      return next == null ? (fallback ?? null) : next;
    },
    [normalizeSymbol]
  );

  const formatCurrencyAlert = (value: number | null | undefined) => {
    const n = normalizeAlertValue(value);
    return n == null ? '' : formatCurrency(n);
  };

  const normalizeStoredStatus = (raw: string): Holding['status'] => {
    switch (raw) {
      case 'Not Reached':
      case 'Buy Triggered':
      case 'Sell Triggered':
      case 'Within 5% of Buy Target':
      case 'Within 5% of Sell Target':
      case 'Within 10% of Buy Target':
      case 'Within 10% of Sell Target':
        return raw;
      default:
        return '';
    }
  };

  const renderAlertStatus = (row: Holding) => {
    const text = String(row.status || '').trim();
    if (!text) return null;

    const isTriggered = text === 'Buy Triggered' || text === 'Sell Triggered';
    const targets = [
      row.buyPriceAlert != null && Number.isFinite(Number(row.buyPriceAlert)) && Number(row.buyPriceAlert) !== 0
        ? { label: 'Buy Target', value: Number(row.buyPriceAlert) }
        : null,
      row.sellPriceAlert != null && Number.isFinite(Number(row.sellPriceAlert)) && Number(row.sellPriceAlert) !== 0
        ? { label: 'Sell Target', value: Number(row.sellPriceAlert) }
        : null,
    ].filter((target): target is { label: string; value: number } => target != null);
    const currentPrice = Number(row.currentPrice);
    const nearestTarget = Number.isFinite(currentPrice)
      ? targets
        .map((target) => ({ ...target, distancePct: Math.abs((currentPrice - target.value) / target.value) * 100 }))
        .sort((a, b) => a.distancePct - b.distancePct)[0]
      : undefined;
    const distancePct = nearestTarget?.distancePct ?? null;
    const targetLabel = nearestTarget?.label ?? 'Target';

    if (!isTriggered && distancePct != null && distancePct <= 10) {
      const isClose = distancePct <= 5;
      return (
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 185, height: 31, boxSizing: 'border-box', borderRadius: 20, gap: 10, padding: '6px 10px', background: isClose ? '#FF572224' : '#FFC10724', color: isClose ? '#c2410c' : '#8a5a00', whiteSpace: 'nowrap', lineHeight: 1.2, fontSize: 14 }}>
          {`Within ${isClose ? '5%' : '10%'} of ${targetLabel}`}
        </span>
      );
    }

    const iconSrc = text === 'Not Reached' ? '/images/grayalert.svg' : '/images/redalert.svg';
    const isNotReached = text === 'Not Reached';
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          lineHeight: 1,
          marginLeft: isNotReached ? -10 : 0,
        }}
      >
        <span
          style={{
            width: 18,
            height: 18,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '0 0 auto',
          }}
        >
          {isNotReached ? (
            <img src={iconSrc} alt="" aria-hidden="true" style={{ width: 18, height: 18, display: 'block' }} />
          ) : (
            <>
              <img src="/images/alert-flicker.gif" alt="" aria-hidden="true" style={{ width: 18, height: 18, display: 'block' }} />
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true" style={{ display: 'none' }}>
              <path
                d="M4.93805 17.1447C5.67087 17.7586 6.53875 18.1901 7.47055 18.4038C7.55971 18.433 7.61971 18.3163 7.55971 18.258C5.56388 16.3538 6.66055 14.293 7.47055 13.3638C8.04805 12.7005 8.87055 11.608 8.81055 10.143C8.81055 9.99634 8.96055 9.87884 9.07888 9.96717C10.2114 10.553 10.9855 11.8422 11.1939 12.8963C11.5222 12.5747 11.6414 12.0763 11.6414 11.6372C11.6414 11.4905 11.8197 11.373 11.9689 11.4905C13.0414 12.4572 14.888 15.738 11.9089 18.3155C11.8497 18.3747 11.9089 18.4922 11.9689 18.463C12.8722 18.2262 13.7217 17.8183 14.4714 17.2613C19.3272 13.5113 16.1689 6.86134 14.113 4.66467C13.8455 4.40051 13.3689 4.57634 13.3689 4.95717C13.3389 5.74884 13.1005 6.62717 12.4755 7.21301C11.9989 5.22134 10.4105 2.95884 8.14638 1.84551C7.84888 1.69884 7.49138 1.93301 7.52138 2.25551C7.57971 4.98051 5.80221 6.71551 4.25305 8.97134C2.88221 10.9922 1.98888 14.713 4.93805 17.1447Z"
                fill="#FF5722"
              />
              <path
                className="alert-flame-flicker"
                style={{
                  animation: 'alertFlameFlicker 1.8s ease-in-out infinite',
                  filter: 'drop-shadow(0 0 2px rgba(255, 87, 34, 0.65))',
                }}
                d="M4.93805 17.1447C5.67087 17.7586 6.53875 18.1901 7.47055 18.4038C7.55971 18.433 7.61971 18.3163 7.55971 18.258C5.56388 16.3538 6.66055 14.293 7.47055 13.3638C8.04805 12.7005 8.87055 11.608 8.81055 10.143C8.81055 9.99634 8.96055 9.87884 9.07888 9.96717C10.2114 10.553 10.9855 11.8422 11.1939 12.8963C11.5222 12.5747 11.6414 12.0763 11.6414 11.6372C11.6414 11.4905 11.8197 11.373 11.9689 11.4905C13.0414 12.4572 14.888 15.738 11.9089 18.3155C11.8497 18.3747 11.9089 18.4922 11.9689 18.463C12.8722 18.2262 13.7217 17.8183 14.4714 17.2613C19.3272 13.5113 16.1689 6.86134 14.113 4.66467C13.8455 4.40051 13.3689 4.57634 13.3689 4.95717C13.3389 5.74884 13.1005 6.62717 12.4755 7.21301C11.9989 5.22134 10.4105 2.95884 8.14638 1.84551C7.84888 1.69884 7.49138 1.93301 7.52138 2.25551C7.57971 4.98051 5.80221 6.71551 4.25305 8.97134C2.88221 10.9922 1.98888 14.713 4.93805 17.1447Z"
                fill="#FF7A50"
              />
            </svg>
            </>
          )}
        </span>
        <span
          style={
            isNotReached
              ? { display: 'inline-flex', alignItems: 'center', minHeight: 18, fontWeight: 400, color: '#5A5A5A', lineHeight: 1, transform: 'translateY(1px)' }
              : { display: 'inline-flex', alignItems: 'center', minHeight: 18, lineHeight: 1, transform: 'translateY(1px)' }
          }
        >
          {text}
        </span>
      </span>
    );
  };

  const getAlertStatusSortMeta = useCallback((row: Holding): { rank: number; text: string } => {
    const text = String(row.status || '').trim();
    if (!text) return { rank: 4, text: '' };

    const isTriggered = text === 'Buy Triggered' || text === 'Sell Triggered';
    if (isTriggered) return { rank: 0, text };

    const isSellTarget = text === 'Sell Triggered' || (row.sellPriceAlert != null && row.buyPriceAlert == null);
    const target = isSellTarget ? row.sellPriceAlert : row.buyPriceAlert;
    const targetLabel = isSellTarget ? 'Sell Target' : 'Buy Target';
    const distancePct = target != null && Number.isFinite(Number(target)) && Number(target) !== 0 && Number.isFinite(Number(row.currentPrice))
      ? Math.abs((Number(row.currentPrice) - Number(target)) / Number(target)) * 100
      : null;

    if (distancePct != null && distancePct <= 10) {
      const isClose = distancePct <= 5;
      return {
        rank: isClose ? 1 : 2,
        text: `Within ${isClose ? '5%' : '10%'} of ${targetLabel}`,
      };
    }

    return { rank: 3, text };
  }, []);

  const uniqueStocksList = useMemo(() => {
    const list = stocksList as StockListItem[];
    const excluded = new Set(['MAG7']);
    const seen = new Set<string>();
    return list.filter((stock) => {
      if (excluded.has(stock.symbol)) return false;
      const key = normalizeSymbol(stock.symbol);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, []);

  const filteredStocks = useMemo(() => {
    const raw = draftSymbol.trim();
    if (!raw) return [] as StockListItem[];

    const query = raw.toLowerCase();
    const querySym = normalizeSearchSymbolInput(raw);
    const normalizedQuery = query.replace(/[^a-z0-9]+/g, ' ').trim();

    const score = (stock: StockListItem): number => {
      const sym = normalizeSymbol(stock.symbol);
      const name = String(stock.name || '').toLowerCase();
      const normalizedName = name.replace(/[^a-z0-9]+/g, ' ').trim();
      const cat = String(stock.category || '').toLowerCase();

      if (sym === 'V' && querySym.startsWith('VIS')) return 1100;
      if (sym === querySym) return 1000;
      if (normalizedQuery && normalizedName === normalizedQuery) return 980;
      if (normalizedQuery && normalizedName.startsWith(normalizedQuery)) return 950;
      if (sym.startsWith(querySym)) return 900;
      if (sym.includes(querySym)) return 800;
      if (name.includes(query)) return 600;
      if (cat.includes(query)) return 500;
      return 0;
    };

    const merged = [...remoteStockResults, ...uniqueStocksList];
    const seen = new Set<string>();

    return merged
      .filter((stock) => {
        const key = normalizeSymbol(stock.symbol);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((s) => ({ stock: s, score: score(s) }))
      .filter((x) => x.score > 0)
      .sort((a, b) =>
        (b.score - a.score) ||
        (getExchangeSearchPriority(a.stock.exchange) - getExchangeSearchPriority(b.stock.exchange)) ||
        a.stock.symbol.localeCompare(b.stock.symbol)
      )
      .slice(0, 25)
      .map((x) => x.stock);
  }, [draftSymbol, normalizeSearchSymbolInput, normalizeSymbol, remoteStockResults, uniqueStocksList]);

  useEffect(() => {
    if (!isAddHoldingOpen) return;
    const raw = draftSymbol.trim();
    if (!raw) {
      setRemoteStockResults([]);
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      const queries = Array.from(new Set([
        raw,
        normalizeSearchSymbolInput(raw),
        normalizeSymbol(raw).replace(/\s+/g, '-'),
      ].map((value) => String(value || '').trim()).filter(Boolean)));

      try {
        const batches = await Promise.all(queries.map((queryText) => searchStocks(queryText, 50).catch(() => [])));
        if (cancelled) return;

        const mapped = batches.flat().map((result) => {
          const sector = String(result.sector || '').trim();
          const industry = String(result.industry || '').trim();
          const exchange = String(result.exchangeShortName || result.stockExchange || result.exchange || '').trim();
          const exchangeName = String(result.stockExchange || result.exchange || '').trim();
          return {
            symbol: String(result.symbol || '').trim(),
            name: String(result.name || '').trim(),
            exchange,
            exchangeName: exchangeName !== exchange ? exchangeName : '',
            category: sector && industry ? `${sector} / ${industry}` : (sector || industry || ''),
          };
        }).filter((result) => result.symbol);

        setRemoteStockResults(Array.from(new Map(mapped.map((row) => [normalizeSymbol(row.symbol), row])).values()));
      } catch {
        if (!cancelled) setRemoteStockResults([]);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [draftSymbol, isAddHoldingOpen, normalizeSearchSymbolInput, normalizeSymbol]);

  useEffect(() => {
    if (!isAddHoldingOpen) return;

    const onDocMouseDown = (event: MouseEvent) => {
      const root = symbolAutocompleteRef.current;
      const input = symbolInputRef.current;
      const target = event.target as Node;
      if (root && root.contains(target)) return;
      if (input && input.contains(target)) return;
      setShowSymbolAutocomplete(false);
      setSymbolSelectedIndex(-1);
    };

    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [isAddHoldingOpen]);

  const runSymbolSearch = async (maybeSymbol?: string) => {
    const raw = maybeSymbol ?? draftSymbol;
    let sym = normalizeSearchSymbolInput(raw);
    if (!sym) {
      onNotify?.('Please enter a stock symbol/name.', 'error');
      return;
    }

    const queryCandidates = Array.from(new Set([
      sym,
      normalizeSymbol(raw),
      normalizeSymbol(raw).replace(/\s+/g, '-'),
    ].map((value) => String(value || '').trim()).filter(Boolean)));

    setDraftSymbol(sym);
    setShowSymbolAutocomplete(false);
    setSymbolSelectedIndex(-1);

    setSymbolSearchBusy(true);
    try {
      for (const queryText of queryCandidates) {
        const matches = await searchStocks(queryText, 50).catch(() => []);
        const exact = matches.find((item) => {
          const itemSymbol = normalizeSymbol(item.symbol);
          const itemCompact = itemSymbol.replace(/[.\-\s]/g, '');
          const queryCompact = normalizeSymbol(queryText).replace(/[.\-\s]/g, '');
          return itemSymbol === normalizeSymbol(queryText) || itemCompact === queryCompact;
        }) ?? matches[0];

        if (exact?.symbol) {
          sym = normalizeSymbol(exact.symbol);
          setDraftSymbol(sym);
          break;
        }
      }

      if (!authToken) return;

      const rows = await adminApi.getQuotesCache(authToken, [sym]);
      const price = Number(rows?.[0]?.currentPrice);
      if (Number.isFinite(price) && price > 0) setDraftCurrentPrice(price);
    } catch {
      // Keep UI usable even if quote fetch fails.
    } finally {
      setSymbolSearchBusy(false);
    }
  };

  const openAddHoldingModalWithPrefilledSymbol = (rawSymbol: string, source: HoldingAddLaunchSource) => {
    const sym = normalizeSearchSymbolInput(rawSymbol);
    if (!sym) return;

    dismissPendingHoldingAdd();
    setHoldingAddLaunchSource(source);

    setActiveTab('holdings');
    setAddHoldingMode('add');
    setEditHoldingId(null);
    resetDraftHolding();
    setDraftSymbol(sym);
    setShowSymbolAutocomplete(false);
    setSymbolSelectedIndex(-1);
    setIsAddHoldingOpen(true);

    void runSymbolSearch(sym);
  };

  useEffect(() => {
    if (!externalAddHoldingRequest) return;
    openAddHoldingModalWithPrefilledSymbol(externalAddHoldingRequest.symbol, 'header');
    onExternalAddHoldingConsumed?.();
  }, [externalAddHoldingRequest, onExternalAddHoldingConsumed, openAddHoldingModalWithPrefilledSymbol]);

  const captureElementToCanvas = async (el: HTMLElement): Promise<HTMLCanvasElement | null> => {
    try {
      // Disable transforms that can confuse html2canvas measurements.
      const prevTransform = el.style.transform;
      el.style.transform = 'none';
      const canvas = await html2canvas(el, {
        backgroundColor: null,
        scale: 3,
        useCORS: true,
        logging: false,
      } as any);
      el.style.transform = prevTransform;
      return canvas;
    } catch {
      return null;
    }
  };

  const toDataUrlWithFallback = (canvas: HTMLCanvasElement, type: string, quality: number): { type: string; dataUrl: string } => {
    try {
      const dataUrl = canvas.toDataURL(type as any, quality as any);
      if (typeof dataUrl === 'string' && dataUrl.startsWith('data:')) return { type, dataUrl };
    } catch {
      // ignore and fall back
    }
    const fallbackType = 'image/png';
    return { type: fallbackType, dataUrl: canvas.toDataURL(fallbackType) };
  };

  const makeThumbDataUrl = async (canvas: HTMLCanvasElement, maxWidthPx: number, quality: number): Promise<string> => {
    const srcW = canvas.width;
    const srcH = canvas.height;
    if (!srcW || !srcH) return toDataUrlWithFallback(canvas, 'image/webp', quality).dataUrl;

    const scale = Math.min(1, maxWidthPx / srcW);
    const dstW = Math.max(1, Math.floor(srcW * scale));
    const dstH = Math.max(1, Math.floor(srcH * scale));

    const thumb = document.createElement('canvas');
    thumb.width = dstW;
    thumb.height = dstH;
    const ctx = thumb.getContext('2d');
    if (!ctx) return toDataUrlWithFallback(canvas, 'image/webp', quality).dataUrl;

    ctx.drawImage(canvas, 0, 0, dstW, dstH);
    return toDataUrlWithFallback(thumb, 'image/webp', quality).dataUrl;
  };

  const saveChartBookmark = async (chartId: string, title: string, fullTitle?: string) => {
    if (!canPersist) {
      onNotify?.('Please login to save screenshots.', 'error');
      return;
    }

    if (savingBookmarkByChartId[chartId]) return;

    // While the icon is in the 30s "saved" state, ignore repeat clicks.
    // After it returns to default, allow saving again.
    if (bookmarkFlashByChartId[chartId]) return;

    setSavingBookmarkByChartId((prev) => ({ ...prev, [chartId]: true }));

    try {
      const root = document.getElementById(chartId);
      if (!root) {
        onNotify?.('Chart is not ready to capture yet.', 'error');
        return;
      }

      const captured = await captureElementToCanvas(root);
      if (!captured) {
        onNotify?.('Chart is not ready to capture yet.', 'error');
        return;
      }

      const full = toDataUrlWithFallback(captured, 'image/png', 1);
      const thumbDataUrl = await makeThumbDataUrl(captured, 1100, 0.82);

      const resolvedTitle = fullTitle || `${selectedPortfolioName} • ${title}`;

      await adminApi.createResearchDeskScreenshot(authToken as string, {
        userId: resolvedUserId,
        sourcePage: 'Portfolios',
        stockSymbol: 'PORTFOLIO',
        title: resolvedTitle,
        imageDataUrl: full.dataUrl,
        thumbDataUrl,
      });

      triggerBookmarkFlash(chartId);
      void `${title} • Screenshot saved`;
    } catch (e: any) {
      if (isUnauthorized(e)) {
        onUnauthorized?.();
        return;
      }
      onNotify?.(e?.message || 'Failed saving screenshot', 'error');
    } finally {
      setSavingBookmarkByChartId((prev) => ({ ...prev, [chartId]: false }));
    }
  };

  // On load, reflect saved screenshots as "bookmarked" so the icon persists.
  useEffect(() => {
    if (!canPersist) {
      setSavingBookmarkByChartId({});
      return;
    }

    let alive = true;
    (async () => {
      try {
        const items = await adminApi.listResearchDeskScreenshots(authToken as string, {
          userId: resolvedUserId,
          sourcePage: 'Portfolios',
          stockSymbol: 'PORTFOLIO',
          limit: 200,
        });
        if (!alive) return;

        const titles = new Set((items || []).map((x) => String(x.title || '').trim()));
        void titles;
      } catch (e: any) {
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        // Ignore load errors; user can still click bookmark.
      }
    })();

    return () => {
      alive = false;
    };
  }, [authToken, resolvedUserId, canPersist, onUnauthorized, selectedPortfolioName]);

  const [rows, setRows] = useState<Holding[]>([]);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    if (lastAuthUserIdRef.current === authUserId) return;
    lastAuthUserIdRef.current = authUserId;
    holdingsCacheRef.current.clear();
    setRows([]);
  }, [authUserId]);

  useEffect(() => {
    if (!selectedPortfolioId) return;
    holdingsCacheRef.current.set(selectedPortfolioId, rows);
  }, [rows, selectedPortfolioId]);

  useEffect(() => {
    if (changePctLoggedRef.current) return;
    if (!rows.length) return;
    const sample = rows.slice(0, 6).map((r) => ({
      symbol: normalizeSymbol(r.symbol),
      changePct: r.changePct ?? null,
      currentPrice: r.currentPrice ?? null,
    }));
    changePctLoggedRef.current = true;
  }, [rows, normalizeSymbol]);

  const toDisplayDate = (utc: string | null | undefined): string => {
    if (!utc) return '';
    const dt = new Date(utc);
    const ms = dt.getTime();
    if (!Number.isFinite(ms)) return '';
    return dt.toLocaleDateString('en-GB');
  };

  const getClosedMetrics = (r: Holding) => {
    const buy = Number(r.buyPrice || 0);
    const shares = Number(r.shares || 0);
    const sell = r.currentPrice != null && Number.isFinite(Number(r.currentPrice)) ? Number(r.currentPrice) : NaN;

    const profitLoss = Number.isFinite(sell) ? (sell - buy) * shares : NaN;
    const changePct = buy > 0 && Number.isFinite(sell) ? ((sell - buy) / buy) * 100 : NaN;
    return { profitLoss, changePct, sellPrice: sell };
  };

  useEffect(() => {
    if (!authToken) {
      setPortfolios([]);
      setSelectedPortfolioId(null);
      setPortfoliosLoaded(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const list = await adminApi.listMyPortfolios(authToken);
        if (cancelled) return;

        const mapped = (list ?? []).map((p) => ({ id: p.id, name: p.name, isDefault: Boolean(p.isDefault) }));
        setPortfolios(mapped);

        const defaultId = (list ?? []).find((p) => p.isDefault)?.id ?? (list ?? [])[0]?.id ?? null;
        setSelectedPortfolioId((prev) => {
          let nextId: number | null = null;
          if (prev === ALL_PORTFOLIOS_ID) {
            nextId = prev;
          } else if (prev != null && (list ?? []).some((p) => p.id === prev)) {
            nextId = prev;
          } else {
            nextId = (list ?? []).length > 1 ? ALL_PORTFOLIOS_ID : defaultId;
          }

          if (nextId != null) {
            const cached = holdingsCacheRef.current.get(nextId);
            if (cached) {
              setRows(cached);
            }
          }

          return nextId;
        });
        setPortfoliosLoaded(true);
      } catch (e: any) {
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        onNotify?.(e?.message || 'Failed loading portfolios', 'error');
        setPortfolios([]);
        setSelectedPortfolioId(null);
        setPortfoliosLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authToken, onNotify, onUnauthorized]);

  const refreshHoldingsFromDb = useCallback(async () => {
    const loadId = ++holdingsLoadIdRef.current;
    setIsHoldingsLoading(true);
    setPortfolioError(null);

    if (holdingsLoadTimeoutRef.current != null) {
      window.clearTimeout(holdingsLoadTimeoutRef.current);
    }
    holdingsLoadTimeoutRef.current = window.setTimeout(() => {
      if (holdingsLoadIdRef.current === loadId) {
        setIsHoldingsLoading(false);
      }
    }, 8000);

    const finishLoading = () => {
      if (holdingsLoadIdRef.current === loadId) {
        setIsHoldingsLoading(false);
      }
      if (holdingsLoadTimeoutRef.current != null) {
        window.clearTimeout(holdingsLoadTimeoutRef.current);
        holdingsLoadTimeoutRef.current = null;
      }
    };

    if (!authToken) {
      setRows([]);
      finishLoading();
      return;
    }

    if (!selectedPortfolioId) {
      setRows([]);
      if (portfoliosLoaded) {
        setPortfolioError('Please select a portfolio.');
      }
      finishLoading();
      return;
    }
    try {
      const items = selectedPortfolioId === ALL_PORTFOLIOS_ID
        ? await adminApi.listMyPortfolioHoldingsAll(authToken)
        : await adminApi.listMyPortfolioHoldingsByPortfolio(authToken, selectedPortfolioId);
      const mapped: Holding[] = (items ?? []).map((it) => {
        const portfolioIdRaw = Number((it as any).portfolioId ?? (it as any).PortfolioId);
        const portfolioId = Number.isFinite(portfolioIdRaw) ? portfolioIdRaw : 0;
        const buyDate = toDisplayDate((it as any).buyDateUtc ?? (it as any).createdAtUtc);
        const isClosed = Boolean((it as any).isClosed);
        const sellDate = isClosed ? toDisplayDate((it as any).sellDateUtc ?? (it as any).updatedAtUtc) : '';

        const buyPriceRaw = Number((it as any).buyPrice);
        const buyPrice = Number.isFinite(buyPriceRaw) ? buyPriceRaw : 0;

        const sharesRaw = Number((it as any).shares);
        const shares = Number.isFinite(sharesRaw) ? sharesRaw : 0;

        const currentPriceSource = (it as any).currentPrice;
        const currentPriceRaw = currentPriceSource == null ? null : Number(currentPriceSource);
        const currentPrice = currentPriceRaw != null && Number.isFinite(currentPriceRaw) && currentPriceRaw > 0 ? currentPriceRaw : null;

        const buyAlert = normalizeAlertValue((it as any).buyPriceAlert);
        const sellAlert = normalizeAlertValue((it as any).sellPriceAlert);
        const status = normalizeStoredStatus(computeAlertStatus(currentPrice, buyAlert, sellAlert));

        return {
          id: String((it as any).id),
          portfolioId,
          symbol: String((it as any).symbol || '').trim().toUpperCase(),
          buyDate: buyDate || new Date().toLocaleDateString('en-GB'),
          sellDate,
          buyPrice,
          currentPrice,
          changePct: null,
          shares,
          buyPriceAlert: buyAlert,
          sellPriceAlert: sellAlert,
          status,
          isClosed,
        };
      });

      // Requirement: for the same symbol, Current Price must be identical across all holdings.
      // First unify using the best DB value we have (max positive), then the quote-enrichment effect
      // will overwrite with the latest market price when available.
      const unifiedPriceBySymbol = new Map<string, number>();
      for (const r of mapped) {
        // Closed rows use `currentPrice` to store the Sell Price snapshot and must never be unified.
        if (r.isClosed) continue;
        const sym = normalizeSymbol(r.symbol);
        const p = r.currentPrice;
        if (!sym) continue;
        if (p == null || !Number.isFinite(p) || p <= 0) continue;
        const prev = unifiedPriceBySymbol.get(sym);
        unifiedPriceBySymbol.set(sym, prev != null ? Math.max(prev, p) : p);
      }

      let normalized = unifiedPriceBySymbol.size
        ? mapped.map((r) => {
            if (r.isClosed) return r;
            const sym = normalizeSymbol(r.symbol);
            const p = sym ? unifiedPriceBySymbol.get(sym) : undefined;
            return p != null ? { ...r, currentPrice: p, status: computeAlertStatus(p, r.buyPriceAlert, r.sellPriceAlert) } : r;
          })
        : mapped;

      const symbols = Array.from(
        new Set(
          normalized
            .filter((r) => !r.isClosed)
            .map((r) => normalizeSymbol(r.symbol))
            .filter(Boolean)
        )
      );

      if (authToken && symbols.length) {
        try {
          const result = await adminApi.getQuotesCacheWithMeta(authToken, symbols);
          const quotes = result?.rows ?? [];
          const bySymbol = new Map(quotes.map((q) => [normalizeSymbol(q.symbol), q]));

          normalized = normalized.map((r) => {
            if (r.isClosed) return r;
            const cached = bySymbol.get(normalizeSymbol(r.symbol));
            if (!cached) return r;
            const nextPrice = cached.currentPrice == null ? r.currentPrice : Number(cached.currentPrice);
            const nextChange = cached.changePct == null ? r.changePct : Number(cached.changePct);
            return {
              ...r,
              currentPrice: Number.isFinite(nextPrice) ? nextPrice : r.currentPrice,
              changePct: Number.isFinite(nextChange) ? nextChange : r.changePct,
              status: Number.isFinite(nextPrice) ? computeAlertStatus(nextPrice, r.buyPriceAlert, r.sellPriceAlert) : r.status,
            };
          });

          quotesBySymbolRef.current = new Map(
            quotes.map((q) => [
              normalizeSymbol(q.symbol),
              {
                currentPrice: q.currentPrice == null ? null : Number(q.currentPrice),
                changePct: q.changePct == null ? null : Number(q.changePct),
              },
            ])
          );
          setQuotesVersion((v) => v + 1);
        } catch {
          // Best-effort: fall back to DB values if quotes fetch fails.
        }
      }

      const pendingDeleteId = pendingHoldingDeleteRef.current?.holdingId;
      const filtered = pendingDeleteId ? normalized.filter((r) => r.id !== pendingDeleteId) : normalized;

      if (holdingsLoadIdRef.current !== loadId) return;
      holdingsCacheRef.current.set(selectedPortfolioId, filtered);
      setRows(filtered);
    } catch (e: any) {
      if (isUnauthorized(e)) {
        onUnauthorized?.();
        return;
      }
      if (holdingsLoadIdRef.current !== loadId) return;
      setPortfolioError(e?.message || 'Failed loading portfolio holdings');
      setRows([]);
    } finally {
      finishLoading();
    }
  }, [authToken, computeAlertStatus, isUnauthorized, normalizeSymbol, onUnauthorized, portfoliosLoaded, selectedPortfolioId]);

  useEffect(() => {
    void refreshHoldingsFromDb();
  }, [refreshHoldingsFromDb, authToken]);

  useEffect(() => {
    if (!authToken) return;

    let disposed = false;
    let timerId: number | undefined;
    const scheduleNext = (delayMs: number) => {
      if (timerId != null) window.clearTimeout(timerId);
      timerId = window.setTimeout(() => {
        if (!disposed) void refreshQuotes();
      }, delayMs);
    };

    const refreshQuotes = async () => {
      const rowsSnapshot = rowsRef.current;
      if (!rowsSnapshot.length) {
        setIsQuotesRefreshing(false);
        if (!disposed) {
          scheduleNext(15000);
        }
        return;
      }

      const symbols = Array.from(
        new Set(
          rowsSnapshot
            .filter((r) => !r.isClosed)
            .map((r) => normalizeSymbol(r.symbol))
            .filter(Boolean)
        )
      );

      if (!symbols.length) {
        setIsQuotesRefreshing(false);
        if (!disposed) {
          scheduleNext(15000);
        }
        return;
      }

      try {
        if (refreshTintTimeoutRef.current == null) {
          setIsQuotesRefreshing(true);
          refreshTintTimeoutRef.current = window.setTimeout(() => {
            setIsQuotesRefreshing(false);
            refreshTintTimeoutRef.current = null;
          }, 2000);
        }

        const result = await adminApi.getQuotesCacheWithMeta(authToken, symbols);
        const quotes = result?.rows ?? [];
        const ttlSecRaw = result?.meta?.ttlSec;
        const ttlSec = Number.isFinite(ttlSecRaw) ? Number(ttlSecRaw) : null;
        if (disposed) return;

        const lastUpdatedBySymbol = lastQuotesUpdatedRef.current;
        const bySymbol = new Map(
          (quotes ?? []).map((q) => [normalizeSymbol(q.symbol), q])
        );
        const sampleSymbols = rowsSnapshot.slice(0, 6).map((r) => normalizeSymbol(r.symbol)).filter(Boolean);
        const sample = sampleSymbols.map((sym) => {
          const cached = bySymbol.get(sym);
          return {
            symbol: sym,
            changePct: cached?.changePct ?? null,
            currentPrice: cached?.currentPrice ?? null,
          };
        });

        setRows((prev) => {
          let changed = false;
          const next = prev.map((row) => {
            if (row.isClosed) return row;
            const cached = bySymbol.get(normalizeSymbol(row.symbol));
            if (!cached) return row;

            const nextPriceRaw = cached.currentPrice;
            const nextPrice = nextPriceRaw == null ? NaN : Number(nextPriceRaw);
            const hasNextPrice = Number.isFinite(nextPrice);
            const updatedStatus = hasNextPrice ? computeAlertStatus(nextPrice, row.buyPriceAlert, row.sellPriceAlert) : row.status;
            const isSamePrice = hasNextPrice ? row.currentPrice === nextPrice : true;
            const isSameStatus = row.status === updatedStatus;
            const nextChangeRaw = cached.changePct == null ? row.changePct : Number(cached.changePct);
            const nextChange = nextChangeRaw == null ? null : (Number.isFinite(Number(nextChangeRaw)) ? Number(nextChangeRaw) : null);
            const isSameChange = (row.changePct == null && nextChange == null)
              || (row.changePct != null && nextChange != null && Number(row.changePct) === Number(nextChange));

            if (isSamePrice && isSameStatus && isSameChange) return row;

            changed = true;
            return {
              ...row,
              currentPrice: hasNextPrice ? nextPrice : row.currentPrice,
              status: updatedStatus,
              changePct: nextChange,
            };
          });

          return changed ? next : prev;
        });

        for (const quote of quotes) {
          const key = normalizeSymbol(quote.symbol);
          if (!key) continue;
          const updatedAt = String(quote.updatedAtUtc ?? '').trim();
          if (updatedAt) lastUpdatedBySymbol.set(key, updatedAt);
        }

        quotesBySymbolRef.current = new Map(
          Array.from(bySymbol.entries()).map(([key, q]) => [
            key,
            {
              currentPrice: q.currentPrice == null ? null : Number(q.currentPrice),
              changePct: q.changePct == null ? null : Number(q.changePct),
            },
          ])
        );
        setQuotesVersion((v) => v + 1);

        const nextDelayMs = Math.max(15_000, Math.min((ttlSec ?? 300) * 1000, 5 * 60 * 1000));
        scheduleNext(nextDelayMs);
      } catch (err) {
        if (disposed) return;
        if (isUnauthorized(err)) {
          onUnauthorized?.();
        } else if (!disposed) {
          scheduleNext(30000);
        }
      }
    };

    void refreshQuotes();

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      void refreshQuotes();
    };
    const onFocus = () => {
      if (document.visibilityState !== 'visible') return;
      void refreshQuotes();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      if (refreshTintTimeoutRef.current != null) {
        window.clearTimeout(refreshTintTimeoutRef.current);
        refreshTintTimeoutRef.current = null;
      }
      setIsQuotesRefreshing(false);
      if (timerId != null) window.clearTimeout(timerId);
    };
  }, [authToken, normalizeSymbol, onUnauthorized]);

  const restoreHoldingRow = useCallback((pending: { row: Holding; index: number }) => {
    setRows((prev) => {
      if (prev.some((r) => r.id === pending.row.id)) return prev;
      const next = [...prev];
      const idx = Math.max(0, Math.min(pending.index, next.length));
      next.splice(idx, 0, pending.row);
      return next;
    });
  }, []);

  const commitPendingHoldingDelete = useCallback(
    async (holdingId: string) => {
      const pending = pendingHoldingDeleteRef.current;
      if (!pending || pending.holdingId !== holdingId) return;

      window.clearTimeout(pending.timerId);
      pendingHoldingDeleteRef.current = null;
      setPendingHoldingDelete(null);

      if (!authToken) return;

      try {
        const id = Number(holdingId);
        if (Number.isFinite(id) && id > 0) await adminApi.deleteMyPortfolioHolding(authToken, id);
        await refreshHoldingsFromDb();
      } catch (e: any) {
        restoreHoldingRow({ row: pending.row, index: pending.index });
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        onNotify?.(e?.message || 'Failed deleting holding', 'error');
      }
    },
    [authToken, onNotify, onUnauthorized, refreshHoldingsFromDb, restoreHoldingRow]
  );

  const undoPendingHoldingDelete = useCallback(() => {
    const pending = pendingHoldingDeleteRef.current;
    if (!pending) return;
    window.clearTimeout(pending.timerId);
    pendingHoldingDeleteRef.current = null;
    setPendingHoldingDelete(null);
    restoreHoldingRow({ row: pending.row, index: pending.index });
  }, [restoreHoldingRow]);

  const undoPendingHoldingEdit = useCallback(() => {
    const pending = pendingHoldingEditUndoRef.current;
    if (!pending) return;

    window.clearTimeout(pending.timerId);
    pendingHoldingEditUndoRef.current = null;
    setPendingHoldingEditUndo(null);

    const snapshot = pending.snapshot;

    if (!authToken) {
      setRows((prev) => prev.map((r) => (r.id === snapshot.id ? snapshot : r)));
      return;
    }

    void (async () => {
      try {
        const holdingId = Number(snapshot.id);
        const buyDateUtc = (() => {
          const input = displayDateToInputDate(snapshot.buyDate);
          return input ? inputDateToUtcIso(input) : null;
        })();

        const sellDateUtc = (() => {
          if (!snapshot.isClosed) return null;
          const input = displayDateToInputDate(String(snapshot.sellDate || ''));
          return input ? inputDateToUtcIso(input) : null;
        })();

        await adminApi.upsertMyPortfolioHolding(authToken, {
          id: Number.isFinite(holdingId) && holdingId > 0 ? holdingId : null,
          portfolioId: pending.portfolioId,
          symbol: snapshot.symbol,
          shares: snapshot.shares,
          buyPrice: snapshot.buyPrice,
          buyDateUtc,
          currentPrice: snapshot.currentPrice,
          buyPriceAlert: snapshot.buyPriceAlert ?? null,
          sellPriceAlert: snapshot.sellPriceAlert ?? null,
          status: snapshot.status || computeAlertStatus(snapshot.currentPrice, snapshot.buyPriceAlert ?? null, snapshot.sellPriceAlert ?? null),
          isClosed: Boolean(snapshot.isClosed),
          sellDateUtc,
        });

        await refreshHoldingsFromDb();
      } catch (e: any) {
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        onNotify?.(e?.message || 'Failed undo', 'error');
      }
    })();
  }, [authToken, onNotify, onUnauthorized, refreshHoldingsFromDb]);

  const undoPendingHoldingSell = useCallback(() => {
    const pending = pendingHoldingSellUndoRef.current;
    if (!pending) return;

    window.clearTimeout(pending.timerId);
    pendingHoldingSellUndoRef.current = null;
    setPendingHoldingSellUndo(null);

    if (pending.kind === 'local') {
      setRows(pending.rows);
      return;
    }

    if (!authToken) {
      onNotify?.('Undo is only available when logged in.', 'error');
      return;
    }

    void (async () => {
      try {
        if (pending.kind === 'closed-add') {
          await adminApi.deleteMyPortfolioHolding(authToken, pending.createdId);
        } else if (pending.kind === 'sell-all') {
          await adminApi.upsertMyPortfolioHolding(authToken, {
            id: pending.holdingId,
            portfolioId: pending.portfolioId,
            symbol: pending.snapshot.symbol,
            shares: pending.snapshot.shares,
            buyPrice: pending.snapshot.buyPrice,
            buyDateUtc: (() => {
              const input = displayDateToInputDate(pending.snapshot.buyDate);
              return input ? inputDateToUtcIso(input) : null;
            })(),
            currentPrice: pending.snapshot.currentPrice,
            buyPriceAlert: pending.snapshot.buyPriceAlert ?? null,
            sellPriceAlert: pending.snapshot.sellPriceAlert ?? null,
            status: pending.snapshot.status || computeAlertStatus(pending.snapshot.currentPrice, pending.snapshot.buyPriceAlert ?? null, pending.snapshot.sellPriceAlert ?? null),
            isClosed: false,
            sellDateUtc: null,
          });
        } else if (pending.kind === 'partial') {
          await adminApi.deleteMyPortfolioHolding(authToken, pending.createdClosedId);
          await adminApi.upsertMyPortfolioHolding(authToken, {
            id: pending.holdingId,
            portfolioId: pending.portfolioId,
            symbol: pending.snapshot.symbol,
            shares: pending.snapshot.shares,
            buyPrice: pending.snapshot.buyPrice,
            buyDateUtc: (() => {
              const input = displayDateToInputDate(pending.snapshot.buyDate);
              return input ? inputDateToUtcIso(input) : null;
            })(),
            currentPrice: pending.snapshot.currentPrice,
            buyPriceAlert: pending.snapshot.buyPriceAlert ?? null,
            sellPriceAlert: pending.snapshot.sellPriceAlert ?? null,
            status: pending.snapshot.status || computeAlertStatus(pending.snapshot.currentPrice, pending.snapshot.buyPriceAlert ?? null, pending.snapshot.sellPriceAlert ?? null),
            isClosed: false,
            sellDateUtc: null,
          });
        }

        await refreshHoldingsFromDb();
      } catch (e: any) {
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        onNotify?.(e?.message || 'Failed undo', 'error');
      }
    })();
  }, [authToken, onNotify, onUnauthorized, refreshHoldingsFromDb]);

  const rowsWithQuotes = useMemo(
    () => rows.map((r) => ({
      ...r,
      changePct: getLatestChangePct(r.symbol, r.changePct ?? null),
    })),
    [getLatestChangePct, quotesVersion, rows]
  );

  const openRows = useMemo(() => rowsWithQuotes.filter((r) => !r.isClosed), [rowsWithQuotes]);
  const closedRows = useMemo(() => rowsWithQuotes.filter((r) => r.isClosed), [rowsWithQuotes]);
  const editRowForModal = useMemo(
    () => (addHoldingMode === 'edit' ? rows.find((r) => r.id === editHoldingId) ?? null : null),
    [addHoldingMode, editHoldingId, rows]
  );
  const isEditingClosedForModal = Boolean(editRowForModal?.isClosed);

  // Ensure each symbol gets a persisted (random) palette color.
  useEffect(() => {
    const symbols = Array.from(new Set(rows.map((r) => (r.symbol || '').trim().toUpperCase()).filter(Boolean)));
    const changed = ensureSymbolColorsAssigned(symbols);
    if (changed) setSymbolColorVersion((v) => v + 1);
  }, [rows]);

  const displayedRows = useMemo((): DisplayHolding[] => {
    const base = activeTab === 'holdings' ? openRows : closedRows;
    if (!groupByStock) return base;

    const isClosedTab = activeTab === 'closed';

    const parseDisplayDateToMs = (raw: string): number => {
      // raw: dd/MM/yyyy
      const s = String(raw || '').trim();
      const parts = s.split('/');
      if (parts.length !== 3) return NaN;
      const [dd, mm, yyyy] = parts;
      const d = Number(dd);
      const m = Number(mm);
      const y = Number(yyyy);
      if (!Number.isFinite(d) || !Number.isFinite(m) || !Number.isFinite(y)) return NaN;
      const dt = new Date(y, m - 1, d);
      const ms = dt.getTime();
      return Number.isFinite(ms) ? ms : NaN;
    };

    const pickLatestDate = (a: string, b: string): string => {
      const ams = parseDisplayDateToMs(a);
      const bms = parseDisplayDateToMs(b);
      if (!Number.isFinite(ams) && !Number.isFinite(bms)) return a || b;
      if (!Number.isFinite(ams)) return b;
      if (!Number.isFinite(bms)) return a;
      return ams >= bms ? a : b;
    };

    // Group by symbol (weighted average prices; sum shares; keep dates best-effort)
    const map = new Map<string, DisplayHolding>();
    const mergePortfolioId = (currentId: number, nextId: number): number => {
      if (currentId <= 0 || nextId <= 0) return 0;
      return currentId === nextId ? currentId : 0;
    };

    for (const r of base) {
      const key = (r.symbol || '').trim().toUpperCase();
      if (!key) continue;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, { ...r, symbol: key, lotCount: 1, changePct: r.changePct ?? null });
        continue;
      }

      const totalShares = existing.shares + r.shares;
      const weightedBuy = (existing.buyPrice * existing.shares + r.buyPrice * r.shares) / Math.max(1, totalShares);

      const weightedSell = (() => {
        const a = Number(existing.currentPrice);
        const b = Number(r.currentPrice);
        const aOk = Number.isFinite(a) && a > 0;
        const bOk = Number.isFinite(b) && b > 0;
        if (!aOk && !bOk) return existing.currentPrice;
        if (!aOk) return r.currentPrice;
        if (!bOk) return existing.currentPrice;
        return (a * existing.shares + b * r.shares) / Math.max(1, totalShares);
      })();

      const latestChange = getLatestChangePct(r.symbol, r.changePct ?? null);
      const nextChangePct = latestChange == null
        ? (existing.changePct ?? null)
        : (Number.isFinite(Number(latestChange)) ? Number(latestChange) : (existing.changePct ?? null));
      map.set(key, {
        ...existing,
        portfolioId: mergePortfolioId(Number(existing.portfolioId || 0), Number(r.portfolioId || 0)),
        shares: totalShares,
        buyDate: pickLatestDate(existing.buyDate, r.buyDate),
        buyPrice: weightedBuy,
        sellDate: isClosedTab ? pickLatestDate(String(existing.sellDate || ''), String(r.sellDate || '')) : existing.sellDate,
        currentPrice: isClosedTab
          ? weightedSell
          : ((r.currentPrice != null && r.currentPrice > 0) ? r.currentPrice : existing.currentPrice),
        buyPriceAlert: mergeAlertValue(existing.buyPriceAlert, r.buyPriceAlert),
        sellPriceAlert: mergeAlertValue(existing.sellPriceAlert, r.sellPriceAlert),
        status: computeAlertStatus(
          isClosedTab
            ? weightedSell
            : ((r.currentPrice != null && r.currentPrice > 0) ? r.currentPrice : existing.currentPrice),
          mergeAlertValue(existing.buyPriceAlert, r.buyPriceAlert),
          mergeAlertValue(existing.sellPriceAlert, r.sellPriceAlert)
        ),
        changePct: nextChangePct,
        lotCount: (existing.lotCount ?? 1) + 1,
      });
    }

    return Array.from(map.values()).sort((a, b) => a.symbol.localeCompare(b.symbol));
  }, [activeTab, closedRows, getLatestChangePct, groupByStock, openRows]);

  useEffect(() => {
    if (!holdingActionsOpenForId) return;
    if (!groupByStock) return;
    const openRow = displayedRows.find((r) => r.id === holdingActionsOpenForId);
    const isUnifiedRow = Boolean(openRow && (openRow.lotCount ?? 1) > 1);
    if (isUnifiedRow) closeHoldingActionsMenu();
  }, [closeHoldingActionsMenu, displayedRows, groupByStock, holdingActionsOpenForId]);

  const computeAllocation = (rows: Holding[]) => {
    const bySymbol = new Map<string, number>();
    for (const r of rows) {
      const sym = (r.symbol || '').trim().toUpperCase();
      if (!sym) continue;

      const price = getLatestCurrentPrice(sym, r.currentPrice ?? null);
      const v = (Number(price) || 0) * (r.shares || 0);
      bySymbol.set(sym, (bySymbol.get(sym) || 0) + (Number.isFinite(v) ? v : 0));
    }
    return Array.from(bySymbol.entries())
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]);
  };

  const computePortfolioPnL = useCallback(
    (rows: Holding[]) => {
      let total = 0;
      for (const r of rows) {
        const sym = (r.symbol || '').trim().toUpperCase();
        if (!sym) continue;
        const price = getLatestCurrentPrice(sym, r.currentPrice ?? null);
        if (price == null || !Number.isFinite(price)) continue;
        const buy = Number(r.buyPrice || 0);
        const shares = Number(r.shares || 0);
        if (!Number.isFinite(buy) || !Number.isFinite(shares)) continue;
        total += (price - buy) * shares;
      }
      return total;
    },
    [getLatestCurrentPrice]
  );

  const buildDoughnutData = (allocationRows: Array<[string, number]>) => {
    void symbolColorVersion;
    const labels = allocationRows.map(([sym]) => sym);
    const data = allocationRows.map(([, v]) => v);
    const isSingleSlice = labels.length <= 1;
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: labels.map((sym) => getSymbolColor(sym)),
          borderColor: colors.cardBg,
          borderWidth: isSingleSlice ? 0 : 2,
        },
      ],
    };
  };

  const buildDoughnutOptions = (totalValue: number, allocationLength: number): ChartOptions<'doughnut'> => {
    const isSingleSlice = allocationLength <= 1;
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
          external: (context) => {
            const { chart, tooltip } = context;
            const tooltipEl = ensureTooltipEl(chart, 'portfolio-holdings-tooltip');
            if (!tooltipEl) return;

            if (!tooltip || tooltip.opacity === 0) {
              tooltipEl.style.opacity = '0';
              return;
            }

            const dataIndex = tooltip.dataPoints?.[0]?.dataIndex ?? 0;
            const label = String(chart.data.labels?.[dataIndex] ?? '').trim();
            const val = Number(chart.data.datasets?.[0]?.data?.[dataIndex] ?? 0);
            const pct = totalValue > 0 ? (100 * val) / totalValue : 0;
            const colorsArr = chart.data.datasets?.[0]?.backgroundColor as string[] | undefined;
            const dotColor = colorsArr?.[dataIndex] ?? colors.primary;

            const line2 = `<span>${formatCurrency(val)}</span><span style="margin-left:10px;">${pct.toFixed(2)}%</span>`;

            tooltipEl.innerHTML = `
              <div style="
                width:max-content;
                min-width:169px;
                max-width:calc(100vw - 24px);
                min-height:68px;
                height:auto;
                background:#111827;
                border:1px solid #4B5563;
                color:#FFFFFF;
                opacity:1;
                border-radius:8px;
                padding:12px;
                box-sizing:border-box;
                display:flex;
                flex-direction:column;
                gap:9px;
                font-family: Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
                position:relative;
              ">
                <div style="display:flex;align-items:center;gap:6px;justify-content:flex-start;margin-top:-4px;">
                  <span style="width:10px;height:10px;border-radius:999px;background:${dotColor};display:inline-block;flex:0 0 auto;"></span>
                  <span style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-weight:700;font-size:12px;line-height:100%;letter-spacing:0;color:#D1D5DB;text-align:center;">${label}</span>
                </div>
                <div style="display:flex;align-items:center;gap:10px;white-space:nowrap;">
                  <span style="font-weight:600;font-size:16px;line-height:100%;letter-spacing:0;color:#FFFFFF;">${formatCurrency(val)}</span>
                  <span style="font-weight:300;font-size:16px;line-height:100%;letter-spacing:0;color:#FFFFFF;">${pct.toFixed(2)}%</span>
                </div>
              </div>
            `;

            const arc = chart.getDatasetMeta?.(0)?.data?.[dataIndex] as { x?: number; y?: number } | undefined;
            const centerX = arc?.x ?? (chart.chartArea.left + chart.chartArea.right) / 2;
            const centerY = arc?.y ?? (chart.chartArea.top + chart.chartArea.bottom) / 2;
            const caretX = (tooltip as any)?.caretX ?? centerX;
            const caretY = (tooltip as any)?.caretY ?? centerY;
            const vx = caretX - centerX;
            const vy = caretY - centerY;
            const len = Math.hypot(vx, vy) || 1;
            const outerRadius = (arc as any)?.outerRadius ?? 0;
            const offset = outerRadius > 0 ? outerRadius + 20 : 30;
            const outX = centerX + (vx / len) * offset;
            const outY = centerY + (vy / len) * offset;

            const tooltipRect = tooltipEl.getBoundingClientRect();
            const canvasRect = chart.canvas.getBoundingClientRect();
            const parentRect = (chart.canvas.parentNode as HTMLElement).getBoundingClientRect();

            const targetX = canvasRect.left + outX;
            const targetY = canvasRect.top + outY;
            const placeBelow = vy >= 0;
            const left = targetX - parentRect.left - tooltipRect.width / 2;
            const top = placeBelow
              ? targetY - parentRect.top + 10
              : targetY - parentRect.top - tooltipRect.height - 10;

            tooltipEl.style.left = `${left}px`;
            tooltipEl.style.top = `${top}px`;
            tooltipEl.style.transform = 'translateY(0)';
            tooltipEl.style.opacity = '1';
          },
        },
      },
      rotation: -90,
      circumference: 360,
      cutout: '70%',
      layout: { padding: isSingleSlice ? 6 : 0 },
    };
  };

  const allocation = useMemo(() => computeAllocation(openRows), [computeAllocation, openRows]);

  const openRowsByPortfolio = useMemo(() => {
    const map = new Map<number, Holding[]>();
    for (const r of openRows) {
      const pid = Number(r.portfolioId ?? 0);
      if (!Number.isFinite(pid) || pid <= 0) continue;
      const list = map.get(pid);
      if (list) list.push(r);
      else map.set(pid, [r]);
    }
    return map;
  }, [openRows]);

  const portfolioCharts = useMemo(() => {
    if (!isAllPortfolios) return [] as Array<{
      portfolioId: number;
      name: string;
      allocation: Array<[string, number]>;
      total: number;
      pnl: number;
    }>;

    const out: Array<{
      portfolioId: number;
      name: string;
      allocation: Array<[string, number]>;
      total: number;
      pnl: number;
    }> = [];

    for (const p of portfolios) {
      const rows = openRowsByPortfolio.get(p.id) ?? [];
      const alloc = computeAllocation(rows);
      if (alloc.length === 0) continue;
      const total = alloc.reduce((acc, [, v]) => acc + v, 0);
      const pnl = computePortfolioPnL(rows);
      out.push({ portfolioId: p.id, name: p.name, allocation: alloc, total, pnl });
      if (out.length >= 3) break;
    }

    return out;
  }, [computeAllocation, computePortfolioPnL, isAllPortfolios, openRowsByPortfolio, portfolios]);

  const totalMarketValue = useMemo(() => allocation.reduce((acc, [, v]) => acc + v, 0), [allocation]);

  const ensureTooltipEl = (chart: any, className: string): HTMLDivElement | null => {
    const parent = chart?.canvas?.parentNode as HTMLElement | null;
    if (!parent) return null;

    let el = parent.querySelector<HTMLDivElement>(`:scope > .${className}`);
    if (el) return el;

    el = document.createElement('div');
    el.className = className;
    el.style.position = 'absolute';
    el.style.pointerEvents = 'none';
    el.style.transform = 'translateY(0)';
    el.style.transition = 'opacity 160ms ease, transform 260ms ease, left 320ms ease, top 320ms ease';
    el.style.willChange = 'left, top, transform, opacity';
    el.style.zIndex = '99999';
    parent.style.position = parent.style.position || 'relative';
    parent.style.overflow = 'visible';
    parent.appendChild(el);
    return el;
  };

  const showPortfolioLegendTooltip = (
    chart: DoughnutChartRef | null,
    anchorEl: HTMLElement,
    dataIndex: number,
    totalValue: number,
    clientX?: number,
    clientY?: number
  ) => {
    if (!chart) return;
    const tooltipEl = ensureTooltipEl(chart, 'portfolio-holdings-tooltip');
    if (!tooltipEl) return;

    const label = String(chart.data.labels?.[dataIndex] ?? '').trim();
    const val = Number(chart.data.datasets?.[0]?.data?.[dataIndex] ?? 0);
    const pct = totalValue > 0 ? (100 * val) / totalValue : 0;
    const colorsArr = chart.data.datasets?.[0]?.backgroundColor as string[] | undefined;
    const dotColor = colorsArr?.[dataIndex] ?? colors.primary;
    const bg = isDarkMode ? 'rgba(0,0,0,0.92)' : 'rgba(15,23,42,0.92)';
    const fg = '#ffffff';
    const muted = 'rgba(255,255,255,0.82)';
    const line2 = `<span>${formatCurrency(val)}</span><span style="margin-left:10px;">${pct.toFixed(2)}%</span>`;

    tooltipEl.innerHTML = `
      <div style="
        width:max-content;
        min-width:169px;
        max-width:calc(100vw - 24px);
        min-height:68px;
        height:auto;
        background:#111827;
        border:1px solid #4B5563;
        color:#FFFFFF;
        opacity:1;
        border-radius:8px;
        padding:12px;
        box-sizing:border-box;
        display:flex;
        flex-direction:column;
        gap:9px;
        font-family: Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
        position:relative;
      ">
        <div style="display:flex;align-items:center;gap:6px;justify-content:flex-start;margin-top:-4px;">
          <span style="width:10px;height:10px;border-radius:999px;background:${dotColor};display:inline-block;flex:0 0 auto;"></span>
          <span style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-weight:700;font-size:12px;line-height:100%;letter-spacing:0;color:#D1D5DB;text-align:center;">${label}</span>
        </div>
        <div style="display:flex;align-items:center;gap:10px;white-space:nowrap;">
          <span style="font-weight:600;font-size:16px;line-height:100%;letter-spacing:0;color:#FFFFFF;">${formatCurrency(val)}</span>
          <span style="font-weight:300;font-size:16px;line-height:100%;letter-spacing:0;color:#FFFFFF;">${pct.toFixed(2)}%</span>
        </div>
      </div>
    `;

    const tooltipRect = tooltipEl.getBoundingClientRect();
    const parentRect = (chart.canvas.parentNode as HTMLElement).getBoundingClientRect();
    const anchorRect = anchorEl.getBoundingClientRect();
    const baseX = clientX ?? anchorRect.left + anchorRect.width;
    const baseY = clientY ?? anchorRect.top + anchorRect.height / 2;
    const left = baseX - parentRect.left + 8;
    const top = baseY - parentRect.top - tooltipRect.height / 2;

    tooltipEl.style.left = `${left}px`;
    tooltipEl.style.top = `${top - 50}px`;
    tooltipEl.style.transform = 'translateY(0)';
    tooltipEl.style.opacity = '1';
  };

  const hidePortfolioLegendTooltip = (chart: DoughnutChartRef | null) => {
    if (!chart) return;
    const tooltipEl = ensureTooltipEl(chart, 'portfolio-holdings-tooltip');
    if (!tooltipEl) return;
    tooltipEl.style.opacity = '0';
  };

  const positionTooltip = (chart: any, tooltip: any, tooltipEl: HTMLDivElement, bg: string) => {
    const canvas = chart?.canvas as HTMLCanvasElement | null;
    if (!canvas) return;

    const tooltipRect = tooltipEl.getBoundingClientRect();
    const caretX = canvas.offsetLeft + (tooltip?.caretX ?? 0);
    const caretY = canvas.offsetTop + (tooltip?.caretY ?? 0);

    const yAlign = String(tooltip?.yAlign || '').toLowerCase();
    let placeAbove = true;
    if (yAlign === 'top') placeAbove = false;
    else if (yAlign === 'bottom') placeAbove = true;
    else placeAbove = caretY > canvas.offsetTop + canvas.offsetHeight / 2;

    let left = caretX - tooltipRect.width / 2;
    const minLeft = canvas.offsetLeft;
    const maxLeft = canvas.offsetLeft + canvas.offsetWidth - tooltipRect.width;
    left = Math.min(Math.max(left, minLeft), maxLeft);

    let top = placeAbove ? caretY - tooltipRect.height - 10 : caretY + 10;
    if (top < canvas.offsetTop) {
      top = caretY + 10;
      placeAbove = false;
    }

    tooltipEl.style.left = `${left}px`;
    tooltipEl.style.top = `${top}px`;
    tooltipEl.style.transform = `translateY(${placeAbove ? '-6px' : '6px'})`;

    const caret = tooltipEl.querySelector<HTMLDivElement>('.tooltip-caret');
    if (caret) {
      const caretLeft = Math.max(10, Math.min(tooltipRect.width - 10, caretX - left));
      caret.style.position = 'absolute';
      caret.style.left = `${caretLeft}px`;
      caret.style.width = '0';
      caret.style.height = '0';
      caret.style.transform = 'translateX(-50%)';
      caret.style.filter = 'drop-shadow(0 2px 2px rgba(0,0,0,0.25))';
      caret.style.transition = 'left 80ms ease, top 80ms ease, bottom 80ms ease';

      if (placeAbove) {
        caret.style.bottom = '-7px';
        caret.style.top = '';
        caret.style.borderLeft = '7px solid transparent';
        caret.style.borderRight = '7px solid transparent';
        caret.style.borderTop = `7px solid ${bg}`;
        caret.style.borderBottom = '0';
      } else {
        caret.style.top = '-7px';
        caret.style.bottom = '';
        caret.style.borderLeft = '7px solid transparent';
        caret.style.borderRight = '7px solid transparent';
        caret.style.borderBottom = `7px solid ${bg}`;
        caret.style.borderTop = '0';
      }
    }
  };

  const doughnutData = useMemo(
    () => buildDoughnutData(allocation),
    [allocation, buildDoughnutData, symbolColorVersion, colors.cardBg]
  );

  const doughnutOptions = useMemo<ChartOptions<'doughnut'>>(
    () => buildDoughnutOptions(totalMarketValue, allocation.length),
    [allocation.length, buildDoughnutOptions, totalMarketValue]
  );

  const holdingsCharts = useMemo(() => {
    if (isAllPortfolios) {
      return portfolioCharts.map((item, idx) => ({
        key: idx === 0 ? 'portfolio-holdings' : `portfolio-holdings-${item.portfolioId}`,
        title: `${item.name} Holdings`,
        allocation: item.allocation,
        total: item.total,
        pnl: item.pnl,
        data: buildDoughnutData(item.allocation),
        options: buildDoughnutOptions(item.total, item.allocation.length),
        isPrimary: idx === 0,
      }));
    }

    return [
      {
        key: 'portfolio-holdings',
        title: 'Portfolio Holdings',
        allocation,
        total: totalMarketValue,
        pnl: computePortfolioPnL(openRows),
        data: doughnutData,
        options: doughnutOptions,
        isPrimary: true,
      },
    ];
  }, [
    allocation,
    buildDoughnutData,
    buildDoughnutOptions,
    computePortfolioPnL,
    doughnutData,
    doughnutOptions,
    isAllPortfolios,
    openRows,
    portfolioCharts,
    totalMarketValue,
  ]);

  const expandedHoldingsChart = useMemo(() => {
    if (!holdingsCharts.length) return null;
    if (!expandedHoldingsKey) return holdingsCharts[0];
    return holdingsCharts.find((c) => c.key === expandedHoldingsKey) || holdingsCharts[0];
  }, [expandedHoldingsKey, holdingsCharts]);

  const profitLossBySymbol = useMemo(() => {
    // Requirement: if there are multiple holdings for the same stock,
    // show ONE weighted entry per symbol (weighted by shares).
    const base = rows
      .filter((r) => !r.isClosed)
      .filter((r) => Number.isFinite(Number(r.buyPrice)) && Number(r.buyPrice) > 0)
      .filter((r) => Number.isFinite(Number(r.shares)) && Number(r.shares) > 0)
      .filter((r) => r.currentPrice != null && Number.isFinite(Number(r.currentPrice)) && Number(r.currentPrice) > 0);

    type Agg = { symbol: string; shares: number; cost: number; currentPrice: number };
    const map = new Map<string, Agg>();

    for (const r of base) {
      const sym = (r.symbol || '').trim().toUpperCase();
      if (!sym) continue;

      const shares = Number(r.shares);
      const buy = Number(r.buyPrice);
      const current = Number(r.currentPrice);
      if (!Number.isFinite(shares) || shares <= 0) continue;
      if (!Number.isFinite(buy) || buy <= 0) continue;
      if (!Number.isFinite(current) || current <= 0) continue;

      const existing = map.get(sym);
      if (!existing) {
        map.set(sym, { symbol: sym, shares, cost: buy * shares, currentPrice: current });
        continue;
      }

      const nextShares = existing.shares + shares;
      const nextCost = existing.cost + buy * shares;
      // Current Price must be the same for all rows; if it isn't, pick the max positive.
      const nextCurrent = Math.max(existing.currentPrice, current);
      map.set(sym, { symbol: sym, shares: nextShares, cost: nextCost, currentPrice: nextCurrent });
    }

    const metrics = Array.from(map.values())
      .filter((a) => a.shares > 0)
      .map((a) => {
        const weightedBuy = a.cost / Math.max(1, a.shares);
        const dollarsPerShare = a.currentPrice - weightedBuy;
        const pct = weightedBuy ? (dollarsPerShare / weightedBuy) * 100 : 0;
        return {
          symbol: a.symbol,
          dollarsPerShare: Number.isFinite(dollarsPerShare) ? dollarsPerShare : 0,
          pct: Number.isFinite(pct) ? pct : 0,
        };
      })
      // Most profitable on the left.
      .sort((a, b) => {
        if (b.pct !== a.pct) return b.pct - a.pct;
        if (b.dollarsPerShare !== a.dollarsPerShare) return b.dollarsPerShare - a.dollarsPerShare;
        return a.symbol.localeCompare(b.symbol);
      });

    return {
      labels: metrics.map((m) => m.symbol),
      dollarsPerShare: metrics.map((m) => m.dollarsPerShare),
      pctValues: metrics.map((m) => m.pct),
    };
  }, [rows]);

  const plBarData = useMemo(() => {
    void symbolColorVersion;
    return {
      labels: profitLossBySymbol.labels,
      datasets: [
        {
          label: 'Profit/Loss (%)',
          data: profitLossBySymbol.pctValues,
          // Match stock colors across charts/tables (not profit sign)
          backgroundColor: profitLossBySymbol.labels.map((sym) => getSymbolColor(sym)),
          borderRadius: 6,
        },
      ],
    };
  }, [profitLossBySymbol, symbolColorVersion]);

  const plBarOptions = useMemo<ChartOptions<'bar'>>(() => {
    const niceStep = (raw: number): number => {
      const v = Math.abs(Number(raw));
      if (!Number.isFinite(v) || v <= 0) return 1;
      const exp = Math.floor(Math.log10(v));
      const base = v / Math.pow(10, exp);
      // 1, 2, 2.5, 5, 10
      const niceBase = base <= 1 ? 1 : base <= 2 ? 2 : base <= 2.5 ? 2.5 : base <= 5 ? 5 : 10;
      return niceBase * Math.pow(10, exp);
    };

    const EPS = 1e-9;

    const pctValues = profitLossBySymbol.pctValues.map((v) => Number(v)).filter((v) => Number.isFinite(v));
    const pctMinObserved = pctValues.length ? Math.min(...pctValues) : 0;
    const pctMaxObserved = pctValues.length ? Math.max(...pctValues) : 0;
    // Snap axis ends to the same tick step as the grid lines.
    // Also ensure 0 is included, and keep at least one tick beyond 0
    // when the series is one-sided (all >= 0 or all <= 0), so the bottom/top
    // label isn't visually cramped at 0.
    const observedNonNeg = pctMinObserved >= -EPS;
    const observedNonPos = pctMaxObserved <= EPS;

    const includeMin = Math.min(pctMinObserved, 0);
    const includeMax = Math.max(pctMaxObserved, 0);

    const baseSpan = Math.max(EPS, includeMax - includeMin);
    const pad = Math.max(baseSpan * 0.08, 0.25);

    const rawMin = includeMin - pad;
    const rawMax = includeMax + pad;
    const span = Math.max(EPS, rawMax - rawMin);

    let pctStep = niceStep(span / 6);
    if (!Number.isFinite(pctStep) || pctStep <= 0) pctStep = 1;

    let pctMin = Math.floor(rawMin / pctStep) * pctStep;
    let pctMax = Math.ceil(rawMax / pctStep) * pctStep;

    if (observedNonNeg) pctMin = Math.min(pctMin, -pctStep);
    if (observedNonPos) pctMax = Math.max(pctMax, pctStep);

    // Defensive: keep 0 inside the visible span.
    if (pctMin > 0) pctMin = -pctStep;
    if (pctMax < 0) pctMax = pctStep;

    const axisTickColor = isDarkMode ? colors.text : '#0D0D0D';
    const axisTickFont = {
      family: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
      size: 12,
      weight: 400,
    };

    const pctSpanForFmt = pctMax - pctMin;
    const pctDecimals = pctSpanForFmt <= 2 ? 2 : (pctSpanForFmt <= 20 ? 1 : 0);
    const fmtPctTick = (v: number) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return '';
      const r = Number(n.toFixed(pctDecimals));
      return `${(Object.is(r, -0) ? 0 : r).toFixed(pctDecimals)}%`;
    };

    const fmtUsdTick = (v: number) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return '';
      const abs = Math.abs(n);
      const maxFractionDigits = abs < 10 ? 2 : 0;
      return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: maxFractionDigits });
    };

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
          external: (context) => {
            const { chart, tooltip } = context;
            const tooltipEl = ensureTooltipEl(chart, 'portfolio-pl-tooltip');
            if (!tooltipEl) return;

            if (!tooltip || tooltip.opacity === 0) {
              tooltipEl.style.opacity = '0';
              return;
            }

            const dataIndex = tooltip.dataPoints?.[0]?.dataIndex ?? 0;
            const label = String(profitLossBySymbol.labels[dataIndex] ?? '').trim();
            const pct = Number(profitLossBySymbol.pctValues[dataIndex] ?? 0);
            const dollars = Number(profitLossBySymbol.dollarsPerShare[dataIndex] ?? 0);
            const dotColor = getSymbolColor(label);

            const pctColor = pct < 0 ? '#FF0052' : '#FFFFFF';

            tooltipEl.innerHTML = `
              <div style="
                width:auto;
                height:auto;
                background:#111827;
                border:1px solid #4B5563;
                opacity:1;
                border-radius:8px;
                padding:12px;
                box-sizing:border-box;
                display:flex;
                flex-direction:column;
                gap:9px;
                font-family: Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
                position:relative;
              ">
                <div style="display:flex;align-items:center;gap:6px;justify-content:flex-start;">
                  <span style="width:10px;height:10px;border-radius:999px;background:${dotColor};display:inline-block;flex:0 0 auto;"></span>
                  <span style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-weight:700;font-size:12px;line-height:100%;letter-spacing:0;color:#D1D5DB;text-align:left;">${label}</span>
                </div>
                <div style="display:flex;align-items:center;gap:6px;">
                  <span style="font-weight:600;font-size:14px;line-height:100%;letter-spacing:0;color:#F9FAFB;">${fmtUsdTick(dollars)}</span>
                  <span style="font-weight:600;font-size:14px;line-height:100%;letter-spacing:0;color:${pctColor};">${pct.toFixed(2)}%</span>
                </div>
              </div>
            `;

            tooltipEl.style.opacity = '1';
            positionTooltip(chart, tooltip, tooltipEl, '#111827');
            const baseTop = Number.parseFloat(tooltipEl.style.top || '0');
            const baseLeft = Number.parseFloat(tooltipEl.style.left || '0');
            const element = tooltip.dataPoints?.[0]?.element as { height?: number } | undefined;
            const barHeight = Math.abs(Number(element?.height ?? 0));
            const yOffset = barHeight > 0 ? Math.min(40, Math.max(8, barHeight * 0.35)) : 40;
            if (Number.isFinite(baseTop)) {
              tooltipEl.style.top = `${baseTop - yOffset}px`;
            }
            if (Number.isFinite(baseLeft)) {
              tooltipEl.style.left = `${baseLeft + 60}px`;
            }
          },
        },
      },
      scales: {
        x: {
          ticks: { color: axisTickColor, font: axisTickFont },
          grid: { display: false },
        },
        y: {
          position: 'right',
          min: pctMin,
          max: pctMax,
          grace: 0,
          ticks: {
            color: axisTickColor,
            font: axisTickFont,
            stepSize: pctStep,
            callback: (v) => fmtPctTick(Number(v)),
          },
          grid: { color: isDarkMode ? 'rgba(148,163,184,0.15)' : 'rgba(148,163,184,0.25)' },
        },
      },
    };
  }, [colors.text, isDarkMode, profitLossBySymbol.dollarsPerShare, profitLossBySymbol.labels, profitLossBySymbol.pctValues]);

  const priceCompare = useMemo(() => {
    const base = openRows;

    const bySymbol = new Map<
      string,
      {
        symbol: string;
        shares: number;
        totalCost: number;
        totalMarketValue: number;
      }
    >();

    for (const row of base) {
      const symbol = String(row.symbol || '').trim().toUpperCase();
      if (!symbol) continue;

      const shares = Number(row.shares ?? 0);
      if (!Number.isFinite(shares) || shares <= 0) continue;

      const buy = Number(row.buyPrice ?? 0);
      const current = Number(row.currentPrice ?? 0);
      const cost = (Number.isFinite(buy) ? buy : 0) * shares;
      const marketValue = (Number.isFinite(current) ? current : 0) * shares;

      let agg = bySymbol.get(symbol);
      if (!agg) {
        agg = { symbol, shares: 0, totalCost: 0, totalMarketValue: 0 };
        bySymbol.set(symbol, agg);
      }

      agg.shares += shares;
      agg.totalCost += cost;
      agg.totalMarketValue += marketValue;
    }

    // Keep the same left-to-right order as the Profit/Loss chart (most profitable on the left).
    const preferred = profitLossBySymbol.labels;
    const preferredSet = new Set(preferred);
    const extras = Array.from(bySymbol.keys())
      .filter((sym) => !preferredSet.has(sym))
      .sort((a, b) => a.localeCompare(b));

    const order = [...preferred.filter((sym) => bySymbol.has(sym)), ...extras];

    const rows = order
      .map((sym) => bySymbol.get(sym))
      .filter((v): v is NonNullable<typeof v> => Boolean(v))
      .map((agg) => {
        const shares = agg.shares;
        const buyPrice = shares > 0 ? agg.totalCost / shares : 0;
        const currentPrice = shares > 0 ? agg.totalMarketValue / shares : 0;
        return {
          symbol: agg.symbol,
          shares,
          buyPrice,
          currentPrice,
        };
      });

    return {
      rows,
      labels: order,
      cost: order.map((sym) => bySymbol.get(sym)?.totalCost ?? 0),
      marketValue: order.map((sym) => bySymbol.get(sym)?.totalMarketValue ?? 0),
    };
  }, [openRows, profitLossBySymbol.labels]);

  const compareData = useMemo(() => {
    void symbolColorVersion;
    const buyColor = '#43AA7A';
    const currentColor = '#2F58F2';
    return {
      labels: priceCompare.labels,
      datasets: [
        {
          label: 'Cost',
          data: priceCompare.cost,
          backgroundColor: buyColor,
          borderRadius: 6,
        },
        {
          label: 'Market Value',
          data: priceCompare.marketValue,
          backgroundColor: currentColor,
          borderRadius: 6,
        },
      ],
    };
  }, [priceCompare, symbolColorVersion]);

  const compareOptions = useMemo<ChartOptions<'bar'>>(() => {
    const buyDot = 'rgba(67,170,122,0.35)';
    const currentDot = 'rgba(47,88,242,0.95)';

    const axisTickColor = isDarkMode ? colors.text : '#0D0D0D';
    const axisTickFont = {
      family: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
      size: 12,
      weight: 400,
    };

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'bottom',
          align: 'center',
          labels: {
            color: colors.subText,
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 10,
            boxHeight: 10,
            padding: 18,
          },
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          enabled: false,
          external: (context) => {
            const { chart, tooltip } = context;
            const tooltipEl = ensureTooltipEl(chart, 'portfolio-compare-tooltip');
            if (!tooltipEl) return;

            if (!tooltip || tooltip.opacity === 0) {
              tooltipEl.style.opacity = '0';
              return;
            }

            const dataIndex = tooltip.dataPoints?.[0]?.dataIndex ?? 0;
            const row = priceCompare.rows[dataIndex];
            const symbol = String(priceCompare.labels[dataIndex] ?? '').trim();
            const dotColor = getSymbolColor(symbol);

            const shares = Number(row?.shares ?? 0);
            const cost = Number(priceCompare.cost[dataIndex] ?? 0);
            const marketValue = Number(priceCompare.marketValue[dataIndex] ?? 0);
            const plValue = marketValue - cost;
            const plPct = cost ? (plValue / cost) * 100 : 0;
            const plPctText = `${plPct >= 0 ? '+' : ''}${Math.abs(plPct).toFixed(0)}%`;
            const plColor = plPct < 0 ? '#FF0052' : '#22C55E';

            const bg = isDarkMode ? 'rgba(0,0,0,0.92)' : 'rgba(15,23,42,0.92)';
            const fg = '#ffffff';
            const muted = 'rgba(255,255,255,0.82)';

            tooltipEl.innerHTML = `
              <div style="
                width:max-content;
                min-width:220px;
                max-width:calc(100vw - 24px);
                min-height:119px;
                height:auto;
                background:#111827;
                border:1px solid #4B5563;
                opacity:1;
                border-radius:8px;
                padding:12px;
                box-sizing:border-box;
                display:flex;
                flex-direction:column;
                gap:10px;
                font-family: Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
                font-weight:400;
                font-size:14px;
                line-height:1;
                letter-spacing:0;
                position:relative;
              ">
                <div style="display:flex;align-items:center;justify-content:flex-start;text-align:left;gap:6px;font-weight:700;font-size:12px;line-height:1;">
                  <span style="width:10px;height:10px;border-radius:999px;background:${dotColor};display:inline-block;flex:0 0 auto;"></span>
                  <span style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:#D1D5DB;">${symbol}</span>
                </div>

                <div style="display:grid;grid-template-columns:minmax(100px,1fr) auto;align-items:center;gap:10px;color:#F9FAFB;">
                  <div style="display:flex;align-items:center;gap:6px;min-width:0;">
                    <span style="width:10px;height:10px;border-radius:999px;background:${buyDot};display:inline-block;flex:0 0 auto;"></span>
                    <span style="font-weight:400;font-size:14px;line-height:100%;letter-spacing:0;color:#F9FAFB;white-space:nowrap;">Cost</span>
                  </div>
                  <div style="text-align:left;white-space:nowrap;font-weight:600;font-size:14px;line-height:100%;letter-spacing:0;color:#F9FAFB;">${formatCurrency0(cost)}</div>
                </div>

                <div style="display:grid;grid-template-columns:minmax(100px,1fr) auto;align-items:center;gap:10px;color:#F9FAFB;">
                  <div style="display:flex;align-items:center;gap:6px;min-width:0;">
                    <span style="width:10px;height:10px;border-radius:999px;background:${currentDot};display:inline-block;flex:0 0 auto;"></span>
                    <span style="font-weight:400;font-size:14px;line-height:100%;letter-spacing:0;color:#F9FAFB;white-space:nowrap;">Market Value</span>
                  </div>
                  <div style="text-align:left;white-space:nowrap;font-weight:600;font-size:14px;line-height:100%;letter-spacing:0;color:#F9FAFB;">${formatCurrency0(marketValue)}</div>
                </div>

                <div style="width:100%;display:flex;flex-direction:column;gap:6px;padding-top:4px;border-top:1px solid #374151;">
                  <div style="display:grid;grid-template-columns:minmax(100px,1fr) auto;align-items:center;gap:10px;color:#F9FAFB;">
                    <div style="display:flex;align-items:center;gap:6px;min-width:0;">
                      <span style="width:10px;height:10px;border-radius:999px;background:#FFFFFF;display:inline-block;flex:0 0 auto;"></span>
                      <span style="font-weight:400;font-size:14px;line-height:100%;letter-spacing:0;color:#F9FAFB;">P/L</span>
                    </div>
                    <div style="display:flex;align-items:center;justify-content:flex-start;gap:8px;white-space:nowrap;">
                      <span style="font-weight:600;font-size:14px;line-height:100%;letter-spacing:0;color:${plColor};">${plPctText}</span>
                      <span style="font-weight:600;font-size:14px;line-height:100%;letter-spacing:0;color:#F9FAFB;">${formatCurrency0(plValue)}</span>
                    </div>
                  </div>
                </div>
              </div>
            `;

            tooltipEl.style.opacity = '1';
            positionTooltip(chart, tooltip, tooltipEl, '#111827');
          },
        },
      },
      scales: {
        x: {
          ticks: { color: axisTickColor, font: axisTickFont },
          grid: { display: false },
        },
        y: {
          position: 'right',
          ticks: {
            color: axisTickColor,
            font: axisTickFont,
            callback: (v) => formatCurrency0(Number(v)),
          },
          grid: { color: isDarkMode ? 'rgba(148,163,184,0.15)' : 'rgba(148,163,184,0.25)' },
        },
      },
    };
  }, [colors.subText, colors.text, isDarkMode, priceCompare.cost, priceCompare.labels, priceCompare.marketValue, priceCompare.rows]);

  const graphCardStyle: React.CSSProperties = {
    width: 518,
    maxWidth: '100%',
    height: 404,
    backgroundColor: colors.cardBg,
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: 6,
    boxShadow: 'none',
    opacity: 1,
    paddingTop: 10,
    paddingRight: 20,
    paddingBottom: 10,
    paddingLeft: 20,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  };

  const tableCardStyle: React.CSSProperties = {
    backgroundColor: colors.cardBg,
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: 12,
    boxShadow: colors.shadow,
  };

  const expandIconStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    padding: 4,
    outline: 'none',
    boxShadow: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '2rem',
  };

  const modalContentStyle: React.CSSProperties = {
    backgroundColor: colors.cardBg,
    borderRadius: 16,
    width: 'min(1200px, 96vw)',
    height: 'min(820px, 86vh)',
    boxShadow: colors.shadow,
    border: `1px solid ${colors.cardBorder}`,
    padding: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
  };

  const addHoldingModalStyle: React.CSSProperties = {
    width: 'min(460px, 96vw)',
    maxHeight: '90vh',
    backgroundColor: isDarkMode ? '#111827' : '#FFFFFF',
    borderRadius: 0,
    boxShadow: colors.shadow,
    border: `1px solid ${colors.cardBorder}`,
    padding: '14px 20px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    opacity: 1,
    overflowY: 'auto',
  };

  const addHoldingTextStyle: React.CSSProperties = {
    fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '100%',
    letterSpacing: 0,
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
  };

  const portfolioModalTitleStyle: React.CSSProperties = {
    fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
    fontWeight: 600,
    fontSize: 20,
    fontStyle: 'normal',
    lineHeight: '100%',
    letterSpacing: 0,
    color: isDarkMode ? '#F9FAFB' : '#000000',
  };

  const addHoldingLabelStyle: React.CSSProperties = {
    ...addHoldingTextStyle,
    display: 'block',
  };

  const addHoldingInputStyle: React.CSSProperties = {
    ...addHoldingTextStyle,
    width: 220,
    minWidth: 220,
    maxWidth: 220,
    height: 40,
    borderRadius: 8,
    border: `1px solid ${isDarkMode ? '#374151' : '#D9D9D9'}`,
    padding: '10px 14px',
    outline: 'none',
    backgroundColor: isDarkMode ? '#111827' : '#FFFFFF',
    boxSizing: 'border-box',
    opacity: 1,
  };

  const addHoldingSymbolInputStyle: React.CSSProperties = {
    ...addHoldingInputStyle,
  };

  const sellHoldingModalStyle: React.CSSProperties = {
    width: 432,
    // Slightly taller so the Sell Date field is visible without clipping.
    height: 430,
    maxHeight: '90vh',
    backgroundColor: isDarkMode ? '#111827' : '#FFFFFF',
    borderRadius: 0,
    boxShadow: colors.shadow,
    border: `1px solid ${colors.cardBorder}`,
    padding: 20,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    opacity: 1,
    overflowY: 'auto',
  };

  const sellHoldingInputStyle: React.CSSProperties = {
    ...addHoldingTextStyle,
    width: 220,
    height: 40,
    borderRadius: 8,
    border: `1px solid ${isDarkMode ? '#374151' : '#D9D9D9'}`,
    padding: '10px 14px',
    outline: 'none',
    backgroundColor: isDarkMode ? '#111827' : '#FFFFFF',
    boxSizing: 'border-box',
    opacity: 1,
  };

  const deleteHoldingModalStyle: React.CSSProperties = {
    width: 432,
    height: 210,
    backgroundColor: isDarkMode ? '#111827' : '#FFFFFF',
    borderRadius: 0,
    boxShadow: colors.shadow,
    border: `1px solid ${colors.cardBorder}`,
    padding: 20,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    opacity: 1,
  };

  const symbolAutocompleteContainerStyle: React.CSSProperties = {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    maxHeight: 220,
    overflowY: 'auto',
    backgroundColor: '#FFFFFF',
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: 10,
    boxShadow: colors.shadow,
    zIndex: 7000,
  };

  const symbolAutocompleteItemStyle = (isSelected: boolean): React.CSSProperties => ({
    padding: '10px 10px',
    cursor: 'pointer',
    backgroundColor: isSelected ? (isDarkMode ? colors.tableRowHover : '#F1F5F9') : 'transparent',
    borderBottom: `1px solid ${colors.cardBorder}`,
    transition: 'background-color 0.15s ease',
  });

  const symbolAutocompleteSymbolStyle: React.CSSProperties = {
    ...addHoldingTextStyle,
    fontWeight: 600,
    fontSize: 14,
  };

  const symbolAutocompleteNameStyle: React.CSSProperties = {
    ...addHoldingTextStyle,
    fontWeight: 400,
    fontSize: 12,
    color: '#474D55',
    marginTop: 2,
  };

  const symbolAutocompleteExchangeStyle: React.CSSProperties = {
    ...addHoldingTextStyle,
    fontWeight: 500,
    fontSize: 11,
    color: isDarkMode ? '#9CA3AF' : '#64748B',
    marginTop: 4,
  };

  const addHoldingSearchButtonStyle: React.CSSProperties = {
    fontFamily: addHoldingTextStyle.fontFamily,
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '100%',
    letterSpacing: 0,
    width: 120,
    height: 37,
    borderRadius: 6,
    padding: '4px 14px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: isDarkMode ? '#3B82F6' : '#0D0D0D',
    color: isDarkMode ? '#111827' : '#FFFFFF',
    border: 'none',
    cursor: 'pointer',
    opacity: 1,
    flex: '0 0 auto',
  };

  const addHoldingAddButtonStyle: React.CSSProperties = {
    fontFamily: addHoldingTextStyle.fontFamily,
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '100%',
    letterSpacing: 0,
    width: 175,
    height: 37,
    borderRadius: 6,
    padding: '4px 14px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: isDarkMode ? '#3B82F6' : '#2F58F2',
    color: isDarkMode ? '#111827' : '#FFFFFF',
    border: 'none',
    cursor: 'pointer',
    opacity: 1,
    flex: '0 0 auto',
  };

  const addHoldingLinkStyle: React.CSSProperties = {
    fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
    fontWeight: 600,
    fontSize: 14,
    lineHeight: '100%',
    letterSpacing: 0,
    color: '#2F58F2',
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'default',
    display: 'inline-flex',
    alignItems: 'center',
    verticalAlign: 'middle',
  };

  const tabButton = (active: boolean): React.CSSProperties => ({
    border: `1px solid ${active ? colors.primary : colors.cardBorder}`,
    background: active ? colors.primary : colors.cardBg,
    color: active ? '#ffffff' : colors.text,
    padding: '8px 12px',
    borderRadius: 8,
    fontWeight: 800,
    cursor: 'pointer',
  });

  const actionButton: React.CSSProperties = {
    border: `1px solid ${colors.cardBorder}`,
    background: colors.cardBg,
    color: colors.text,
    padding: '8px 12px',
    borderRadius: 10,
    fontWeight: 800,
    cursor: 'pointer',
  };

  const primaryButton: React.CSSProperties = {
    ...actionButton,
    background: colors.primary,
    borderColor: colors.primary,
    color: '#ffffff',
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    minWidth: 1640,
    borderCollapse: 'separate',
    borderSpacing: 0,
  };

  const thStyle: React.CSSProperties = {
    position: 'sticky',
    top: 0,
    height: 56,
    zIndex: 1,
    textAlign: 'center',
    fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '100%',
    letterSpacing: '0.14px',
    textTransform: 'none',
    padding: '10px 10px',
    background: isDarkMode ? '#1F2937' : '#F6F6F6',
    color: isDarkMode ? colors.text : '#0D0D0D',
    borderBottom: `1px solid ${isDarkMode ? '#374151' : colors.cardBorder}`,
    borderRight: `1px solid ${isDarkMode ? '#374151' : colors.cardBorder}`,
    whiteSpace: 'nowrap',
  };

  const thClickable = (key: SortableHoldingColumn): React.CSSProperties => ({
    ...thStyle,
    cursor: 'pointer',
    userSelect: 'none',
  });

  const marketValueColumnWidth = 160;
  const costColumnWidth = marketValueColumnWidth;

  const tdStyle: React.CSSProperties = {
    padding: '11px 10px 9px',
    borderBottom: `1px solid ${isDarkMode ? '#374151' : colors.cardBorder}`,
    fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
    fontWeight: 600,
    fontSize: 14,
    lineHeight: '100%',
    letterSpacing: '0.14px',
    color: colors.text,
    borderRight: `1px solid ${isDarkMode ? '#374151' : colors.cardBorder}`,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  };

  const ChangeArrowIcon: React.FC<{ dir: 'up' | 'down'; color: string }> = ({ dir, color }) => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'inline', marginLeft: 4 }}
    >
      {dir === 'up' ? (
        <path d="M6 2 L10 8 L2 8 Z" fill={color} />
      ) : (
        <path d="M6 10 L2 4 L10 4 Z" fill={color} />
      )}
    </svg>
  );

  const parseBuyDateToMs = (raw: string): number => {
    // raw: dd/MM/yyyy
    const s = String(raw || '').trim();
    const parts = s.split('/');
    if (parts.length !== 3) return NaN;
    const [dd, mm, yyyy] = parts;
    const d = Number(dd);
    const m = Number(mm);
    const y = Number(yyyy);
    if (!Number.isFinite(d) || !Number.isFinite(m) || !Number.isFinite(y)) return NaN;
    const dt = new Date(y, m - 1, d);
    const ms = dt.getTime();
    return Number.isFinite(ms) ? ms : NaN;
  };

  const sortedTableRows = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    const list = [...displayedRows];

    const sortValue = (r: Holding): string | number | null => {
      switch (sort.key) {
        case 'portfolioName': {
          const label = portfolioNameById.get(r.portfolioId) || (r.portfolioId > 0 ? `Portfolio ${r.portfolioId}` : 'Multiple');
          const normalized = String(label || '').trim();
          return normalized ? normalized.toUpperCase() : null;
        }
        case 'symbol':
          return String(r.symbol || '').trim().toUpperCase();
        case 'sellDate': {
          const ms = parseBuyDateToMs(String(r.sellDate || ''));
          return Number.isFinite(ms) ? ms : null;
        }
        case 'changePct': {
          if (activeTab === 'closed') {
            const { changePct } = getClosedMetrics(r);
            return Number.isFinite(changePct) ? changePct : null;
          }
          const pct = getLatestChangePct(r.symbol, r.changePct ?? null);
          const pctValue = pct == null ? NaN : Number(pct);
          return Number.isFinite(pctValue) ? pctValue : null;
        }
        case 'buyDate': {
          const ms = parseBuyDateToMs(r.buyDate);
          return Number.isFinite(ms) ? ms : null;
        }
        case 'buyPrice':
          return Number.isFinite(Number(r.buyPrice)) ? Number(r.buyPrice) : null;
        case 'currentPrice':
          return Number.isFinite(Number(r.currentPrice)) ? Number(r.currentPrice) : null;
        case 'cost': {
          const v = Number(r.buyPrice || 0) * Number(r.shares || 0);
          return Number.isFinite(v) ? v : null;
        }
        case 'marketValue': {
          const v = Number(r.currentPrice || 0) * Number(r.shares || 0);
          return Number.isFinite(v) ? v : null;
        }
        case 'profitLoss': {
          const v = (Number(r.currentPrice || 0) - Number(r.buyPrice || 0)) * Number(r.shares || 0);
          return Number.isFinite(v) ? v : null;
        }
        case 'shares':
          return Number.isFinite(Number(r.shares)) ? Number(r.shares) : null;
        case 'buyPriceAlert':
          return r.buyPriceAlert == null ? null : (Number.isFinite(Number(r.buyPriceAlert)) ? Number(r.buyPriceAlert) : null);
        case 'sellPriceAlert':
          return r.sellPriceAlert == null ? null : (Number.isFinite(Number(r.sellPriceAlert)) ? Number(r.sellPriceAlert) : null);
        case 'status':
          {
            const s = getAlertStatusSortMeta(r).text;
            return s ? s : null;
          }
        default:
          return null;
      }
    };

    list.sort((a, b) => {
      if (sort.key === 'status') {
        const av = getAlertStatusSortMeta(a);
        const bv = getAlertStatusSortMeta(b);
        if (av.rank !== bv.rank) return (av.rank - bv.rank) * dir;
        const textDiff = av.text.localeCompare(bv.text);
        if (textDiff !== 0) return textDiff * dir;
        return String(a.symbol || '').localeCompare(String(b.symbol || '')) * dir;
      }

      const av = sortValue(a);
      const bv = sortValue(b);
      // Keep blanks (null/undefined/NaN) last for both asc/desc.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });

    return list;
  }, [displayedRows, portfolioNameById, sort.dir, sort.key, getAlertStatusSortMeta]);

  useLayoutEffect(() => {
    const el = holdingsTableWrapRef.current;
    if (!el) return;

    const update = () => {
      const measure = () => {
        const table = el.querySelector('table');
        const contentWidth = table
          ? Math.ceil(table.getBoundingClientRect().width)
          : Math.ceil(el.scrollWidth);
        const containerWidth = Math.ceil(el.clientWidth);
        const next = contentWidth - containerWidth > 16;
        setHasHoldingsOverflowX((prev) => (prev === next ? prev : next));
      };

      if (typeof requestAnimationFrame !== 'undefined') {
        requestAnimationFrame(() => requestAnimationFrame(measure));
      } else {
        measure();
      }
    };

    update();

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => update());
      ro.observe(el);
    }

    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      ro?.disconnect();
    };
  }, [activeTab, groupByStock, sortedTableRows.length]);

  const statusPill = (status: Holding['status']) => {
    if (status === 'Buy Triggered') return { text: 'Buy Triggered', color: colors.warn };
    if (status === 'Sell Triggered') return { text: 'Sell Triggered', color: colors.danger };
    return { text: 'Not Reached', color: colors.subText };
  };

  const handleRowAction = (rowId: string, action: string) => {
    if (!action || action === 'Actions') return;

    if (action === 'Edit') {
      const row = rows.find((r) => r.id === rowId);
      if (row) openEditHoldingModal(row);
      return;
    }

    if (action === 'Delete') {
      if (skipDeleteConfirm) {
        performDeleteHolding(rowId);
        return;
      }

      const row = rows.find((r) => r.id === rowId);
      if (row) {
        openDeleteModal(row);
      } else {
        // Fallback (shouldn't happen) - just delete.
        performDeleteHolding(rowId);
      }
      return;
    }

    if (action === 'Sell') {
      const row = rows.find((r) => r.id === rowId);
      if (!row) return;

      openSellModal(row);
      return;
    }

    if (action === 'Reopen') {
      // Only one undo snackbar at a time.
      dismissAllPortfoliosUndoSnackbars();

      if (!authToken) {
        const row = rows.find((r) => r.id === rowId);
        if (!row) return;
        const snapshot: Holding = { ...row };

        setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, isClosed: false, sellDate: '', status: 'Not Reached' } : r)));

        const timerId = window.setTimeout(() => {
          pendingHoldingEditUndoRef.current = null;
          setPendingHoldingEditUndo(null);
        }, HOLDING_EDIT_UNDO_MS);

        pendingHoldingEditUndoRef.current = { message: 'Holding reopened', timerId, snapshot, portfolioId: -1 };
        setPendingHoldingEditUndo({ message: 'Holding reopened', timerId });
        return;
      }

      const row = rows.find((r) => r.id === rowId);
      if (!row) return;
      const rowPortfolioId = resolveRowPortfolioId(row);
      if (!rowPortfolioId) {
        onNotify?.('Please select a portfolio first.', 'error');
        return;
      }
      const snapshot: Holding = { ...row };

      void (async () => {
        try {
          const id = Number(rowId);
          await adminApi.upsertMyPortfolioHolding(authToken, {
            id: Number.isFinite(id) && id > 0 ? id : null,
            portfolioId: rowPortfolioId,
            symbol: row.symbol,
            shares: row.shares,
            buyPrice: row.buyPrice,
            currentPrice: row.currentPrice,
            buyPriceAlert: row.buyPriceAlert ?? null,
            sellPriceAlert: row.sellPriceAlert ?? null,
            status: 'Not Reached',
            isClosed: false,
          });
          await refreshHoldingsFromDb();

          if (rowPortfolioId) {
            const timerId = window.setTimeout(() => {
              pendingHoldingEditUndoRef.current = null;
              setPendingHoldingEditUndo(null);
            }, HOLDING_EDIT_UNDO_MS);

            pendingHoldingEditUndoRef.current = {
              message: 'Holding reopened',
              timerId,
              snapshot,
              portfolioId: rowPortfolioId,
            };
            setPendingHoldingEditUndo({ message: 'Holding reopened', timerId });
          }
        } catch (e: any) {
          if (isUnauthorized(e)) {
            onUnauthorized?.();
            return;
          }
          onNotify?.(e?.message || 'Failed reopening holding', 'error');
        }
      })();
    }
  };

  const submitSellHolding = () => {
    if (!sellRow) return;

    setSellError(null);
    const sharesToSell = parseNumOrNull(sellSharesText);
    const sellPrice = parseNumOrNull(sellPriceText);
    const sellDateUtc = inputDateToUtcIso(sellDateText);

    const buyDateUtc = (() => {
      const input = displayDateToInputDate(sellRow.buyDate);
      return input ? inputDateToUtcIso(input) : null;
    })();

    const sellDateMs = (() => {
      const t = String(sellDateText || '').trim();
      const parts = t.split('-');
      if (parts.length !== 3) return NaN;
      const [yy, mm, dd] = parts;
      const y = Number(yy);
      const m = Number(mm);
      const d = Number(dd);
      if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return NaN;
      const dt = new Date(y, m - 1, d);
      const ms = dt.getTime();
      return Number.isFinite(ms) ? ms : NaN;
    })();

    const buyDateMs = parseBuyDateToMs(sellRow.buyDate);

    if (!sharesToSell || sharesToSell <= 0) {
      pushSellError('Please enter number of shares to sell.');
      return;
    }
    if (!sellPrice || sellPrice <= 0) {
      pushSellError('Please enter average sell price per share.');
      return;
    }

    if (!sellDateUtc) {
      pushSellError('Please select sell date.');
      return;
    }
    if (Number.isFinite(buyDateMs) && Number.isFinite(sellDateMs) && sellDateMs < buyDateMs) {
      pushSellError('Sell date cannot be before buy date.');
      return;
    }

    const totalShares = Number(sellRow.shares || 0);
    const remainingShares = totalShares - sharesToSell;

    void (async () => {
      if (!authToken) {
        // Local fallback (no persistence)
        const prevRowsSnapshot = rows.slice();
        setRows((prev) => {
          const nowDate = inputDateToDisplayDate(sellDateText) || new Date().toLocaleDateString('en-GB');
          const next: Holding[] = [];
          for (const r of prev) {
            if (r.id !== sellRow.id) {
              next.push(r);
              continue;
            }

            if (sellRow.isClosed) {
              // Selling again from Closed tab: always add a new closed row.
              next.push(r);
              next.push({
                ...r,
                id: `local-sell-${Date.now()}`,
                shares: sharesToSell,
                currentPrice: sellPrice,
                isClosed: true,
                sellDate: nowDate,
                buyPriceAlert: null,
                sellPriceAlert: null,
                status: 'Not Reached',
              });
              continue;
            }

            if (sharesToSell > totalShares) {
              // Keep current rows unchanged; validation handled above in persisted mode.
              next.push(r);
              continue;
            }

            if (remainingShares > 0) {
              next.push({ ...r, shares: remainingShares, isClosed: false });
              next.push({
                ...r,
                id: `local-sell-${Date.now()}`,
                shares: sharesToSell,
                currentPrice: sellPrice,
                isClosed: true,
                sellDate: nowDate,
                buyPriceAlert: null,
                sellPriceAlert: null,
                status: 'Not Reached',
              });
            } else {
              next.push({ ...r, currentPrice: sellPrice, isClosed: true, sellDate: nowDate, buyPriceAlert: null, sellPriceAlert: null, status: 'Not Reached' });
            }
          }
          return next;
        });
        closeSellModal();

        dismissAllPortfoliosUndoSnackbars();
        const existing = pendingHoldingSellUndoRef.current;
        if (existing) window.clearTimeout(existing.timerId);

        const timerId = window.setTimeout(() => {
          pendingHoldingSellUndoRef.current = null;
          setPendingHoldingSellUndo(null);
        }, HOLDING_SELL_UNDO_MS);

        const pending: PendingUndo & { kind: 'local'; rows: Holding[] } = {
          message: 'Holding sold',
          timerId,
          kind: 'local',
          rows: prevRowsSnapshot,
        };

        pendingHoldingSellUndoRef.current = pending;
        setPendingHoldingSellUndo({ message: pending.message, timerId: pending.timerId });
        return;
      }

      try {
        const existingId = Number(sellRow.id);
        const rowPortfolioId = resolveRowPortfolioId(sellRow);
        if (!rowPortfolioId) {
          pushSellError('Please select a portfolio first.');
          return;
        }

        // Selling again from Closed tab: always create a new closed record.
        if (sellRow.isClosed) {
          const created = await adminApi.upsertMyPortfolioHolding(authToken, {
            id: null,
            portfolioId: rowPortfolioId,
            symbol: sellRow.symbol,
            shares: sharesToSell,
            buyPrice: sellRow.buyPrice,
            buyDateUtc,
            currentPrice: sellPrice,
            buyPriceAlert: null,
            sellPriceAlert: null,
            status: 'Not Reached',
            isClosed: true,
            sellDateUtc,
          });
          await refreshHoldingsFromDb();
          closeSellModal();

          const createdId = Number((created as any)?.id);
          if (Number.isFinite(createdId) && createdId > 0) {
            dismissAllPortfoliosUndoSnackbars();
            const timerId = window.setTimeout(() => {
              pendingHoldingSellUndoRef.current = null;
              setPendingHoldingSellUndo(null);
            }, HOLDING_SELL_UNDO_MS);

            const pending: PendingUndo & { kind: 'closed-add'; createdId: number } = {
              message: 'Holding sold',
              timerId,
              kind: 'closed-add',
              createdId,
            };

            pendingHoldingSellUndoRef.current = pending;
            setPendingHoldingSellUndo({ message: pending.message, timerId: pending.timerId });
          }
          return;
        }

        if (sharesToSell > totalShares) {
          pushSellError('Shares to sell cannot exceed shares owned.');
          return;
        }

        if (remainingShares > 0) {
          const snapshot: Holding = { ...sellRow };
          // Update open holding (remaining shares)
          await adminApi.upsertMyPortfolioHolding(authToken, {
            id: Number.isFinite(existingId) && existingId > 0 ? existingId : null,
            portfolioId: rowPortfolioId,
            symbol: sellRow.symbol,
            shares: remainingShares,
            buyPrice: sellRow.buyPrice,
            currentPrice: sellRow.currentPrice,
            buyPriceAlert: null,
            sellPriceAlert: null,
            status: 'Not Reached',
            isClosed: false,
          });

          // Create a closed holding for the sold shares
          const createdClosed = await adminApi.upsertMyPortfolioHolding(authToken, {
            id: null,
            portfolioId: rowPortfolioId,
            symbol: sellRow.symbol,
            shares: sharesToSell,
            buyPrice: sellRow.buyPrice,
            buyDateUtc,
            currentPrice: sellPrice,
            buyPriceAlert: null,
            sellPriceAlert: null,
            status: 'Not Reached',
            isClosed: true,
            sellDateUtc,
          });

          const createdClosedId = Number((createdClosed as any)?.id);
          if (Number.isFinite(existingId) && existingId > 0 && Number.isFinite(createdClosedId) && createdClosedId > 0) {
            dismissAllPortfoliosUndoSnackbars();
            const timerId = window.setTimeout(() => {
              pendingHoldingSellUndoRef.current = null;
              setPendingHoldingSellUndo(null);
            }, HOLDING_SELL_UNDO_MS);

            const pending: PendingUndo & {
              kind: 'partial';
              snapshot: Holding;
              portfolioId: number;
              holdingId: number;
              createdClosedId: number;
            } = {
              message: 'Holding sold',
              timerId,
              kind: 'partial',
              snapshot,
              portfolioId: rowPortfolioId,
              holdingId: existingId,
              createdClosedId,
            };

            pendingHoldingSellUndoRef.current = pending;
            setPendingHoldingSellUndo({ message: pending.message, timerId: pending.timerId });
          }
        } else {
          const snapshot: Holding = { ...sellRow };
          // Sell all -> move holding to closed
          await adminApi.upsertMyPortfolioHolding(authToken, {
            id: Number.isFinite(existingId) && existingId > 0 ? existingId : null,
            portfolioId: rowPortfolioId,
            symbol: sellRow.symbol,
            shares: totalShares,
            buyPrice: sellRow.buyPrice,
            currentPrice: sellPrice,
            buyPriceAlert: null,
            sellPriceAlert: null,
            status: 'Not Reached',
            isClosed: true,
            sellDateUtc,
          });

          if (Number.isFinite(existingId) && existingId > 0) {
            dismissAllPortfoliosUndoSnackbars();
            const timerId = window.setTimeout(() => {
              pendingHoldingSellUndoRef.current = null;
              setPendingHoldingSellUndo(null);
            }, HOLDING_SELL_UNDO_MS);

            const pending: PendingUndo & { kind: 'sell-all'; snapshot: Holding; portfolioId: number; holdingId: number } = {
              message: 'Holding sold',
              timerId,
              kind: 'sell-all',
              snapshot,
              portfolioId: rowPortfolioId,
              holdingId: existingId,
            };

            pendingHoldingSellUndoRef.current = pending;
            setPendingHoldingSellUndo({ message: pending.message, timerId: pending.timerId });
          }
        }

        await refreshHoldingsFromDb();
        closeSellModal();
      } catch (e: any) {
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        pushSellError(e?.message || 'Failed selling holding');
      }
    })();
  };

  return (
    <div
      style={{
        padding: '18px 10px 18px 3.2rem',
        backgroundColor: colors.pageBg,
        minHeight: 'calc(100vh - 4.375rem)',
        color: colors.text,
      }}
      id="portfolios-page"
      data-testid="portfolios-page"
    >
      {isPortfolioNameModalOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={portfolioNameModalMode === 'edit' ? 'Edit portfolio name' : 'Add portfolio name'}
          id="portfolios-portfolio-name-modal-overlay"
          data-testid="portfolios-portfolio-name-modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setIsPortfolioNameModalOpen(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: isDarkMode ? 'rgba(0,0,0,0.55)' : 'rgba(15,23,42,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 18,
            zIndex: 6000,
          }}
        >
          <div
            onMouseDown={(e) => e.stopPropagation()}
            id="portfolios-portfolio-name-modal"
            data-testid="portfolios-portfolio-name-modal"
            style={{
              width: 420,
              backgroundColor: colors.cardBg,
              border: `1px solid ${colors.cardBorder}`,
              borderRadius: 12,
              boxShadow: colors.shadow,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                height: 56,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 16px',
                borderBottom: `1px solid ${colors.cardBorder}`,
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 900, color: colors.text }}>
                {portfolioNameModalMode === 'edit' ? 'Edit portfolio name' : 'Add portfolio name'}
              </div>
              <button
                type="button"
                onClick={() => setIsPortfolioNameModalOpen(false)}
                aria-label="Close"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border: `1px solid ${colors.cardBorder}`,
                  backgroundColor: 'transparent',
                  color: colors.subText,
                  cursor: 'pointer',
                  fontSize: 16,
                  lineHeight: '32px',
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: 16, display: 'grid', gap: 10 }}>
              <input
                ref={portfolioNameModalInputRef}
                value={portfolioNameModalValue}
                onChange={(e) => {
                  setPortfolioNameModalValue(e.target.value);
                  if (portfolioNameModalError) setPortfolioNameModalError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitPortfolioNameModal();
                  if (e.key === 'Escape') setIsPortfolioNameModalOpen(false);
                }}
                placeholder={portfolioNameModalMode === 'edit' ? 'Edit portfolio name' : 'Add portfolio name'}
                id="portfolios-portfolio-name-input"
                data-testid="portfolios-portfolio-name-input"
                style={{
                  width: '100%',
                  height: 40,
                  borderRadius: 8,
                  border: `1px solid ${colors.cardBorder}`,
                  backgroundColor: isDarkMode ? '#0b1220' : '#ffffff',
                  color: colors.text,
                  padding: '0 10px',
                  outline: 'none',
                  fontSize: 13,
                }}
                disabled={portfolioNameModalBusy || (portfolioNameModalMode === 'add' && portfolios.length >= MAX_PORTFOLIOS_PER_USER)}
              />

              {portfolioNameModalError ? (
                <div style={{ fontSize: 12, fontWeight: 800, color: isDarkMode ? '#fca5a5' : '#b91c1c' }}>{portfolioNameModalError}</div>
              ) : null}
            </div>

            <div
              style={{
                padding: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 10,
                borderTop: `1px solid ${colors.cardBorder}`,
              }}
            >
              <button
                type="button"
                onClick={() => setIsPortfolioNameModalOpen(false)}
                disabled={portfolioNameModalBusy}
                style={{
                  height: 38,
                  padding: '0 16px',
                  borderRadius: 8,
                  border: `1px solid ${colors.cardBorder}`,
                  backgroundColor: 'transparent',
                  color: colors.text,
                  fontWeight: 900,
                  cursor: portfolioNameModalBusy ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitPortfolioNameModal}
                disabled={portfolioNameModalBusy || (portfolioNameModalMode === 'add' && portfolios.length >= MAX_PORTFOLIOS_PER_USER)}
                id="portfolios-portfolio-name-submit"
                data-testid="portfolios-portfolio-name-submit"
                style={{
                  height: 38,
                  padding: '0 16px',
                  borderRadius: 8,
                  border: '1px solid transparent',
                  backgroundColor: '#2F58F2',
                  color: '#ffffff',
                  fontWeight: 900,
                  cursor:
                    portfolioNameModalBusy || (portfolioNameModalMode === 'add' && portfolios.length >= MAX_PORTFOLIOS_PER_USER) ? 'not-allowed' : 'pointer',
                  minWidth: 110,
                  opacity: portfolioNameModalBusy || (portfolioNameModalMode === 'add' && portfolios.length >= MAX_PORTFOLIOS_PER_USER) ? 0.7 : 1,
                }}
              >
                {portfolioNameModalMode === 'edit' ? 'Save' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <style>{`
        #portfolios-holdings-table-wrapper {
          scrollbar-width: thin; /* Firefox */
          scrollbar-color: ${isDarkMode ? '#374151' : '#cbd5f5'} transparent;
        }
        #portfolios-holdings-table-wrapper::-webkit-scrollbar {
          height: 8px;
        }
        #portfolios-holdings-table-wrapper::-webkit-scrollbar-track {
          background: transparent;
        }
        #portfolios-holdings-table-wrapper::-webkit-scrollbar-thumb {
          background: ${isDarkMode ? '#374151' : '#cbd5f5'};
          border-radius: 999px;
        }
      `}</style>
      {(!authToken || (portfolioError && portfoliosLoaded)) ? (
        <div
          style={{
            margin: '0 0 12px 0',
            maxWidth: 1600,
            padding: 12,
            borderRadius: 10,
            border: `1px solid ${colors.cardBorder}`,
            background: isDarkMode ? 'rgba(255,255,255,0.03)' : '#fff7ed',
            color: isDarkMode ? colors.text : '#9a3412',
            fontWeight: 800,
            fontSize: 13,
          }}
        >
          {authToken ? portfolioError : 'You must be logged in to view Portfolios.'}
        </div>
      ) : null}
      <div
        style={{
          width: '100%',
          maxWidth: 1650,
          margin: 0,
          boxSizing: 'border-box',
        }}
        id="portfolios-container"
        data-testid="portfolios-container"
      >
      <div
        style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}
        id="portfolios-topbar"
        data-testid="portfolios-topbar"
      >
        <div
          ref={portfolioMenuRef}
          style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
          id="portfolios-portfolio-menu"
          data-testid="portfolios-portfolio-menu"
        >
          <div
            id="portfolios-portfolio-menu-toggle"
            data-testid="portfolios-portfolio-menu-toggle"
            style={{
              height: 48,
              minWidth: 220,
              borderRadius: 10,
              border: 'none',
              background: colors.cardBg,
              padding: '0 10px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 25,
              fontWeight: 800,
              fontSize: 13,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
            }}
            onClick={() => setIsPortfolioMenuOpen((prev) => !prev)}
          >
            <span
              style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
                fontWeight: 600,
                fontSize: 22,
                lineHeight: '1.3',
                paddingBottom: 2,
                letterSpacing: '0.01em',
                color: isDarkMode ? colors.text : '#0D0D0D',
              }}
            >
              {selectedPortfolioName}
            </span>
            <img
              src="/images/arrow.svg"
              alt=""
              aria-hidden="true"
              style={{ width: 20, height: 20, display: 'block', opacity: 1, filter: 'brightness(0) saturate(100%) invert(24%) sepia(90%) saturate(1565%) hue-rotate(214deg) brightness(95%) contrast(96%)' }}
            />
          </div>
          {isPortfolioMenuOpen && (
            <div
              role="menu"
              aria-label="Select portfolio"
              style={{
                position: 'absolute',
                top: 54,
                left: 0,
                minWidth: 260,
                background: colors.cardBg,
                border: `1px solid ${colors.cardBorder}`,
                borderRadius: 10,
                boxShadow: colors.shadow,
                padding: 8,
                zIndex: 100,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              {portfolios.map((p, index) => {
                const selected = selectedPortfolioId === p.id;
                const isLast = index === portfolios.length - 1;
                return (
                  <React.Fragment key={p.id}>
                    <div
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}
                    >
                      <button
                        type="button"
                        onClick={() => selectPortfolio(p.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          padding: '6px 8px',
                          textAlign: 'left',
                          borderRadius: 8,
                          flex: 1,
                        }}
                      >
                        <span
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: 999,
                            border: `2px solid ${selected ? colors.primary : colors.cardBorder}`,
                            background: selected ? colors.primary : 'transparent',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxSizing: 'border-box',
                          }}
                        >
                          {selected ? (
                            <svg width="12" height="9" viewBox="0 0 12 9" aria-hidden="true">
                              <path d="M1 4.5L4.5 8L11 1" fill="none" stroke="#F6F6F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          ) : null}
                        </span>
                        <span style={{ fontWeight: 600, color: colors.text }}>{p.name}</span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditPortfolioModal(p);
                        }}
                        title="Edit portfolio"
                        aria-label="Edit portfolio"
                        style={{
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          padding: 4,
                          borderRadius: 6,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <img src="/images/action-edit.svg" alt="" aria-hidden="true" style={{ width: 16, height: 16 }} />
                      </button>
                    </div>
                    {!isLast && <div style={{ height: 1, background: '#D9D9D9', width: '100%', margin: '4px 0' }} />}
                  </React.Fragment>
                );
              })}

              <div style={{ height: 1, background: '#D9D9D9', width: '100%', margin: '4px 0' }} />

              <button
                type="button"
                onClick={() => selectPortfolio(ALL_PORTFOLIOS_ID)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: '6px 8px',
                  textAlign: 'left',
                  borderRadius: 8,
                }}
              >
                <span
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 999,
                    border: `2px solid ${isAllPortfolios ? colors.primary : colors.cardBorder}`,
                    background: isAllPortfolios ? colors.primary : 'transparent',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxSizing: 'border-box',
                  }}
                >
                  {isAllPortfolios ? (
                    <svg width="12" height="9" viewBox="0 0 12 9" aria-hidden="true">
                      <path d="M1 4.5L4.5 8L11 1" fill="none" stroke="#F6F6F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : null}
                </span>
                <span style={{ fontWeight: 600, color: colors.text }}>All Portfolios</span>
              </button>

              <div style={{ height: 1, background: '#D9D9D9', width: '100%', margin: '4px 0' }} />

              <button
                type="button"
                onClick={openAddPortfolioModal}
                disabled={!canAddPortfolio}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  border: 'none',
                  background: 'transparent',
                  cursor: canAddPortfolio ? 'pointer' : 'not-allowed',
                  padding: '6px 8px',
                  textAlign: 'left',
                  borderRadius: 8,
                  color: canAddPortfolio ? (isDarkMode ? '#F9FAFB' : '#0D0D0D') : colors.subText,
                  opacity: canAddPortfolio ? 1 : 0.6,
                  fontWeight: 700,
                }}
              >
                <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1, color: '#2F58F2' }}>+</span>
                <span>Add</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div>
        <div
          id="portfolios-charts-grid"
          data-testid="portfolios-charts-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, 518px)',
            gap: 34,
            alignItems: 'stretch',
            justifyContent: 'start',
            marginLeft: 5,
            marginBottom: 14,
          }}
        >
        {/* Card 1: Portfolio Holdings (donut) */}
        {holdingsCharts.map((chart) => (
          <div
            key={chart.key}
            style={graphCardStyle}
            id={chart.key}
            data-testid={`portfolios-card-${chart.key}`}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <button
                    style={expandIconStyle}
                    onClick={() => {
                      setExpandedHoldingsKey(chart.key);
                      setExpandedChart('portfolio-holdings');
                    }}
                    title="Expand"
                    aria-label="Expand"
                    id={chart.isPrimary ? 'portfolios-expand-portfolio-holdings' : `portfolios-expand-${chart.key}`}
                    data-testid={chart.isPrimary ? 'portfolios-expand-portfolio-holdings' : `portfolios-expand-${chart.key}`}
                  >
                    <img src={zoomInIcon} alt="Expand" style={{ width: 24, height: 24 }} />
                  </button>
                  <div
                    style={{
                      fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
                      fontWeight: 900,
                      fontSize: 16,
                      color: isDarkMode ? colors.text : '#0D0D0D',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {chart.title}
                  </div>
                </div>

                <button
                  id={chart.isPrimary ? 'portfolios-bookmark-portfolio-holdings' : `portfolios-bookmark-${chart.key}`}
                  data-testid={chart.isPrimary ? 'portfolios-bookmark-portfolio-holdings' : `portfolios-bookmark-${chart.key}`}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      padding: 4,
                      outline: 'none',
                      boxShadow: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    opacity: savingBookmarkByChartId[chart.key] ? 0.6 : 1,
                      flex: '0 0 auto',
                    }}
                    title="Bookmark"
                    aria-label="Bookmark"
                  disabled={savingBookmarkByChartId[chart.key]}
                  onClick={() => saveChartBookmark(chart.key, 'Portfolio Holdings', isAllPortfolios ? chart.title : undefined)}
                  >
                    <img
                    src={bookmarkFlashByChartId[chart.key] ? '/images/bookmark_fill.svg' : bookmarkEmptyIcon}
                      alt="Bookmark"
                      style={{ width: 16, height: 16 }}
                    />
                  </button>
              </div>
            </div>

            <div
              style={{
                flex: 1,
                minHeight: 0,
                display: 'grid',
                gridTemplateColumns: '1fr 140px',
                gap: 12,
                alignItems: 'stretch',
                paddingLeft: 20,
              }}
            >
              <div style={{ minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div
                  style={{ width: 240, height: 240, maxWidth: '100%', maxHeight: '100%', position: 'relative', cursor: 'pointer' }}
                  role="button"
                  tabIndex={0}
                  aria-label="Expand Portfolio Holdings chart"
                  id={`portfolios-chart-${chart.key}`}
                  data-testid={`portfolios-chart-${chart.key}`}
                  onClick={() => {
                    setExpandedHoldingsKey(chart.key);
                    setExpandedChart('portfolio-holdings');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExpandedHoldingsKey(chart.key);
                      setExpandedChart('portfolio-holdings');
                    }
                  }}
                >
                  <Doughnut
                    ref={setDoughnutChartRef(chart.key)}
                    data={chart.data}
                    options={chart.options}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <div
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        fontStyle: 'normal',
                        fontSize: 18,
                        fontWeight: 600,
                        lineHeight: '100%',
                        letterSpacing: '0px',
                        color: '#2E384D',
                        marginBottom: 8,
                      }}
                    >
                      Current Value
                    </div>
                    <AutoFitText
                      text={formatCurrency0(chart.total)}
                      maxFontSize={27}
                      minFontSize={27}
                      fontFamily="Manrope, sans-serif"
                      fontWeight={300}
                      color="#0D0D0D"
                      maxWidth="68%"
                      paddingX={6}
                    />
                    {Number.isFinite(chart.pnl) && (
                      <div
                        style={{
                          fontFamily: 'Manrope, sans-serif',
                          fontStyle: 'normal',
                          fontSize: 18,
                          fontWeight: 500,
                          lineHeight: '100%',
                          letterSpacing: '0px',
                          color: chart.pnl > 0 ? '#12B76A' : chart.pnl < 0 ? colors.danger : colors.subText,
                          marginTop: 4,
                        }}
                      >
                        ({chart.pnl > 0 ? '+' : chart.pnl < 0 ? '-' : ''}{formatCurrency0(Math.abs(chart.pnl))})
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div
                style={{
                  minHeight: 0,
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  gap: 8,
                  width: '100%',
                  paddingLeft: 20,
                  paddingRight: 0,
                  boxSizing: 'border-box',
                  scrollbarGutter: 'stable',
                }}
              >
                {chart.data.labels.map((lbl, i) => {
                  const bg = (chart.data.datasets[0].backgroundColor as string[])[i];
                  return (
                    <div
                      key={lbl}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10, width: '100%' }}
                      onMouseEnter={(e) => showPortfolioLegendTooltip(doughnutChartRefs.current[chart.key] ?? null, e.currentTarget, i, chart.total, e.clientX, e.clientY)}
                      onMouseMove={(e) => showPortfolioLegendTooltip(doughnutChartRefs.current[chart.key] ?? null, e.currentTarget, i, chart.total, e.clientX, e.clientY)}
                      onMouseLeave={() => hidePortfolioLegendTooltip(doughnutChartRefs.current[chart.key] ?? null)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 999, background: bg }} />
                        <span
                          style={{
                            fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
                            fontSize: 16,
                            fontWeight: 400,
                            color: isDarkMode ? colors.text : '#0D0D0D',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {lbl}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}

        {!isAllPortfolios && (
          <div style={graphCardStyle} id="profit-loss" data-testid="portfolios-card-profit-loss">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <button
                  style={expandIconStyle}
                  onClick={() => setExpandedChart('profit-loss')}
                  title="Expand"
                  aria-label="Expand"
                  id="portfolios-expand-profit-loss"
                  data-testid="portfolios-expand-profit-loss"
                >
                  <img src={zoomInIcon} alt="Expand" style={{ width: 24, height: 24 }} />
                </button>
                <div style={{ fontWeight: 900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Profit/Loss per Share</div>
              </div>

              <button
                id="portfolios-bookmark-profit-loss"
                data-testid="portfolios-bookmark-profit-loss"
                style={{
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  padding: 4,
                  outline: 'none',
                  boxShadow: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: savingBookmarkByChartId['profit-loss'] ? 0.6 : 1,
                  flex: '0 0 auto',
                }}
                title="Bookmark"
                aria-label="Bookmark"
                disabled={savingBookmarkByChartId['profit-loss']}
                onClick={() => saveChartBookmark('profit-loss', 'Profit/Loss per Share')}
              >
                <img
                  src={bookmarkFlashByChartId['profit-loss'] ? '/images/bookmark_fill.svg' : bookmarkEmptyIcon}
                  alt="Bookmark"
                  style={{ width: 16, height: 16 }}
                />
              </button>
            </div>
          </div>
          <div style={{ flex: 1, minHeight: 0 }}>
            <div
              style={{ height: '100%', minHeight: 0, cursor: 'pointer' }}
              role="button"
              tabIndex={0}
              aria-label="Expand Profit/Loss per Share chart"
              id="portfolios-chart-profit-loss"
              data-testid="portfolios-chart-profit-loss"
              onClick={() => setExpandedChart('profit-loss')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setExpandedChart('profit-loss');
                }
              }}
            >
              <Bar data={plBarData} options={plBarOptions} />
            </div>
          </div>
          </div>
        )}

        {!isAllPortfolios && (
          <div style={graphCardStyle} id="buy-vs-current" data-testid="portfolios-card-buy-vs-current">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <button
                  style={expandIconStyle}
                  onClick={() => setExpandedChart('buy-vs-current')}
                  title="Expand"
                  aria-label="Expand"
                  id="portfolios-expand-buy-vs-current"
                  data-testid="portfolios-expand-buy-vs-current"
                >
                  <img src={zoomInIcon} alt="Expand" style={{ width: 24, height: 24 }} />
                </button>
                <div style={{ fontWeight: 900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  Cost and Market Value per Holding
                </div>
              </div>

              <button
                id="portfolios-bookmark-buy-vs-current"
                data-testid="portfolios-bookmark-buy-vs-current"
                style={{
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  padding: 4,
                  outline: 'none',
                  boxShadow: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: savingBookmarkByChartId['buy-vs-current'] ? 0.6 : 1,
                  flex: '0 0 auto',
                }}
                title="Bookmark"
                aria-label="Bookmark"
                disabled={savingBookmarkByChartId['buy-vs-current']}
                onClick={() => saveChartBookmark('buy-vs-current', 'Cost and Market Value per Holding')}
              >
                <img
                  src={bookmarkFlashByChartId['buy-vs-current'] ? '/images/bookmark_fill.svg' : bookmarkEmptyIcon}
                  alt="Bookmark"
                  style={{ width: 16, height: 16 }}
                />
              </button>
            </div>
          </div>
          <div style={{ flex: 1, minHeight: 0 }}>
            <div
              style={{ height: '100%', minHeight: 0, cursor: 'pointer' }}
              role="button"
              tabIndex={0}
              aria-label="Expand Cost and Market Value per Holding chart"
              id="portfolios-chart-buy-vs-current"
              data-testid="portfolios-chart-buy-vs-current"
              onClick={() => setExpandedChart('buy-vs-current')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setExpandedChart('buy-vs-current');
                }
              }}
            >
              <Bar data={compareData} options={compareOptions} />
            </div>
          </div>
          </div>
        )}
        </div>
      </div>

      <div
        style={{
          ...tableCardStyle,
          padding: 0,
          overflow: 'hidden',
          maxWidth: '99%',
          border: 'none',
          boxShadow: 'none',
        }}
        id="portfolios-holdings-card"
        data-testid="portfolios-holdings-card"
      >
        <div
          style={{ padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', backgroundColor: isDarkMode ? '#111827' : '#FFFFFF' }}
          id="portfolios-holdings-toolbar"
          data-testid="portfolios-holdings-toolbar"
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              style={{
                fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
                fontWeight: activeTab === 'holdings' ? 700 : 400,
                fontSize: 16,
                color: activeTab === 'holdings'
                  ? (isDarkMode ? '#000000' : '#FFFFFF')
                  : (isDarkMode ? '#F9FAFB' : '#1E2127'),
                background: activeTab === 'holdings' ? '#2F58F2' : isDarkMode ? colors.cardBg : '#FFFFFF',
                width: 180,
                height: 34,
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                verticalAlign: 'middle',
              }}
              onClick={() => {
                portfolioLastTabDirtyRef.current = true;
                setActiveTab('holdings');
                if (!authToken) return;
                void adminApi.setPortfolioLastTab(authToken, 'holdings').catch(() => {
                  // Best-effort
                });
              }}
              id="portfolios-tab-holdings"
              data-testid="portfolios-tab-holdings"
            >
              Portfolio Holdings
            </button>
            <button
              type="button"
              style={{
                ...tabButton(activeTab === 'closed'),
                border: 'none',
                fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
                fontWeight: 400,
                fontSize: 16,
                color: activeTab === 'closed'
                  ? (isDarkMode ? '#000000' : '#FFFFFF')
                  : (isDarkMode ? '#F9FAFB' : '#1E2127'),
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                verticalAlign: 'middle',
              }}
              onClick={() => {
                portfolioLastTabDirtyRef.current = true;
                setActiveTab('closed');
                if (!authToken) return;
                void adminApi.setPortfolioLastTab(authToken, 'closed').catch(() => {
                  // Best-effort
                });
              }}
              id="portfolios-tab-closed"
              data-testid="portfolios-tab-closed"
            >
              Closed Holdings
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginLeft: 'auto', transform: 'translateX(10px)' }}>
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
                fontWeight: 500,
                fontSize: 16,
                color: isDarkMode ? colors.subText : '#474D55',
              }}
              id="portfolios-group-by-stock-label"
              data-testid="portfolios-group-by-stock-label"
            >
              <input
                type="checkbox"
                checked={groupByStock}
                onChange={(e) => {
                  const next = e.target.checked;
                  groupByStockDirtyRef.current = true;
                  setGroupByStock(next);
                  if (!authToken) return;
                  void adminApi.setPortfolioGroupByStock(authToken, next).catch(() => {
                    // Best-effort: don't break UX if preference save fails.
                  });
                }}
                id="portfolios-group-by-stock"
                data-testid="portfolios-group-by-stock"
                style={{
                  width: 16,
                  height: 18,
                  margin: 0,
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  borderRadius: 4,
                  border: `1px solid ${groupByStock ? (isDarkMode ? '#3B82F6' : '#2F58F2') : isDarkMode ? colors.cardBorder : '#C7CED9'}`,
                  backgroundColor: groupByStock ? (isDarkMode ? '#3B82F6' : '#2F58F2') : 'transparent',
                  backgroundImage: groupByStock
                    ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 12'%3E%3Cpath d='M2 6.5l3.2 3.2L14 1.9' stroke='${isDarkMode ? '%23374151' : '%23fff'}' stroke-width='2.2' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`
                    : 'none',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  cursor: 'pointer',
                  flex: '0 0 auto',
                }}
              />
              <span>Group by </span>
              <span style={{ color: isDarkMode ? colors.text : '#232A31' }}>Stock</span>
            </label>

            <button
              type="button"
              style={{
                ...primaryButton,
                background: isDarkMode ? '#111827' : '#FFFFFF',
                width: 150,
                height: 37,
                borderRadius: 6,
                border: `1px solid ${isDarkMode ? '#3B82F6' : colors.primary}`,
                padding: '4px 14px',
                opacity: activeTab === 'closed' || isAllPortfolios ? 0.5 : 1,
                color: isDarkMode ? '#3B82F6' : colors.primary,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                cursor: activeTab === 'closed' || isAllPortfolios ? 'not-allowed' : 'pointer',
              }}
              disabled={activeTab === 'closed' || isAllPortfolios}
              onClick={() => {
                if (activeTab === 'closed' || isAllPortfolios) return;
                openAddHoldingModal();
              }}
              id="portfolios-add-holding"
              data-testid="portfolios-add-holding"
            >
              <img src="/images/plus.svg" alt="" aria-hidden="true" style={{ width: 16, height: 16 }} />
              <span
                style={{
                  fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
                  fontWeight: 600,
                  fontSize: 14,
                  lineHeight: '100%',
                  letterSpacing: 0,
                  verticalAlign: 'middle',
                  color: isDarkMode ? '#3B82F6' : '#2F58F2',
                }}
              >
                Add Holding
              </span>
            </button>
          </div>
        </div>

        <div
          style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            border: isDarkMode ? '1px solid #374151' : '1px solid #ECECF4',
            borderRadius: 4.52,
          }}
          ref={holdingsTableWrapRef}
          data-overflow-x={hasHoldingsOverflowX ? '1' : '0'}
          id="portfolios-holdings-table-wrapper"
          data-testid="portfolios-holdings-table-wrapper"
        >
          <table style={tableStyle} aria-label="Portfolio holdings table" id="portfolios-holdings-table" data-testid="portfolios-holdings-table">
            <thead>
              <tr>
                {activeTab === 'closed' ? (
                  <>
                    {isAllPortfolios && (
                      <th style={thClickable('portfolioName')} onClick={() => toggleSort('portfolioName')}>
                        Portfolio Name{sortIndicator('portfolioName')}
                      </th>
                    )}
                    <th style={thClickable('symbol')} onClick={() => toggleSort('symbol')}>
                      Stock Symbol{sortIndicator('symbol')}
                    </th>
                    <th style={thClickable('buyDate')} onClick={() => toggleSort('buyDate')}>
                      Buy Date{sortIndicator('buyDate')}
                    </th>
                    <th style={thClickable('shares')} onClick={() => toggleSort('shares')}>
                      Shares Sold{sortIndicator('shares')}
                    </th>
                    <th style={thClickable('buyPrice')} onClick={() => toggleSort('buyPrice')}>
                      Buy Price{sortIndicator('buyPrice')}
                    </th>
                    <th style={thClickable('sellDate')} onClick={() => toggleSort('sellDate')}>
                      Sell Date{sortIndicator('sellDate')}
                    </th>
                    <th style={thClickable('currentPrice')} onClick={() => toggleSort('currentPrice')}>
                      Sell Price{sortIndicator('currentPrice')}
                    </th>
                    <th style={thClickable('changePct')} onClick={() => toggleSort('changePct')}>
                      Change (%){sortIndicator('changePct')}
                    </th>
                    <th style={thClickable('profitLoss')} onClick={() => toggleSort('profitLoss')}>
                      Profit/Loss ($){sortIndicator('profitLoss')}
                    </th>
                    {!isAllPortfolios && <th style={{ ...thStyle, borderRight: 'none' }}>Actions</th>}
                  </>
                ) : (
                  <>
                    {isAllPortfolios && (
                      <th style={thClickable('portfolioName')} onClick={() => toggleSort('portfolioName')}>
                        Portfolio Name{sortIndicator('portfolioName')}
                      </th>
                    )}
                    <th style={thClickable('symbol')} onClick={() => toggleSort('symbol')}>
                      Stock Symbol{sortIndicator('symbol')}
                    </th>
                    <th style={thClickable('buyDate')} onClick={() => toggleSort('buyDate')}>
                      Buy Date{sortIndicator('buyDate')}
                    </th>
                    <th style={thClickable('buyPrice')} onClick={() => toggleSort('buyPrice')}>
                      Buy Price{sortIndicator('buyPrice')}
                    </th>
                    <th style={thClickable('currentPrice')} onClick={() => toggleSort('currentPrice')}>
                      Current Price{sortIndicator('currentPrice')}
                    </th>
                    <th style={thClickable('changePct')} onClick={() => toggleSort('changePct')}>
                      Change (%){sortIndicator('changePct')}
                    </th>
                    <th style={thClickable('shares')} onClick={() => toggleSort('shares')}>
                      Shares{sortIndicator('shares')}
                    </th>
                    <th style={{ ...thClickable('cost'), width: costColumnWidth }} onClick={() => toggleSort('cost')}>
                      Cost{sortIndicator('cost')}
                    </th>
                    <th style={{ ...thClickable('marketValue'), width: marketValueColumnWidth }} onClick={() => toggleSort('marketValue')}>
                      Market Value{sortIndicator('marketValue')}
                    </th>
                    <th style={thClickable('profitLoss')} onClick={() => toggleSort('profitLoss')}>
                      Profit/Loss{sortIndicator('profitLoss')}
                    </th>
                    <th style={thClickable('buyPriceAlert')} onClick={() => toggleSort('buyPriceAlert')}>
                      Buy Price Alert{sortIndicator('buyPriceAlert')}
                    </th>
                    <th style={thClickable('sellPriceAlert')} onClick={() => toggleSort('sellPriceAlert')}>
                      Sell Price Alert{sortIndicator('sellPriceAlert')}
                    </th>
                    <th style={thClickable('status')} onClick={() => toggleSort('status')}>
                      Alert Status{sortIndicator('status')}
                    </th>
                    {!isAllPortfolios && <th style={{ ...thStyle, borderRight: 'none' }}>Actions</th>}
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {sortedTableRows.map((r) => {
                const isUnifiedRow = groupByStock && (r.lotCount ?? 1) > 1;
                const portfolioLabel = portfolioNameById.get(r.portfolioId) || (r.portfolioId > 0 ? `Portfolio ${r.portfolioId}` : 'Multiple');
                const stockColor = getSymbolColor(r.symbol);

                const symbolCell = (
                  <td style={{ ...tdStyle, textAlign: 'left' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <span
                        aria-hidden="true"
                        style={{
                          width: 6,
                          height: 22,
                          borderRadius: 999,
                          background: stockColor,
                          display: 'inline-block',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => onOpenResearch?.(r.symbol)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          padding: 0,
                          margin: 0,
                          cursor: onOpenResearch ? 'pointer' : 'default',
                          color: isDarkMode ? colors.text : '#000000',
                          font: 'inherit',
                          textDecoration: 'none',
                        }}
                        title={onOpenResearch ? `Open ${r.symbol} in Charts` : r.symbol}
                        aria-label={onOpenResearch ? `Open ${r.symbol} in Charts` : r.symbol}
                        id={`portfolios-open-charts-${r.id}`}
                        data-testid={`portfolios-open-charts-${r.id}`}
                      >
                        {r.symbol}
                      </button>
                    </div>
                  </td>
                );

                const actionsCell = !isAllPortfolios ? (
                  <td style={{ ...tdStyle, borderRight: 'none' }}>
                    <button
                      type="button"
                      id={`portfolios-holding-actions-btn-${r.id}`}
                      data-testid={`portfolios-holding-actions-btn-${r.id}`}
                      aria-haspopup="menu"
                      aria-expanded={holdingActionsOpenForId === r.id}
                      aria-controls={holdingActionsOpenForId === r.id ? `portfolios-holding-actions-menu-${r.id}` : undefined}
                      aria-disabled={isUnifiedRow || undefined}
                      disabled={isUnifiedRow}
                      onClick={() => toggleHoldingActionsMenu(r.id)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: isUnifiedRow ? colors.subText : colors.text,
                        padding: '6px 8px',
                        borderRadius: 10,
                        fontWeight: 400,
                        cursor: isUnifiedRow ? 'not-allowed' : 'pointer',
                        outline: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        width: 98,
                        opacity: isUnifiedRow ? 0.55 : 1,
                      }}
                    >
                      <span>Actions</span>
                      <img
                        src="/images/action-down.svg"
                        alt=""
                        aria-hidden="true"
                        style={{ width: 14, height: 14, display: 'block', opacity: isDarkMode ? 0.9 : 0.8 }}
                      />
                    </button>
                  </td>
                ) : null;

                if (activeTab === 'closed') {
                  const { profitLoss, changePct } = getClosedMetrics(r);
                  const plColor = Number.isFinite(profitLoss) && profitLoss >= 0 ? colors.success : colors.danger;

                  return (
                    <tr
                      key={r.id}
                      id={`portfolios-holding-row-${r.id}`}
                      data-testid={`portfolios-holding-row-${r.id}`}
                      style={{ background: 'transparent' }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background = colors.tableRowHover;
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background = 'transparent';
                      }}
                    >
                      {isAllPortfolios && (
                        <td style={{ ...tdStyle, textAlign: 'left' }}>{portfolioLabel}</td>
                      )}
                      {symbolCell}
                      <td
                        style={
                          groupByStock && (r.lotCount ?? 1) > 1
                            ? { ...tdStyle, color: colors.subText, opacity: isDarkMode ? 0.75 : 0.7, fontWeight: 500 }
                            : tdStyle
                        }
                      >
                        {r.buyDate}
                      </td>
                      <td style={tdStyle}>{formatNumber(r.shares)}</td>
                      <td style={tdStyle}>{formatCurrency(r.buyPrice)}</td>
                      <td style={tdStyle}>{r.sellDate || ''}</td>
                      <td style={tdStyle}>{formatCurrencyMaybe(r.currentPrice)}</td>
                      <td style={{ ...tdStyle, color: plColor }}>
                        {Number.isFinite(changePct) ? `${changePct >= 0 ? '+' : ''}${changePct.toFixed(2)}%` : ''}
                      </td>
                      <td style={{ ...tdStyle, color: plColor }}>
                        {Number.isFinite(profitLoss) ? formatCurrency(profitLoss) : ''}
                      </td>
                      {actionsCell}
                    </tr>
                  );
                }

                const currentPrice = r.currentPrice;
                const effectiveCurrentPrice = (typeof currentPrice === 'number' && Number.isFinite(currentPrice) && currentPrice > 0)
                  ? currentPrice
                  : r.buyPrice;
                const pl = (effectiveCurrentPrice - r.buyPrice) * r.shares;
                const plPct = r.buyPrice ? ((effectiveCurrentPrice - r.buyPrice) / r.buyPrice) * 100 : 0;
                const changePct = (() => {
                  const pct = getLatestChangePct(r.symbol, r.changePct ?? null);
                  return pct == null ? NaN : Number(pct);
                })();
                const cost = (r.buyPrice || 0) * (r.shares || 0);
                const marketValue = (effectiveCurrentPrice || 0) * (r.shares || 0);
                const plColor = pl >= 0 ? colors.success : colors.danger;
                const plDigitCount = Math.abs(pl).toFixed(2).replace(/[^0-9]/g, '').length;
                const shouldStackProfitLoss = plDigitCount >= 3;

                return (
                  <tr
                    key={r.id}
                    id={`portfolios-holding-row-${r.id}`}
                    data-testid={`portfolios-holding-row-${r.id}`}
                    style={{ background: 'transparent' }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.background = colors.tableRowHover;
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.background = 'transparent';
                    }}
                  >
                    {isAllPortfolios && (
                      <td style={{ ...tdStyle, textAlign: 'left' }}>{portfolioLabel}</td>
                    )}
                    {symbolCell}
                    <td
                      style={
                        groupByStock && (r.lotCount ?? 1) > 1
                          ? { ...tdStyle, color: colors.subText, opacity: isDarkMode ? 0.75 : 0.7, fontWeight: 500 }
                          : tdStyle
                      }
                    >
                      {r.buyDate}
                    </td>
                    <td style={tdStyle}>{formatCurrency(r.buyPrice)}</td>
                    <td
                      style={{
                        ...tdStyle,
                        color: isQuotesRefreshing ? '#8a8a8a' : tdStyle.color,
                      }}
                    >
                      {formatCurrencyMaybe(r.currentPrice)}
                    </td>
                    <td style={tdStyle}>
                      {(() => {
                        const c = changePct;
                        const isPos = Number.isFinite(c) && c > 0;
                        const isNeg = Number.isFinite(c) && c < 0;
                        const color = isPos ? colors.success : isNeg ? colors.danger : colors.subText;
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', fontWeight: 900, color }}>
                            {Number.isFinite(c) ? formatPctUpTo2(c) : ''}
                            {isPos && <ChangeArrowIcon dir="up" color={color} />}
                            {isNeg && <ChangeArrowIcon dir="down" color={color} />}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={tdStyle}>{formatNumber(r.shares)}</td>
                    <td style={{ ...tdStyle, width: costColumnWidth }}>{formatCurrency(cost)}</td>
                    <td style={{ ...tdStyle, width: marketValueColumnWidth }}>{formatCurrency(marketValue)}</td>
                    <td style={{ ...tdStyle, color: plColor }}>
                      {shouldStackProfitLoss ? (
                        <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 4, lineHeight: 1.1 }}>
                          <span>{formatCurrency(pl)}</span>
                          <span>({plPct >= 0 ? '+' : ''}{plPct.toFixed(2)}%)</span>
                        </span>
                      ) : (
                        <span>
                          {formatCurrency(pl)} ({plPct >= 0 ? '+' : ''}{plPct.toFixed(2)}%)
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>{formatCurrencyAlert(r.buyPriceAlert)}</td>
                    <td style={tdStyle}>{formatCurrencyAlert(r.sellPriceAlert)}</td>
                    <td style={{ ...tdStyle, textAlign: 'center', overflow: 'visible', textOverflow: 'clip' }}>{renderAlertStatus(r)}</td>
                    {actionsCell}
                  </tr>
                );
              })}
              {sortedTableRows.length === 0 && (
                <tr>
                  <td colSpan={activeTab === 'closed' ? 9 : 13} style={{ ...tdStyle, borderRight: 'none', padding: 18, color: colors.subText, fontWeight: 400 }}>
                    No rows.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {holdingActionsOpenForId && holdingActionsAnchor
        ? (() => {
            const row = rows.find((r) => r.id === holdingActionsOpenForId);
            if (!row) return null;

            const menuId = `portfolios-holding-actions-menu-${holdingActionsOpenForId}`;
            const menuStyle: React.CSSProperties = {
              position: 'fixed',
              top: holdingActionsAnchor.top,
              left: holdingActionsAnchor.left,
              zIndex: 6000,
              minWidth: 200,
              backgroundColor: colors.cardBg,
              border: `1px solid ${colors.cardBorder}`,
              borderRadius: 10,
              boxShadow: colors.shadow,
              overflow: 'hidden',
            };

            const menuItemBase: React.CSSProperties = {
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              padding: '10px 12px',
              border: 0,
              backgroundColor: 'transparent',
              color: isDarkMode ? '#F9FAFB' : colors.text,
              cursor: 'pointer',
              textAlign: 'left',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
            };

            const iconStyle: React.CSSProperties = { width: 16, height: 16, display: 'block', flex: '0 0 auto' };
            const spacerStyle: React.CSSProperties = { width: 16, height: 16, flex: '0 0 auto' };

            const items: Array<{ label: 'Edit' | 'Sell' | 'Delete'; icon?: string; danger?: boolean }> =
              activeTab === 'closed'
                ? [
                    { label: 'Edit', icon: '/images/action-edit.svg' },
                    { label: 'Delete', icon: '/images/action-delete.svg', danger: true },
                  ]
                : [
                    { label: 'Sell', icon: '/images/action-sell.svg' },
                    { label: 'Edit', icon: '/images/action-edit.svg' },
                    { label: 'Delete', icon: '/images/action-delete.svg', danger: true },
                  ];

            return createPortal(
              <div
                ref={holdingActionsMenuRef}
                id={menuId}
                data-testid={menuId}
                style={menuStyle}
                role="menu"
                aria-label={`${row.symbol} actions`}
              >
                {items.map((it, idx) => (
                  <button
                    key={it.label}
                    type="button"
                    role="menuitem"
                    style={{
                      ...menuItemBase,
                      color: isDarkMode ? '#F9FAFB' : it.danger ? colors.danger : menuItemBase.color,
                      borderBottom:
                        idx < items.length - 1
                          ? `1px solid ${isDarkMode ? '#374151' : '#D9D9D9'}`
                          : 'none',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.tableRowHover;
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                    }}
                    onClick={() => {
                      closeHoldingActionsMenu();
                      handleRowAction(row.id, it.label);
                    }}
                  >
                    {it.icon ? <img src={it.icon} alt="" aria-hidden="true" style={iconStyle} /> : <span aria-hidden="true" style={spacerStyle} />}
                    <span>{it.label}</span>
                  </button>
                ))}
              </div>,
              document.body
            );
          })()
        : null}

      {isAddHoldingOpen && (
        <div
          style={modalOverlayStyle}
          onClick={closeAddHoldingModal}
          id="portfolios-add-holding-modal-overlay"
          data-testid="portfolios-add-holding-modal-overlay"
        >
          <div
            style={addHoldingModalStyle}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={addHoldingMode === 'edit' ? 'Edit Holdings' : 'Add Holdings to the Portfolio'}
            id="portfolios-add-holding-modal"
            data-testid="portfolios-add-holding-modal"
          >
            <div
              style={{
                height: 53,
                width: 'calc(100% + 40px)',
                marginLeft: -20,
                marginRight: -20,
                marginTop: -15,
                paddingLeft: 10,
                paddingRight: 10,
                opacity: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                boxSizing: 'border-box',
              }}
            >
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <img
                  src={addHoldingMode === 'edit' ? '/images/action-edit.svg' : '/images/plus.svg'}
                  alt=""
                  aria-hidden="true"
                  style={{ width: 16, height: 16, display: 'block' }}
                />
                <div
                  style={{
                    ...(addHoldingMode === 'edit'
                      ? portfolioModalTitleStyle
                      : {
                          fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
                          fontWeight: 600,
                          fontStyle: 'normal',
                          fontSize: 20,
                          lineHeight: '100%',
                          letterSpacing: 0,
                          color: '#000000',
                        }),
                    whiteSpace: 'nowrap',
                    overflow: 'visible',
                  }}
                >
                  <span style={{ color: isDarkMode ? '#F9FAFB' : 'inherit' }}>
                    {addHoldingMode === 'edit' ? 'Edit Holdings' : 'Add Holdings to the Portfolio'}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={closeAddHoldingModal}
                aria-label="Close"
                title="Close"
                id="portfolios-add-holding-modal-close"
                data-testid="portfolios-add-holding-modal-close"
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  cursor: 'pointer',
                  color: '#0D0D0D',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 6,
                }}
              >
                <img
                  src="/images/remove.svg"
                  alt="Close"
                  style={{ width: 24, height: 24, display: 'block' }}
                />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 25, flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 4 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 18, alignItems: 'end' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginLeft: 5 }}>
                  <label style={addHoldingLabelStyle} htmlFor="portfolios-add-holding-symbol">
                    Stock Name
                  </label>
                  <div style={{ position: 'relative', width: 220 }}>
                    <input
                      ref={symbolInputRef}
                      id="portfolios-add-holding-symbol"
                      data-testid="portfolios-add-holding-symbol"
                      name="portfolio-holding-symbol"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="none"
                      spellCheck={false}
                      value={draftSymbol}
                      onChange={(e) => {
                        const v = e.target.value;
                        setDraftSymbol(v);
                        setShowSymbolAutocomplete(v.trim().length > 0);
                        setSymbolSelectedIndex(-1);
                        if (addHoldingError) setAddHoldingError(null);
                      }}
                      placeholder="Search Symbol"
                      style={addHoldingSymbolInputStyle}
                      onFocus={() => {
                        if (draftSymbol.trim()) setShowSymbolAutocomplete(true);
                      }}
                      onKeyDown={(e) => {
                        if (!showSymbolAutocomplete || filteredStocks.length === 0) {
                          if (e.key === 'Enter') void runSymbolSearch();
                          if (e.key === 'Escape') {
                            setShowSymbolAutocomplete(false);
                            setSymbolSelectedIndex(-1);
                          }
                          return;
                        }

                        switch (e.key) {
                          case 'ArrowDown':
                            e.preventDefault();
                            setSymbolSelectedIndex((prev) => (prev < filteredStocks.length - 1 ? prev + 1 : prev));
                            break;
                          case 'ArrowUp':
                            e.preventDefault();
                            setSymbolSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
                            break;
                          case 'Enter':
                            e.preventDefault();
                            if (symbolSelectedIndex >= 0 && symbolSelectedIndex < filteredStocks.length) {
                              const s = filteredStocks[symbolSelectedIndex].symbol;
                              setDraftSymbol(s);
                              setShowSymbolAutocomplete(false);
                              setSymbolSelectedIndex(-1);
                              void runSymbolSearch(s);
                            } else {
                              void runSymbolSearch();
                            }
                            break;
                          case 'Escape':
                            setShowSymbolAutocomplete(false);
                            setSymbolSelectedIndex(-1);
                            break;
                        }
                      }}
                    />

                    {showSymbolAutocomplete && filteredStocks.length > 0 && (
                      <div ref={symbolAutocompleteRef} style={symbolAutocompleteContainerStyle}>
                        {filteredStocks.map((stock, index) => (
                          <div
                            key={`${stock.symbol}-${stock.exchange}-${index}`}
                            style={symbolAutocompleteItemStyle(index === symbolSelectedIndex)}
                            onClick={() => {
                              setDraftSymbol(stock.symbol);
                              setShowSymbolAutocomplete(false);
                              setSymbolSelectedIndex(-1);
                              void runSymbolSearch(stock.symbol);
                            }}
                            onMouseEnter={() => setSymbolSelectedIndex(index)}
                          >
                            <div style={symbolAutocompleteSymbolStyle}>
                              {stock.exchange ? `${stock.symbol} - ${stock.exchange}` : stock.symbol}
                            </div>
                            <div style={symbolAutocompleteNameStyle}>{stock.name}</div>
                            {(stock.exchangeName || stock.category) ? (
                              <div style={symbolAutocompleteExchangeStyle}>
                                {[stock.exchangeName, stock.category].filter(Boolean).join(' • ')}
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  style={addHoldingSearchButtonStyle}
                  id="portfolios-add-holding-search"
                  data-testid="portfolios-add-holding-search"
                  onClick={() => void runSymbolSearch()}
                  disabled={symbolSearchBusy}
                >
                  {symbolSearchBusy ? 'Searching' : 'Search'}
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginLeft: 5 }}>
                <label style={addHoldingLabelStyle} htmlFor="portfolios-add-holding-shares">
                  Number of Shares
                </label>
                <input
                  id="portfolios-add-holding-shares"
                  data-testid="portfolios-add-holding-shares"
                  value={draftShares}
                  onChange={(e) => {
                    setDraftShares(e.target.value);
                    if (addHoldingError) setAddHoldingError(null);
                  }}
                  placeholder=""
                  inputMode="decimal"
                  style={addHoldingInputStyle}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginLeft: 5 }}>
                <label style={addHoldingLabelStyle} htmlFor="portfolios-add-holding-open-date">
                  {isEditingClosedForModal ? 'Sell Date' : 'Buy Date'}
                </label>
                <input
                  id="portfolios-add-holding-open-date"
                  data-testid="portfolios-add-holding-open-date"
                  type="date"
                  value={draftOpenDate}
                  onChange={(e) => {
                    setDraftOpenDate(e.target.value);
                    if (addHoldingError) setAddHoldingError(null);
                  }}
                  style={addHoldingInputStyle}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={addHoldingLabelStyle} htmlFor="portfolios-add-holding-buy-price">
                  {isEditingClosedForModal ? 'Sell Price per Share' : 'Average Price per Share'}
                </label>
                <input
                  id="portfolios-add-holding-buy-price"
                  data-testid="portfolios-add-holding-buy-price"
                  value={draftBuyPrice}
                  onChange={(e) => {
                    setDraftBuyPrice(e.target.value);
                    if (addHoldingError) setAddHoldingError(null);
                  }}
                  placeholder=""
                  inputMode="decimal"
                  style={addHoldingInputStyle}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={addHoldingLabelStyle} htmlFor="portfolios-add-holding-buy-alert">
                  Buy Price Alert
                </label>
                <input
                  id="portfolios-add-holding-buy-alert"
                  data-testid="portfolios-add-holding-buy-alert"
                  value={draftBuyAlert}
                  onChange={(e) => {
                    setDraftBuyAlert(e.target.value);
                    if (addHoldingError) setAddHoldingError(null);
                  }}
                  placeholder=""
                  inputMode="decimal"
                  style={addHoldingInputStyle}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={addHoldingLabelStyle} htmlFor="portfolios-add-holding-sell-alert">
                  Sell Price Alert
                </label>
                <input
                  id="portfolios-add-holding-sell-alert"
                  data-testid="portfolios-add-holding-sell-alert"
                  value={draftSellAlert}
                  onChange={(e) => {
                    setDraftSellAlert(e.target.value);
                    if (addHoldingError) setAddHoldingError(null);
                  }}
                  placeholder=""
                  inputMode="decimal"
                  style={addHoldingInputStyle}
                />
              </div>
            </div>

            {addHoldingError ? (
              <div style={{ color: colors.danger, fontWeight: 700, fontSize: 13 }}>
                {addHoldingError}
              </div>
            ) : null}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 18 }}>
              <button
                type="button"
                style={addHoldingAddButtonStyle}
                id="portfolios-add-holding-submit"
                data-testid="portfolios-add-holding-submit"
                onClick={() => {
                  setAddHoldingError(null);
                  const symbol = normalizeSearchSymbolInput(draftSymbol);
                  const shares = parseNumOrNull(draftShares);
                  const buyPrice = parseNumOrNull(draftBuyPrice);
                  const buyPriceAlert = parseNumOrNull(draftBuyAlert);
                  const sellPriceAlert = parseNumOrNull(draftSellAlert);
                  const openDateUtc = inputDateToUtcIso(draftOpenDate);
                  const editRow = addHoldingMode === 'edit'
                    ? rows.find((r) => r.id === editHoldingId)
                    : null;
                  const isEditingClosed = Boolean(editRow?.isClosed);
                  const buyDateUtc = isEditingClosed
                    ? inputDateToUtcIso(displayDateToInputDate(String(editRow?.buyDate || '')))
                    : openDateUtc;
                  const editSellDateUtc = isEditingClosed ? openDateUtc : null;

                  if (!symbol) {
                    pushAddHoldingError('Please enter a stock symbol/name.');
                    return;
                  }
                  if (!shares || shares <= 0) {
                    pushAddHoldingError('Please enter number of shares.');
                    return;
                  }
                  if (!buyPrice || buyPrice <= 0) {
                    pushAddHoldingError(isEditingClosed
                      ? 'Please enter sell price per share.'
                      : 'Please enter average price per share.');
                    return;
                  }
                  if (!openDateUtc) {
                    pushAddHoldingError(isEditingClosed ? 'Please select sell date.' : 'Please select buy date.');
                    return;
                  }

                  closeAddHoldingModal();

                  window.setTimeout(() => {
                    void (async () => {
                    let resolvedCurrentPrice: number | null = null;
                    if (isEditingClosed) {
                      resolvedCurrentPrice = buyPrice;
                    } else {
                      resolvedCurrentPrice = draftCurrentPrice && draftCurrentPrice > 0 ? draftCurrentPrice : null;
                    }

                    if (!resolvedCurrentPrice && !isEditingClosed) {
                      try {
                        if (authToken) {
                          const rows = await adminApi.getQuotesCache(authToken, [symbol]);
                          const p = Number(rows?.[0]?.currentPrice);
                          if (Number.isFinite(p) && p > 0) resolvedCurrentPrice = p;
                        }

                        if (!resolvedCurrentPrice) {
                          const q = await getStockQuote(symbol);
                          const p = Number(q?.price);
                          if (Number.isFinite(p) && p > 0) resolvedCurrentPrice = p;
                        }
                      } catch {
                        // ignore
                      }
                    }

                    const currentPrice = resolvedCurrentPrice && resolvedCurrentPrice > 0 ? resolvedCurrentPrice : buyPrice;
                    const existingBuyPrice = Number(editRow?.buyPrice);
                    const resolvedBuyPrice = isEditingClosed
                      ? (Number.isFinite(existingBuyPrice) && existingBuyPrice > 0 ? existingBuyPrice : buyPrice)
                      : buyPrice;
                    if (!authToken) {
                      pushAddHoldingError('You must be logged in to add/edit holdings.');
                      return;
                    }

                    const targetPortfolioId = addHoldingMode === 'edit'
                      ? resolveRowPortfolioId(editRow)
                      : selectedPortfolioId;
                    if (!targetPortfolioId || targetPortfolioId === ALL_PORTFOLIOS_ID) {
                      pushAddHoldingError('Please select a portfolio first.');
                      return;
                    }

                    try {
                      const maybeId = addHoldingMode === 'edit' ? Number(editHoldingId) : null;
                      const res = await adminApi.upsertMyPortfolioHolding(authToken, {
                        id: Number.isFinite(maybeId) && (maybeId as number) > 0 ? (maybeId as number) : null,
                        portfolioId: targetPortfolioId,
                        symbol,
                        shares,
                        buyPrice: resolvedBuyPrice,
                        buyDateUtc,
                        currentPrice,
                        buyPriceAlert,
                        sellPriceAlert,
                        status: 'Not Reached',
                        isClosed: addHoldingMode === 'edit' ? isEditingClosed : false,
                        sellDateUtc: addHoldingMode === 'edit' && isEditingClosed ? editSellDateUtc : undefined,
                      });
                      const savedId = Number((res as any)?.id);
                      await refreshHoldingsFromDb();

                      if (addHoldingMode === 'edit') {
                        const snapshot = lastEditHoldingSnapshotRef.current;
                        if (snapshot && targetPortfolioId) {
                          dismissAllPortfoliosUndoSnackbars();

                          const timerId = window.setTimeout(() => {
                            pendingHoldingEditUndoRef.current = null;
                            setPendingHoldingEditUndo(null);
                          }, HOLDING_EDIT_UNDO_MS);

                          const pending: PendingUndo & { snapshot: Holding; portfolioId: number } = {
                            message: 'Holding updated',
                            timerId,
                            snapshot,
                            portfolioId: targetPortfolioId,
                          };

                          pendingHoldingEditUndoRef.current = pending;
                          setPendingHoldingEditUndo({ message: pending.message, timerId: pending.timerId });
                        }

                        lastEditHoldingSnapshotRef.current = null;
                      }

                      if (
                        addHoldingMode !== 'edit' &&
                        holdingAddLaunchSource === 'header' &&
                        Number.isFinite(savedId) &&
                        savedId > 0
                      ) {
                        const existing = pendingHoldingAddRef.current;
                        if (existing) window.clearTimeout(existing.timerId);

                        const timerId = window.setTimeout(() => {
                          pendingHoldingAddRef.current = null;
                          setPendingHoldingAdd(null);
                        }, HOLDING_ADD_UNDO_MS);

                        const pending: PendingHoldingAdd = {
                          holdingId: savedId,
                          portfolioName: selectedPortfolioName,
                          timerId,
                        };

                        pendingHoldingAddRef.current = pending;
                        setPendingHoldingAdd(pending);
                      }

                      setHoldingAddLaunchSource('portfolios');
                    } catch (e: any) {
                      if (isUnauthorized(e)) {
                        onUnauthorized?.();
                        return;
                      }
                      pushAddHoldingError(e?.message || 'Failed saving holding');
                    }
                  })();
                  }, 0);
                }}
              >
                {addHoldingMode === 'edit' ? 'Save' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isDeleteModalOpen && deleteRow && (
        <div
          style={modalOverlayStyle}
          onClick={closeDeleteModal}
          id="portfolios-delete-holding-modal-overlay"
          data-testid="portfolios-delete-holding-modal-overlay"
        >
          <div
            style={deleteHoldingModalStyle}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Delete this holding"
            id="portfolios-delete-holding-modal"
            data-testid="portfolios-delete-holding-modal"
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <img src="/images/action-delete.svg" alt="" aria-hidden="true" style={{ width: 16, height: 16, display: 'block', flex: '0 0 auto' }} />
                <div
                  style={{
                    ...portfolioModalTitleStyle,
                    whiteSpace: 'nowrap',
                    color: isDarkMode ? '#F9FAFB' : portfolioModalTitleStyle.color,
                  }}
                >
                  Delete this holding?
                </div>
              </div>

              <button
                type="button"
                onClick={closeDeleteModal}
                aria-label="Close"
                title="Close"
                id="portfolios-delete-holding-modal-close"
                data-testid="portfolios-delete-holding-modal-close"
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  cursor: 'pointer',
                  color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img src="/images/remove.svg" alt="Close" style={{ width: 24, height: 24, display: 'block' }} />
              </button>
            </div>

            <div
              style={{
                fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
                fontWeight: 400,
                fontSize: 16,
                lineHeight: '20px',
                letterSpacing: 0,
                verticalAlign: 'middle',
                color: isDarkMode ? '#F9FAFB' : '#5A5A5A',
              }}
            >
              You're about to delete this holding.
            </div>

            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
                fontWeight: 400,
                fontSize: 16,
                lineHeight: '20px',
                letterSpacing: 0,
                verticalAlign: 'middle',
                color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
              }}
              id="portfolios-delete-holding-dont-show-again-label"
              data-testid="portfolios-delete-holding-dont-show-again-label"
            >
              <input
                type="checkbox"
                checked={deleteDontShowAgain}
                onChange={(e) => setDeleteDontShowAgain(e.target.checked)}
                id="portfolios-delete-holding-dont-show-again"
                data-testid="portfolios-delete-holding-dont-show-again"
                style={{
                  width: 16,
                  height: 18,
                  margin: 0,
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  borderRadius: 4,
                  border: `1px solid ${deleteDontShowAgain ? '#2F58F2' : '#D9D9D9'}`,
                  backgroundColor: deleteDontShowAgain ? '#2F58F2' : 'transparent',
                  backgroundImage: deleteDontShowAgain
                    ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 12'%3E%3Cpath d='M2 6.5l3.2 3.2L14 1.9' stroke='%23fff' stroke-width='2.2' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`
                    : 'none',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  cursor: 'pointer',
                  flex: '0 0 auto',
                }}
              />
              <span>Don't show again</span>
            </label>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 18, marginTop: 'auto' }}>
              <button
                type="button"
                id="portfolios-delete-holding-confirm"
                data-testid="portfolios-delete-holding-confirm"
                onClick={() => {
                  if (deleteDontShowAgain) {
                    try {
                      window.localStorage.setItem('portfolios_skip_delete_confirm', '1');
                      setSkipDeleteConfirm(true);
                    } catch {
                      // ignore
                    }
                  }

                  performDeleteHolding(deleteRow.id);
                  closeDeleteModal();
                }}
                style={{
                  background: '#EF4444',
                  border: 'none',
                  color: isDarkMode ? '#111827' : '#FFFFFF',
                  width: 170,
                  height: 40,
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 16,
                  fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {isSellModalOpen && sellRow && (
        <div
          style={modalOverlayStyle}
          onClick={closeSellModal}
          id="portfolios-sell-holding-modal-overlay"
          data-testid="portfolios-sell-holding-modal-overlay"
        >
          <div
            style={{
              ...sellHoldingModalStyle,
              position: 'absolute',
              left: sellModalPos ? sellModalPos.x : '50%',
              top: sellModalPos ? sellModalPos.y : '50%',
              transform: sellModalPos ? 'translate(0,0)' : 'translate(-50%,-50%)',
            }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Sell Holdings for ${sellRow.symbol}`}
            id="portfolios-sell-holding-modal"
            data-testid="portfolios-sell-holding-modal"
          >
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, cursor: 'move', userSelect: 'none' }}
              role="presentation"
              onMouseDown={(e) => {
                if (e.button !== 0) return;
                e.preventDefault();
                const pos = sellModalPos || { x: 12, y: 12 };
                sellDragRef.current = { startClientX: e.clientX, startClientY: e.clientY, originX: pos.x, originY: pos.y };
                setIsSellDragging(true);
              }}
            >
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <img src="/images/action-sell.svg" alt="" aria-hidden="true" style={{ width: 16, height: 16, display: 'block' }} />
                <div style={{ ...portfolioModalTitleStyle, whiteSpace: 'nowrap' }}>
                  Sell Holdings for
                </div>
                <div
                  style={{
                    ...addHoldingTextStyle,
                    width: 75,
                    height: 31,
                    opacity: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    borderRadius: 4,
                    border: '1px solid #D9D9D9',
                    padding: '2px 8px',
                    background: isDarkMode ? '#1E293B' : '#F1F7FF',
                    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
                    fontWeight: 700,
                    lineHeight: 1,
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {sellRow.symbol}
                </div>
              </div>
              <button
                type="button"
                onClick={closeSellModal}
                aria-label="Close"
                title="Close"
                id="portfolios-sell-holding-modal-close"
                data-testid="portfolios-sell-holding-modal-close"
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  cursor: 'pointer',
                  color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img src="/images/remove.svg" alt="Close" style={{ width: 24, height: 24, display: 'block' }} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 25, flex: 1, minHeight: 0, justifyContent: 'flex-start', marginTop: 5, marginLeft: 5 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={addHoldingLabelStyle} htmlFor="portfolios-sell-holding-shares">
                  Number of Shares
                </label>
                <input
                  id="portfolios-sell-holding-shares"
                  data-testid="portfolios-sell-holding-shares"
                  value={sellSharesText}
                  onChange={(e) => {
                    setSellSharesText(e.target.value);
                    if (sellError) setSellError(null);
                  }}
                  placeholder=""
                  inputMode="decimal"
                  style={sellHoldingInputStyle}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={addHoldingLabelStyle} htmlFor="portfolios-sell-holding-price">
                  Average Price per Share
                </label>
                <input
                  id="portfolios-sell-holding-price"
                  data-testid="portfolios-sell-holding-price"
                  value={sellPriceText}
                  onChange={(e) => {
                    setSellPriceText(e.target.value);
                    if (sellError) setSellError(null);
                  }}
                  placeholder=""
                  inputMode="decimal"
                  style={sellHoldingInputStyle}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={addHoldingLabelStyle} htmlFor="portfolios-sell-holding-date">
                  Sell Date
                </label>
                <input
                  id="portfolios-sell-holding-date"
                  data-testid="portfolios-sell-holding-date"
                  type="date"
                  value={sellDateText}
                  onChange={(e) => {
                    setSellDateText(e.target.value);
                    if (sellError) setSellError(null);
                  }}
                  placeholder=""
                  style={sellHoldingInputStyle}
                />
              </div>
            </div>

            {sellError ? (
              <div style={{ color: colors.danger, fontWeight: 700, fontSize: 13 }}>
                {sellError}
              </div>
            ) : null}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 18, transform: 'translateY(-10px)' }}>
              <button
                type="button"
                style={{
                  ...addHoldingAddButtonStyle,
                  color: isDarkMode ? '#111827' : addHoldingAddButtonStyle.color,
                }}
                id="portfolios-sell-holding-submit"
                data-testid="portfolios-sell-holding-submit"
                onClick={submitSellHolding}
              >
                Sell
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expanded Chart Modal (matches Key Metrics behavior) */}
      {expandedChart && (
        <div
          style={modalOverlayStyle}
          onClick={() => {
            setExpandedChart(null);
            setExpandedHoldingsKey(null);
          }}
          id="portfolios-expanded-modal-overlay"
          data-testid="portfolios-expanded-modal-overlay"
        >
          <div
            style={modalContentStyle}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            id="portfolios-expanded-modal"
            data-testid="portfolios-expanded-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexShrink: 0 }}>
              <h2
                style={{ fontSize: '1.25rem', fontWeight: 900, margin: 0, display: 'inline-flex', alignItems: 'center', gap: 10 }}
                id="portfolios-expanded-modal-title"
                data-testid="portfolios-expanded-modal-title"
              >
                <img src={zoomInIcon} alt="" aria-hidden="true" style={{ width: 24, height: 24, display: 'block' }} />
                <span
                  style={{
                    fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
                    fontWeight: 900,
                    fontSize: 18,
                    color: isDarkMode ? colors.text : '#0D0D0D',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {expandedChart === 'portfolio-holdings' && (expandedHoldingsChart?.title || 'Portfolio Holdings')}
                  {expandedChart === 'profit-loss' && 'Profit/Loss per Share'}
                  {expandedChart === 'buy-vs-current' && 'Cost and Market Value per Holding'}
                </span>
              </h2>
              <button
                onClick={() => {
                  setExpandedChart(null);
                  setExpandedHoldingsKey(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '2rem',
                  cursor: 'pointer',
                  padding: 0,
                  color: colors.text,
                  lineHeight: 1,
                }}
                aria-label="Close"
                title="Close"
                id="portfolios-expanded-modal-close"
                data-testid="portfolios-expanded-modal-close"
              >
                ×
              </button>
            </div>

            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {expandedChart === 'portfolio-holdings' && (
                <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1fr 220px', gap: 16, alignItems: 'center' }}>
                  <div style={{ height: '100%', minHeight: 0, position: 'relative' }}>
                    <Doughnut
                      ref={doughnutModalChartRef}
                      data={expandedHoldingsChart?.data || doughnutData}
                      options={expandedHoldingsChart?.options || doughnutOptions}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                      }}
                    >
                        <div
                          style={{
                            fontFamily: 'Manrope, sans-serif',
                            fontStyle: 'normal',
                            fontSize: 34,
                            fontWeight: 600,
                            lineHeight: '100%',
                            letterSpacing: '0px',
                            color: '#2E384D',
                            marginBottom: 14,
                          }}
                        >
                          Current Value
                        </div>
                        <AutoFitText
                          text={formatCurrency0(expandedHoldingsChart?.total ?? totalMarketValue)}
                          maxFontSize={39}
                          minFontSize={39}
                          fontFamily="Manrope, sans-serif"
                          fontWeight={300}
                          color="#0D0D0D"
                          maxWidth="68%"
                          paddingX={8}
                        />
                        {Number.isFinite(expandedHoldingsChart?.pnl ?? NaN) && (
                          <div
                            style={{
                              fontFamily: 'Manrope, sans-serif',
                              fontStyle: 'normal',
                              fontSize: 24,
                              fontWeight: 500,
                              lineHeight: '100%',
                              letterSpacing: '0px',
                              color: (expandedHoldingsChart?.pnl ?? 0) > 0
                                ? '#12B76A'
                                : (expandedHoldingsChart?.pnl ?? 0) < 0
                                  ? colors.danger
                                  : colors.subText,
                              marginTop: 10,
                            }}
                          >
                            ({(expandedHoldingsChart?.pnl ?? 0) > 0 ? '+' : (expandedHoldingsChart?.pnl ?? 0) < 0 ? '-' : ''}
                            {formatCurrency0(Math.abs(expandedHoldingsChart?.pnl ?? 0))})
                          </div>
                        )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignSelf: 'stretch', justifyContent: 'center' }}>
                    {(expandedHoldingsChart?.data || doughnutData).labels.map((lbl, i) => {
                      const data = expandedHoldingsChart?.data || doughnutData;
                      const total = expandedHoldingsChart?.total ?? totalMarketValue;
                      const val = Number(data.datasets[0].data[i] || 0);
                      const pct = total > 0 ? (100 * val) / total : 0;
                      const bg = (data.datasets[0].backgroundColor as string[])[i];
                      return (
                        <div
                          key={lbl}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}
                          onMouseEnter={(e) => showPortfolioLegendTooltip(doughnutModalChartRef.current, e.currentTarget, i, total, e.clientX, e.clientY)}
                          onMouseMove={(e) => showPortfolioLegendTooltip(doughnutModalChartRef.current, e.currentTarget, i, total, e.clientX, e.clientY)}
                          onMouseLeave={() => hidePortfolioLegendTooltip(doughnutModalChartRef.current)}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                            <span style={{ width: 12, height: 12, borderRadius: 999, background: bg }} />
                            <span
                              style={{
                                fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
                                fontSize: 16,
                                fontWeight: 400,
                                color: isDarkMode ? colors.text : '#0D0D0D',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {lbl}
                            </span>
                          </div>
                          <span
                            style={{
                              fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
                              fontSize: 16,
                              fontWeight: 400,
                              color: isDarkMode ? colors.text : '#0D0D0D',
                            }}
                          >
                            {pct.toFixed(2)}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {expandedChart === 'profit-loss' && (
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    boxSizing: 'border-box',
                    position: 'relative',
                    paddingRight: 5,
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 10,
                      bottom: 30,
                      width: 1,
                      backgroundColor: colors.cardBorder,
                      pointerEvents: 'none',
                    }}
                  />
                  <div style={{ height: '100%' }}>
                    <Bar data={plBarData} options={plBarOptions} />
                  </div>
                </div>
              )}

              {expandedChart === 'buy-vs-current' && (
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    position: 'relative',
                    boxSizing: 'border-box',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 10,
                      bottom: 70,
                      width: 1,
                      backgroundColor: colors.cardBorder,
                      pointerEvents: 'none',
                    }}
                  />
                  <div style={{ height: '100%' }}>
                    <Bar data={compareData} options={compareOptions} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          marginTop: 12,
          textAlign: 'left',
          fontSize: 12,
          color: colors.subText,
          fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
        }}
      >
        Data powered by{' '}
        <a
          href="https://fiscal.ai"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: colors.primary,
            fontWeight: 700,
            textDecoration: 'underline',
          }}
        >
          Fiscal.ai
        </a>
      </div>

      <UndoSnackbar
        open={!!pendingHoldingAdd}
        message={pendingHoldingAdd ? `Added to my ${pendingHoldingAdd.portfolioName}` : ''}
        onUndo={() => {
          const pending = pendingHoldingAddRef.current;
          if (!pending) return;

          if (!authToken) {
            onNotify?.('You must be logged in to undo.', 'error');
            dismissPendingHoldingAdd();
            return;
          }

          window.clearTimeout(pending.timerId);
          pendingHoldingAddRef.current = null;
          setPendingHoldingAdd(null);

          void (async () => {
            try {
              await adminApi.deleteMyPortfolioHolding(authToken, pending.holdingId);
              await refreshHoldingsFromDb();
            } catch (e: any) {
              if (isUnauthorized(e)) {
                onUnauthorized?.();
                return;
              }
              onNotify?.(e?.message || 'Failed undo', 'error');
            }
          })();
        }}
        onDismiss={dismissPendingHoldingAdd}
      />

      <UndoSnackbar
        open={!!pendingHoldingDelete}
        message={pendingHoldingDelete ? `Deleted ${pendingHoldingDelete.row.symbol}` : ''}
        onUndo={undoPendingHoldingDelete}
        onDismiss={() => {
          const pending = pendingHoldingDeleteRef.current;
          if (!pending) return;
          void commitPendingHoldingDelete(pending.holdingId);
        }}
      />

      <UndoSnackbar
        open={!!pendingHoldingEditUndo}
        message={pendingHoldingEditUndo?.message ?? ''}
        onUndo={undoPendingHoldingEdit}
        onDismiss={dismissPendingHoldingEditUndo}
      />

      <UndoSnackbar
        open={!!pendingHoldingSellUndo}
        message={pendingHoldingSellUndo?.message ?? ''}
        onUndo={undoPendingHoldingSell}
        onDismiss={dismissPendingHoldingSellUndo}
      />
      </div>
    </div>
  );
};

export default PortfoliosScreen;
